import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	inject,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ObjectDetector, ObjectDetectorResult } from '@mediapipe/tasks-vision';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	ButtonCollapseComponent,
	ButtonComponent,
	ButtonIconComponent,
	CollapseComponent,
	IconComponent,
	INavHelp,
	ItemComponent,
	ItemGroupComponent,
	InfoGroupComponent,
	DividerComponent,
	LinkComponent,
	NavHelpComponent,
	ProgressComponent,
	PwcsLayoutComponent,
	QrComponent,
	ScanComponent,
	SpinnerComponent,
	SwitchComponent,
	TagImageComponent,
	TileComponent,
	TopnavComponent,
	WatermarkComponent,
	CardAccessComponent,
	ListDetailComponent,
	ITagImage,
} from '@tecalisdev/infinity';
import { AnimationOptions, BMCompleteLoopEvent, LottieComponent } from 'ngx-lottie';
import { Subject, Subscription, interval, takeUntil, timer } from 'rxjs';
import { ImageHelperService } from 'src/app/core/helpers/image-helper.service';
import { ProgressService } from 'src/app/core/helpers/progress.service';
import { IAlert } from 'src/app/core/models/alert.models';
import { ILoading } from 'src/app/core/models/loading.models';
import { NavHelpAction } from 'src/app/core/models/navHelp.models';
import { ConfigService } from 'src/app/shared/services/config/config.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { ScreenshotService } from 'src/app/core/services/screenshot.service';
import { HandsetLandscapeTileComponent } from 'src/app/shared/components/handset-landscape-tile/handset-landscape-tile.component';
import { ModalChangeDeviceComponent } from 'src/app/shared/components/modal-change-device/modal-change-device.component';
import { ModalHelpNoCameraPermissionsComponent } from 'src/app/shared/components/modal-help-no-camera-permissions/modal-help-no-camera-permissions.component';
import { ModalScanQrComponent } from 'src/app/shared/components/modal-scan-qr/modal-scan-qr.component';
import { PwcsInlineSvgComponent } from 'src/app/shared/components/pwcs-inline-svg/pwcs-inline-svg.component';
import { VideoComponent } from 'src/app/shared/components/video/video.component';
import { ERROR_CODE } from 'src/app/shared/models/error.models';
import { IMaster } from 'src/app/shared/models/master.models';
import { AudioService } from 'src/app/shared/services/audio.service';
import { CameraService } from 'src/app/shared/services/camera.service';
import { CameraFocusService } from 'src/app/shared/services/camera-focus.service';
import { CameraPermissionsTimerService } from 'src/app/shared/services/camera-permissions-timer.service';
import { waitForVideoPreviewReady } from 'src/app/shared/utils/wait-for-video-preview-ready.util';
import { isStorageRetryError } from 'src/app/shared/utils/storage-retry.utils';
import {
	onCameraPreviewSwitchCompleted,
	onCameraPreviewSwitchStarted,
} from 'src/app/shared/utils/camera-preview-switch.util';
import { CloudLogsService, LogType } from 'src/app/shared/services/cloud-logs.service';
import { DesignService } from 'src/app/shared/services/config/design.service';
import { LottieThemeService } from 'src/app/shared/services/config/lottie/lottie-theme.service';
import { ErrorService } from 'src/app/shared/services/error.service';
import { FullscreenService } from 'src/app/shared/services/fullscreen.service';
import { IdentitySDKService, OperationType } from 'src/app/shared/services/identity-sdk.service';
import { MasterService } from 'src/app/shared/services/master.service';
import { LanguagePickerUiService } from 'src/app/shared/services/language-picker-ui.service';
import { NavHelpService } from 'src/app/shared/services/nav-help.service';
import { OperationService } from 'src/app/shared/services/step.service';
import { StreamingManagementService } from 'src/app/shared/services/streaming-management.service';
import { TextToSpeechService } from 'src/app/shared/services/text-to-speech.service';
import { environment } from 'src/environments/environment';
import {
	DOCUMENT_CHECK_ID,
	IDocumentDetection,
	IDocumentResult,
	IDocumentTestPhoto,
	IOcr,
	IOcrConf,
	IOcrErrorResponse,
	IOcrPayload,
	IOcrResponse,
	OCR_WARNING_TIME,
	OCR_SEMI_MANUAL_TIME,
	IOcrHistory,
	IOcrDocumentStatus,
	IOcrPromptConfig,
	OCR_SCAN_PREVIEW_ASPECT,
	SEG_HISTORY_INTERVAL,
	IBarcodeCapturedFrame,
	IBarcodeReadPayload,
	IBarcodeReadResponse,
} from '../../models/ocr';
import {
	OcrLog,
	IDocumentsDocs,
	IFinishOperation,
	IOcrOperation,
	IOperation,
	IPageWarning,
	TEST_STATUS,
	MODE,
	OPERATION_STATUS,
	OPERATION_TYPE,
	quality,
	TestStatus,
	PAGE_WARNING,
	MODEL_DOCUMENT_CATEGORY,
} from '../../models/user-identification';
import { MiDNIApiService } from '../../services/ocr/midni-api.service';
import { EudiApiService } from '../../services/ocr/eudi-api.service';
import {
	buildEudiFaqItems,
	EUDI_DATA_GROUPS,
	isEudiOnlyOcrConfig,
	resolveEudiInstructionKey,
} from '../../utils/eudi-help.util';
import { MidniQrScannerComponent, MiDNIQrResult } from './midni-qr-scanner/midni-qr-scanner.component';
import { BarcodeScanComponent } from './barcode-scan/barcode-scan.component';
import { DocumentUploadComponent } from '../docs/components/document-upload/document-upload.component';
import { ModalInstructionsComponent } from '../docs/modals/modal-instructions.component';
import {
	hasDuplicateOcrFlexibleUploadFiles,
	isOcrFlexibleMode,
	isOcrFlexibleUploadFileAllowed,
	normalizeOcrFlexibleUploadDataUrl,
	cropOcrFlexibleUploadDataUrl,
	looksLikeDualLayoutUploadDataUrl,
	ocrCapturePreferLinkI18nKey,
	ocrFlexibleMethodTagImage,
	OCR_FLEXIBLE_UPLOAD_EXTENSIONS,
	OCR_FLEXIBLE_UPLOAD_MAX_BYTES,
	OCR_FLEXIBLE_UPLOAD_MAX_FILES,
	OcrFlexibleMethod,
	OcrFlexibleScreen,
	readFileAsDataUrl,
	resolveOcrCaptureAlternatives,
	resolveOcrCaptureMethod,
	resolveOcrFlexiblePickerCols,
	resolveOcrFlexiblePickerMethods,
} from '../../utils/ocr-flexible-capture.util';
import { OcrService } from '../../services/ocr/ocr.service';
import { PositionDocumentService } from '../../services/ocr/position-document.service';
import { ScoringDocumentService } from '../../services/ocr/scoring-document.service';
import { applyCenterZoomToSourceRect } from 'src/app/shared/services/video-cover-frame.util';
import { OcrTimerManagerService } from '../../services/ocr/timer-manager.service';
import { ProcessHelperService } from '../../services/process-helper.service';
import { ProcessService } from '../../services/process.service';
import {
	getOcrAllowedDniText as resolveOcrAllowedDniText,
	getOcrTutorialDniOnlyTitle,
	isFirstSideScan as isFirstSideScanUtil,
	isDniForOtherCountriesInOcr,
	isDniForSpainInOcr,
	isPassportForOtherCountriesInOcr,
	isPassportForSpainInOcr,
	isSpanishCompany as isSpanishCompanyCountry,
	resolveFirstScanOnInitKey,
} from '../../utils/ocr-allowed-dni-label.util';
import {
	isOcrRestrictionErrorCode,
	resolveOcrRestrictionErrorAudioKey,
	resolveOcrRestrictionErrorMessage,
} from '../../utils/ocr-restriction-error.util';
import { resolveDniPhotoSideAsset } from '../../utils/dni-wireframe-photo-side.util';
import { mergeFormConfigFromProcess } from '../../utils/form-operation.util';
import { detectFaceBboxOnJpegDataUrl } from '../../utils/ocr-client-face-bbox.util';
import { KoDebugImagesService } from '../../services/debug/ko-debug-images.service';
import { OCR_SHARPNESS_DESKTOP, ocrBackConf, ocrConf, ocrFrontConf } from './ocr-conf';
import { UserAgentService } from 'src/app/shared/services/user-agent.service';
import { IImageHelper } from 'src/app/core/models/image.models';
import { findLastIndex } from 'src/app/core/utils/utils';
import { AudioHistoryService } from '../../services/ocr/audio-history.service';

@Component({
	selector: 'app-ocr',
	imports: [
		ReactiveFormsModule,
		TranslatePipe,
		AsyncPipe,
		NgClass,
		NgStyle,
		NgTemplateOutlet,
		LottieComponent,
		AlertComponent,
		ButtonCollapseComponent,
		ButtonComponent,
		ButtonIconComponent,
		CollapseComponent,
		IconComponent,
		ItemComponent,
		ItemGroupComponent,
		InfoGroupComponent,
		DividerComponent,
		LinkComponent,
		NavHelpComponent,
		ProgressComponent,
		PwcsLayoutComponent,
		QrComponent,
		ScanComponent,
		SpinnerComponent,
		SwitchComponent,
		TagImageComponent,
		TileComponent,
		TopnavComponent,
		WatermarkComponent,
		VideoComponent,
		HandsetLandscapeTileComponent,
		ModalScanQrComponent,
		ModalHelpNoCameraPermissionsComponent,
		ModalChangeDeviceComponent,
		MidniQrScannerComponent,
		BarcodeScanComponent,
		PwcsInlineSvgComponent,
		CardAccessComponent,
		DocumentUploadComponent,
		ModalInstructionsComponent,
		ListDetailComponent,
	],
	templateUrl: './ocr.component.html',
	styleUrls: ['./ocr.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class OcrComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() operations: IOperation[];
	@Input()
	get operation(): IOcrOperation {
		return this._operation;
	}
	set operation(operation: IOcrOperation) {
		this._operation = operation;
		this.showLoading(this.trans.instant('GENERAL.LOADING.CAMERA_INIT'));
		this.activatePreviewLoading('GENERAL.LOADING.CAMERA_INIT');
		this.ocrConf = this.setOcrSetting(operation);
		// NIF-936: con tutorial, «Estoy listo» espera preview (y WHIP si aplica) listo.
		this.tutorialContinueEnabled = !operation?.config?.tutorial;

		// Actualizamos si el pasaporte está permitido
		this.updateIsPassportAllowed();

		// Actualizamos el subtitle
		this.updateSubtitle();
	}

	private _operation: IOcrOperation;
	private languagePickerUi = inject(LanguagePickerUiService);

	// General
	public env = environment;
	public loading: ILoading = { show: false, text: '' };
	public navsHelp: Array<INavHelp> = this.navHelpService.getNavsHelp();
	public navsHelpTutorial: Array<INavHelp> = this.navHelpService.getTutorialNavsHelp();
	public isVisibleScan: boolean = false;
	public isHelpByNavHelp: boolean = false;
	public permissionsCamera: boolean = true;
	/**
	 * NIF-936: con tutorial activo, «Estoy listo» solo se habilita cuando el preview de
	 * `app-video` tiene frame (`waitForVideoPreviewReady`) y, si hay grabación, WHIP listo.
	 */
	public tutorialContinueEnabled: boolean = true;
	private cameraPermissionsReady = false;
	private cameraPermissionsTimerService = inject(CameraPermissionsTimerService);
	public pageWarning: IPageWarning = { show: false, type: null };
	public PAGE_WARNING = PAGE_WARNING;
	public isVisibleHelpNoPermissionsCamera: boolean = false;
	public isVisibleChangeDevice: boolean = false;
	public isVisibleHelpValidDocuments: boolean = false;
	public isNotValidDocument: boolean = false;
	/** Mensaje de restricción OCR (2003/2070/2071) mostrado en la ayuda de documento no aceptado. */
	public restrictionErrorMsg: string = '';
	public operationType = OPERATION_TYPE;
	public OcrLog: any = OcrLog;
	public LogType: any = LogType;
	public openQR: boolean = false;
	public handsetLandscape: boolean = false;
	public documentCheckId = DOCUMENT_CHECK_ID;
	public isGapBetweenTests: boolean = false;
	private isPassportAllowed: boolean = true;
	private destroy$: Subject<void> = new Subject<void>();
	private browserstack: boolean = false;
	public isModeSemiManual: boolean = false;
	public sendingResult: boolean = false;
	/** false al completar OCR o destruir componente: bloquea RAF, snapshots y POST tardíos hacia POL. */
	private ocrCaptureActive = true;
	/** NIF-1033: motivo fino de cancelación para Cloud Logs (patrón Docs). */
	private lastCancelReason: string | null = null;
	private lastCancelHttp: { error_code?: number | string; details_code?: number | string; retry?: boolean } | null =
		null;
	private screenStartedAt: number = Date.now();
	private ocrSendAttempts = 0;

	// MiDNI
	@ViewChild('midniScanner') midniScanner?: MidniQrScannerComponent;
	public midniScreen: 'desktop' | null = null;

	// Barcode (DNI_COL PDF417)
	@ViewChild('barcodeScanner') barcodeScanner?: BarcodeScanComponent;
	public barcodeScanActive = false;
	private awaitingBarcodeScan = false;
	public barcodeSending = false;
	private barcodeInstructionIntervalId: ReturnType<typeof setInterval> | null = null;
	private static readonly BARCODE_INSTRUCTION_REPEAT_MS = 30_000;
	public midniError: string = '';
	/** Clave i18n del hint activo mostrado como tec-alert bajo el scanner */
	public midniHintKey: string = '';
	public isMidniHelpContext = false;
	/** Viewport CDK Handset (pantalla tipo móvil); combinar con `isMidniMobileBrowser()` en plantilla si hace falta */
	public midniHandsetViewport = false;
	get midniEnabled(): boolean {
		return !!this.operation?.config?.midni_validation_enabled;
	}

	// NIF-802 Flexible capture
	public flexibleScreen: OcrFlexibleScreen | null = null;
	public flexibleUploadControl = new FormControl(null);
	public flexibleUploadFiles: File[] = [];
	public flexibleUploadErrorKey = '';
	/** Tras OK de 1ª cara: falta la otra; se mantiene el 1er fichero en la UI de upload. */
	public flexibleUploadMissingSide = false;
	/** Usuario cerró el alert inicial «ambas caras». */
	public flexibleUploadBothSidesAlertDismissed = false;
	/** Ficheros ya enviados con OK en esta sesión de upload (no re-OCR al Continuar). */
	private flexibleUploadConsumedCount = 0;
	/**
	 * Sesión de método «Adjuntar»: tras tips/errores OCR (como escaneo) se vuelve a upload.
	 * No confundir con `flexibleScreen` (puede ser null mientras se muestra un pageWarning).
	 */
	public flexibleUploadSession = false;
	/**
	 * True mientras se encadenan ≥2 ficheros en un mismo Continuar: sin TTS/UI de «otra cara»
	 * entre un sendResult y el siguiente.
	 */
	private flexibleUploadChaining = false;
	/** True si el PAGE_WARNING upload es KO tras OCR (Figma ADJUNTAR KO), no falta-cara al Continuar. */
	private flexibleUploadKoAfterSend = false;
	/** True si el aviso es misma foto / misma cara duplicada (NIF-921 Gemma), no «falta anverso». */
	private flexibleUploadDuplicateSide = false;
	public isVisibleFlexibleUploadInstructions = false;
	public flexibleUploadInstructionsDoc: IDocumentsDocs | null = null;
	public readonly flexibleUploadAcceptedTypes = [...OCR_FLEXIBLE_UPLOAD_EXTENSIONS];

	get isFlexibleMode(): boolean {
		return isOcrFlexibleMode(this.operation?.config?.mode);
	}

	get flexibleAlternatives(): OcrFlexibleMethod[] {
		return resolveOcrCaptureAlternatives({
			flexible: this.isFlexibleMode,
			midniEnabled: this.midniEnabled,
			eudiEnabled: this.eudiEnabled,
		});
	}

	/** Métodos del picker (incluye escaneo automático). */
	get flexiblePickerMethods(): OcrFlexibleMethod[] {
		return resolveOcrFlexiblePickerMethods({
			flexible: this.isFlexibleMode,
			midniEnabled: this.midniEnabled,
			eudiEnabled: this.eudiEnabled,
		});
	}

	/** NIF-960: 4 métodos → grid-e4; resto → 3 columnas. */
	get flexiblePickerCols(): number {
		return resolveOcrFlexiblePickerCols(this.flexiblePickerMethods.length);
	}

	get flexiblePreferLinkKey(): string | null {
		if (this.flexibleScreen !== null || this.midniScreen !== null || this.eudiScreen !== null) {
			return null;
		}
		return ocrCapturePreferLinkI18nKey(this.flexibleAlternatives);
	}

	get showFlexiblePreferLink(): boolean {
		return !!this.flexiblePreferLinkKey && !this.loading.show && !this.pageWarning.show && this.eudiScreen === null;
	}

	/** Subtitle del layout en overlays Flexible (sustituye el copy de encuadre OCR). */
	get flexibleLayoutSubtitle(): string {
		// Figma falta-cara: sin subtítulo de layout (solo title/subtitle de la tile status).
		if (this.pageWarning.type === PAGE_WARNING.FLEXIBLE_UPLOAD_MISSING_SIDE) {
			return '';
		}
		if (this.flexibleScreen === 'picker') {
			return this.trans.instant('OCR.FLEXIBLE.PICKER_TITLE');
		}
		if (this.flexibleScreen === 'upload') {
			return this.trans.instant('DOCUMENTS.SUBTITLE.SINGLE');
		}
		return this.subtitle;
	}

	/**
	 * Perfil de documentos para copy/UI de adjuntar (NIF-921 Carmen).
	 * passport_only | id_only | id_and_passport.
	 */
	get flexibleUploadDocProfile(): 'passport_only' | 'id_only' | 'id_and_passport' {
		const passport = this.isPassportAllowed;
		const identity = this.isIdentityDocAllowed();
		if (passport && !identity) {
			return 'passport_only';
		}
		if (passport && identity) {
			return 'id_and_passport';
		}
		return 'id_only';
	}

	/** Título de la tile de adjuntar según documentos permitidos. */
	get flexibleUploadTitleKey(): string {
		return this.flexibleUploadDocProfile === 'passport_only'
			? 'OCR.GENERAL.DOCUMENT_TYPE.PASSPORT'
			: 'OCR.GENERAL.DOCUMENT_TYPE.DOCUMENT_ID';
	}

	/** Alert informativo inicial según perfil (type message). */
	get flexibleUploadAlertKey(): string {
		switch (this.flexibleUploadDocProfile) {
			case 'passport_only':
				return 'OCR.FLEXIBLE.UPLOAD_ALERT_PASSPORT_ONLY';
			case 'id_and_passport':
				return 'OCR.FLEXIBLE.UPLOAD_ALERT_WITH_PASSPORT';
			default:
				return 'OCR.FLEXIBLE.UPLOAD_ALERT_ID_ONLY';
		}
	}

	get flexibleUploadHelpBodyKey(): string {
		switch (this.flexibleUploadDocProfile) {
			case 'passport_only':
				return 'OCR.FLEXIBLE.UPLOAD_HELP_PASSPORT_ONLY';
			case 'id_and_passport':
				return 'OCR.FLEXIBLE.UPLOAD_HELP_WITH_PASSPORT';
			default:
				return 'OCR.FLEXIBLE.UPLOAD_HELP_ID_ONLY';
		}
	}

	/** Máx. ficheros: 1 si solo pasaporte; 2 si documento de dos caras. */
	get flexibleUploadMaxFiles(): number {
		return this.flexibleUploadDocProfile === 'passport_only' ? 1 : OCR_FLEXIBLE_UPLOAD_MAX_FILES;
	}

	/** Título fullscreen / pending side: anverso o reverso. */
	get flexibleUploadPendingSideAlertKey(): string {
		const pending = this.getPendingOcrDocument();
		if (!pending) {
			return 'OCR.FLEXIBLE.UPLOAD_MISSING_SIDE';
		}
		return pending.documentTestId === DOCUMENT_CHECK_ID.FRONT
			? 'OCR.FLEXIBLE.UPLOAD_ATTACH_FRONT'
			: 'OCR.FLEXIBLE.UPLOAD_ATTACH_BACK';
	}

	/**
	 * Título tile status upload Flexible:
	 * - Misma foto / misma cara → copy dedicado (NIF-921 Gemma)
	 * - KO tras enviar → «Asegúrate de subir el anverso y el reverso»
	 * - Falta cara al Continuar → «Debes adjuntar también el anverso/reverso…»
	 */
	get flexibleUploadMissingSideTitleKey(): string {
		if (this.flexibleUploadDuplicateSide) {
			return 'OCR.FLEXIBLE.UPLOAD_DUPLICATE_SIDE_TITLE';
		}
		if (this.flexibleUploadKoAfterSend) {
			return 'OCR.FLEXIBLE.UPLOAD_KO_TITLE';
		}
		const pending = this.getPendingOcrDocument();
		if (pending?.documentTestId === DOCUMENT_CHECK_ID.BACK) {
			return 'OCR.FLEXIBLE.UPLOAD_MISSING_SIDE_TITLE_BACK';
		}
		return 'OCR.FLEXIBLE.UPLOAD_MISSING_SIDE_TITLE_FRONT';
	}

	// EUDI Wallet (NIF-960)
	public eudiScreen: 'wallet' | 'help' | null = null;
	public eudiAuthorizationUri: string | null = null;
	public eudiTransactionId: string | null = null;
	public eudiError: string = '';
	public eudiWaiting = false;
	public eudiHelpExpanded: Record<string, boolean> = {};
	private eudiPollSub: Subscription | null = null;
	/** Deadline de sesión EUDI (ms epoch); controlado por `side_max_time` del back. */
	private eudiSessionDeadlineMs: number | null = null;
	private eudiSessionWatchSub: Subscription | null = null;
	/** NIF-960: evita segundo validate / regenerar QR tras éxito. */
	private eudiValidateCompleted = false;

	/** Grupos de datos visibles en Figma (credencial PID). */
	public readonly eudiDataGroups = EUDI_DATA_GROUPS;

	get eudiEnabled(): boolean {
		// NIF-960: además del flag de perfil, solo INT/local (`environment.eudiWalletAllowed`).
		return (
			environment.eudiWalletAllowed === true && !!this.operation?.config?.eudi_wallet_enabled
		);
	}

	/** Solo EUDI Wallet (sin DNI/pasaporte/NIE): no hay pantalla física a la que «Volver atrás». */
	get isEudiOnlyMode(): boolean {
		return isEudiOnlyOcrConfig(this.operation?.config);
	}

	get isEudiMobileContext(): boolean {
		return this.isMidniMobileBrowser() || this.midniHandsetViewport;
	}

	/** FAQ acordeón: Q1 cambia de copy entre desktop (QR) y móvil (deep link). */
	get eudiFaqItems() {
		return buildEudiFaqItems(this.isEudiMobileContext);
	}

	/** Tiempo máximo de sesión EUDI (seg) = `side_max_time` OCR del back (default 150). */
	get eudiMaxSessionSeconds(): number {
		const fromBack = this.operation?.config?.setting?.side_max_time;
		return typeof fromBack === 'number' && fromBack > 0 ? fromBack : 150;
	}

	/** Subtítulo tile upload: duplicado vs falta cara genérica. */
	get flexibleUploadMissingSideDescriptionKey(): string {
		return this.flexibleUploadDuplicateSide
			? 'OCR.FLEXIBLE.UPLOAD_DUPLICATE_SIDE_DESCRIPTION'
			: 'OCR.FLEXIBLE.UPLOAD_MISSING_SIDE_DESCRIPTION';
	}

	/** UA móvil o viewport Handset: layout del scanner (clases, hint mismo dispositivo); no define el copy principal. */
	get isMidniMobileScanContext(): boolean {
		return this.isMidniMobileBrowser() || this.midniHandsetViewport;
	}

	/**
	 * Clave i18n de la instrucción bajo el scanner MiDNI.
	 * - Móvil o vista móvil: SCANNER_INSTRUCTION_MOBILE salvo cuando hay `alertContent` (solo alerta flotante).
	 * - Escritorio / no vista móvil: siempre SCANNER_INSTRUCTION y la alerta se muestra a la vez (flotante).
	 */
	get midniScannerInstructionI18nKey(): string {
		if (this.isMidniMobileScanContext) {
			if (this.alertContent.label) {
				return '';
			}
			return 'OCR.MIDNI.SCANNER_INSTRUCTION_MOBILE';
		}
		return 'OCR.MIDNI.SCANNER_INSTRUCTION';
	}

	private midniCountdownSub: Subscription | null = null;
	private isSharpValid: boolean = false;
	private isBrightnessValid: boolean = false;

	// Textos y alertas
	public subtitle: string = '';
	public helpTitle: string = '';
	private stickyTimer: any = null;
	/** Timer para mostrar «Escaneando…» tras 2 s con borde detectado sin verde (NIF-610). */
	private scanningTimer: ReturnType<typeof setTimeout> | null = null;
	private scanningAlertActive = false;
	private static readonly SCANNING_DELAY_MS = 2000;
	public centerScanText: string = '';
	public alertContent: IAlert = { label: '', status: 'default' };

	// Controles
	public formRect: FormControl = new FormControl(false);
	public testMode: boolean = false;

	// Document check
	private initOperation: boolean = false;
	public match: boolean = true;
	public check: boolean = false;
	public detected: boolean = false;
	public documentDetector: ObjectDetector;
	private runningMode: 'IMAGE' | 'VIDEO' = 'IMAGE';
	public results: ObjectDetectorResult = undefined;
	public ocrConf: IOcrConf;
	public animation: string = null;
	/** Opciones para ng-lottie con colores aplicados desde el JSON (diseño). */
	public lottieAnimationOptions: AnimationOptions | null = null;
	private lottieThemeSub: Subscription | null = null;
	private subscriptionTimers: Subscription = new Subscription();
	private ocrFrontConf: IOcr = JSON.parse(JSON.stringify(ocrFrontConf));
	private ocrBackConf: IOcr = JSON.parse(JSON.stringify(ocrBackConf));

	// Camara
	public video!: HTMLVideoElement;
	public canvas!: HTMLCanvasElement;
	public ctx!: CanvasRenderingContext2D;
	public stream: MediaStream;
	public allCameras: Array<MediaDeviceInfo> = [];
	public animationFrame: number;
	/** Zoom preview OCR; fijado al actualizar detección (no getter en plantilla). */
	public ocrPreviewZoomFactor: number = 1;
	public ocrPreviewZoomOffsetX: number = 0;
	public ocrPreviewZoomOffsetY: number = 0;
	public ocrPreviewTransformOrigin: string = '0 0';
	private lastAnalysisViewportAspect = 0;
	private cdr = inject(ChangeDetectorRef);

	// Historial de datos cada segundo
	private secondlyTimerSubscription: Subscription | null = null;
	private lastVideoTime: number = -1;
	private documentWithoutStrictMatchSince: number | null = null;
	private lastNoProgressLogAt: number = 0;
	/** Throttle telemetría periódica de centrado OCR automático (NIF-827). */
	private lastOcrAutoCenterLogAt: number = 0;
	private static readonly OCR_AUTO_CENTER_LOG_INTERVAL_MS = 6000;
	/** Tras promptRequiredSide evita que textAndSpeechByHistory reproduzca audio contradictorio. */
	private skipSidePromptHistoryOnce = false;

	// Imagen capturada
	public capturedImage: string | null = null;
	public showCapturedImage: boolean = false;
	public imageData16x9: string = '';

	constructor(
		public progressService: ProgressService,
		public processHelperService: ProcessHelperService,
		public audioService: AudioService,
		public positionDocumentService: PositionDocumentService,
		public cloudLogsService: CloudLogsService,
		public ocrService: OcrService,
		public designService: DesignService,
		private lottieThemeService: LottieThemeService,
		public auth: AuthService,
		public timerManagerService: OcrTimerManagerService,
		private loadingService: LoadingService,
		private route: ActivatedRoute,
		private fullscreenService: FullscreenService,
		private trans: TranslateService,
		private imageHelperService: ImageHelperService,
		private tts: TextToSpeechService,
		private scoringDocumentService: ScoringDocumentService,
		private cameraService: CameraService,
		private cameraFocusService: CameraFocusService,
		private screenShotService: ScreenshotService,
		private processService: ProcessService,
		private breakpointObserver: BreakpointObserver,
		private navHelpService: NavHelpService,
		private errorService: ErrorService,
		private masterService: MasterService,
		private operationService: OperationService,
		private identitySDKService: IdentitySDKService,
		private userAgentService: UserAgentService,
		private streamingManagementService: StreamingManagementService,
		private audioHistoryService: AudioHistoryService,
		private midniApiService: MiDNIApiService,
		private eudiApiService: EudiApiService,
		private koDebugImagesService: KoDebugImagesService,
		private configService: ConfigService,
	) {
		this.route.queryParams.subscribe((params: any) => {
			this.testMode = Boolean(params.testmode);
			this.browserstack = Boolean(params.browserstack);
		});

		this.midniHandsetViewport = this.breakpointObserver.isMatched(Breakpoints.Handset);
		this.breakpointObserver
			.observe([Breakpoints.Handset])
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.midniHandsetViewport = result.matches;
			});

		this.breakpointObserver
			.observe([Breakpoints.HandsetLandscape])
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.handsetLandscape = result.matches;
				if (this.handsetLandscape) {
					this.stopTotalTime();
					if (!this.pageWarning.show) {
						this.startWarningTime();
					}
				} else {
					// Resetear el estado de validación del documento cuando volvemos a portrait
					this.resetDocumentValidationState();
					this.startTotalTime();
					if (!this.pageWarning.show) {
						this.stopWarningTime();
					}
				}
			});
	}

	ngOnInit(): void {
		this.positionDocumentService.setIsMobile(this.userAgentService.isMobile());
		this.designService
			.getConfigDesign()
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => this.refreshOcrLottieOptions());
		this.cameraService.allCameras$.pipe(takeUntil(this.destroy$)).subscribe((cameras: MediaDeviceInfo[]) => {
			this.allCameras = cameras;
			this.updateCameraNavHelpItems();
		});
	}

	private updateCameraNavHelpItems(): void {
		const baseNavs = this.navsHelp.filter((nav: INavHelp) => nav.id !== NavHelpAction.ROTATE);
		const cameraCount = this.allCameras?.length ?? 0;
		this.navsHelp = cameraCount > 1 ? [this.navHelpService.getRotateCameraNav(), ...baseNavs] : baseNavs;
	}

	ngAfterViewInit(): void {
		// Mobile
		this.positionDocumentService.setIsMobile(this.userAgentService.isMobile());
		this.screenStartedAt = Date.now();

		// Marcar OCR como activo para evitar cambios automáticos de cámara
		this.cameraService.setProcessingOCR(true);

		// Asegurar que las cámaras se carguen inmediatamente para que estén disponibles cuando se envíe el resultado
		this.cameraService.getAllCameras();

		// Iniciar operación
		this.masterService.getMaster().subscribe((master: IMaster) => {
			if (master && !this.initOperation) {
				this.initOperation = true;
				this.processService.initOperation(this.operation.type);
			}
		});
		// NIF-1033: 1× mount si tutorial activo
		if (this.operation.config.tutorial) {
			this.cloudLogsService.sendCloudLogs(
				{ event: 'tutorialMount', operationType: 'OCR', tutorialActive: true },
				OcrLog.PRELOAD,
				LogType.Info,
			);
		}
		// Sin tutorial
		if (!this.operation.config.tutorial) {
			// EUDI-only: precargar solo 2 MP3 y abrir ya (no perder gesto/activación con el bundle OCR).
			if (isEudiOnlyOcrConfig(this.operation?.config)) {
				void this.bootstrapEudiOnlyEntry();
			} else {
				this.audioService.preloadOcrMrzAudios();
			}
		}
		this.checkCameraPermissions();

		// Load models
		this.processHelperService.getDocumentModel().subscribe((model: ObjectDetector) => {
			if (model) {
				this.documentDetector = model;
				// Si el video ya está listo (loadeddata ya se disparó), iniciar predictWebcam manualmente
				if (this.video && this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
					this.predictWebcam();
				}
				if (window.location.hostname !== 'localhost' && environment.saveScreenShot) {
					this.screenShotService.startSaveScreenshot(OcrLog.SCREENSHOT);
				}
			}
		});
	}

	public nextOperation(status: OPERATION_STATUS): void {
		this.operation.completed = true;
		const found = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
		if (found) {
			(found as IFinishOperation).config.status = status;
		}
	}

	private startWebcam(): void {
		if (this.permissionsCamera && !this.cameraService.hasGetUserMedia()) {
			this.setPageWarning(PAGE_WARNING.CAMERA_ERROR);
			this.cloudLogsService.sendCloudLogs(
				{ getUserMedia: 'getUserMedia is not supported by your browser' },
				OcrLog.PRELOAD,
				LogType.Error,
			);
			this.hideLoading();
			this.hidePreviewLoading();
		}
	}

	// #region SEND RESULT

	// #region SEND RESULT — Orquestación

	/** Envía el resultado OCR al back y procesa la respuesta (OK/KO). */
	private async sendResult(valid: boolean, status: TEST_STATUS = TEST_STATUS.OK): Promise<boolean> {
		this.beginSendResultRequest();
		this.ocrSendAttempts++;

		if (!valid) {
			// NIF-1033: guardar motivo en memoria; el log canónico va en verificationFail/Finish.
			this.lastCancelReason = status;
			this.lastCancelHttp = null;
		}

		const body: IOcrPayload = this.getBodyResult(valid, status);
		await this.attachFaceBboxIfNeeded(body);

		if (valid) {
			await this.showValidSendResultFeedback(body);
		} else {
			this.showInvalidSendResultLoading(status);
		}

		return this.postOcrResult(body, status);
	}

	// #endregion SEND RESULT — Orquestación

	// #region SEND RESULT — Preparación POST

	/** Estado inicial común antes del POST (flags, alertas, pausa de timers). */
	private beginSendResultRequest(): void {
		this.sendingResult = true;
		this.clearScanningAlert();
		this.centerScanText = '';
		this.alertContent = this.audioHistoryService.getEmptyAlert();

		// Pausar todos los timers mientras se procesa la petición para evitar que se muestre la ayuda
		this.stopTimersDuringRequest();
	}

	/** Feedback visual previo al POST cuando la captura local es válida. */
	private async showValidSendResultFeedback(body: IOcrPayload): Promise<void> {
		// Bloquear el loop de detección ANTES de cualquier otra acción
		// Prevenir capturas adicionales durante la animación de éxito
		this.showCapturedImage = true;

		// Mostrar la última imagen capturada o la enviada
		// Se hace para que el usuario no tenga sensación de que lentitud
		const documentTest: IOcr | undefined = this.getPendingOcrDocument();
		this.showCapturedImageDuringSend(
			documentTest?.photos?.length ? documentTest.photos[documentTest.photos.length - 1].img : body.image,
		);

		// NIF-802 upload: sin check/tirín (tec-scan está oculto en flexibleScreen=upload; el sonido
		// confundía como “OK final”). Solo loading; al encadenar 2 caras no se apaga entre POSTs.
		if (this.flexibleUploadSession) {
			this.showLoading(this.trans.instant('OCR.GENERAL.CHECKING_DATA'));
			this.cameraService.setProcessingOCR(true);
			return;
		}

		// Mostrar animación de verificación
		await this.showCheckAnimation();
		// Mostrar loading
		this.showLoading(this.trans.instant('OCR.GENERAL.CHECKING_DATA'));

		// Establecer flag para prevenir cambios automáticos de cámara durante el procesamiento OCR
		this.cameraService.setProcessingOCR(true);
	}

	/** Muestra loading en KO de cliente antes del POST (max intentos, permisos, etc.). */
	private showInvalidSendResultLoading(status: TEST_STATUS): void {
		if (
			status === TEST_STATUS.KO_MAXTRIES ||
			status === TEST_STATUS.KO_HELP ||
			status === TEST_STATUS.KO_CAMERA ||
			status === TEST_STATUS.KO_PERMISSION ||
			status === TEST_STATUS.KO_HANDSET_LANDSCAPE ||
			status === TEST_STATUS.KO_TUTORIAL
		) {
			this.showLoading(this.trans.instant('OCR.GENERAL.CHECKING_DATA'));
		}
	}

	// #endregion SEND RESULT — Preparación POST

	// #region SEND RESULT — HTTP y callbacks

	/** Ejecuta el POST y delega el manejo de la respuesta en callbacks dedicados. */
	private postOcrResult(body: IOcrPayload, status: TEST_STATUS): Promise<boolean> {
		return new Promise((resolve: (value: boolean) => void) => {
			// takeUntil(destroy$): no procesar respuestas si el componente ya pasó a POL.
			this.ocrService
				.sendInfoOcr(body)
				.pipe(takeUntil(this.destroy$))
				.subscribe({
					next: (response: IOcrResponse) => this.handleSendResultResponse(response, status, resolve),
					error: (error: { error: IOcrErrorResponse }) =>
						this.handleSendResultHttpError(error, status, resolve),
				});
		});
	}

	/** Rama `next` del subscribe: OK, KO en body o error interno de procesamiento. */
	private handleSendResultResponse(
		response: IOcrResponse,
		status: TEST_STATUS,
		resolve: (value: boolean) => void,
	): void {
		// Componente destruido (p. ej. ya en POL): ignorar respuesta tardía
		if (this.destroy$.closed) {
			resolve(false);
			return;
		}
		try {
			if (response.result === OPERATION_STATUS.OK) {
				this.processSendResultOk(response, resolve);
			} else {
				const error: { error: IOcrErrorResponse } = { error: response as unknown as IOcrErrorResponse };
				this.handleSendResultError(error, status, resolve);
			}
		} catch (error) {
			this.cameraService.setProcessingOCR(false);
			this.handleSendResultError(
				{ error: { error: { msg: 'Internal error processing OCR response' } } as any },
				status,
				resolve,
			);
		}

		this.sendingResult = false;
		// Encadenado upload 2 caras: mantener spinner hasta el siguiente POST (evita flash de UI upload).
		if (!this.flexibleUploadChaining) {
			this.hideLoading();
		}
	}

	/** Rama `error` del subscribe (fallo HTTP o body de error del servicio). */
	private handleSendResultHttpError(
		error: { error: IOcrErrorResponse },
		status: TEST_STATUS,
		resolve: (value: boolean) => void,
	): void {
		if (this.destroy$.closed) {
			resolve(false);
			return;
		}
		try {
			this.handleSendResultError(error, status, resolve);
			this.hideLoading();
		} catch (handlerError) {
			this.cameraService.setProcessingOCR(false);
			resolve(false);
		}
		this.sendingResult = false;
	}

	// #endregion SEND RESULT — HTTP y callbacks

	// #region SEND RESULT — Respuesta OK

	/** Procesa respuesta OK: notifica lado, aplica transición de cara y limpia estado. */
	private processSendResultOk(response: IOcrResponse, resolve: (value: boolean) => void): void {
		// Indicar que hay un espacio entre pruebas
		this.isGapBetweenTests = true;

		// Obtener el documento actual antes de procesarlo
		const currentDoc: IOcr | undefined = this.getPendingOcrDocument();
		const documentTestId: DOCUMENT_CHECK_ID | undefined = currentDoc?.documentTestId;

		// Notificar evento específico según el lado del documento
		this.notifyDocumentSideComplete(documentTestId, response);

		this.applySendResultOkSideAction(response, currentDoc, documentTestId);
		this.finalizeSendResultOk(response);
		resolve(true);
	}

	/**
	 * Decide la acción tras OK según `required_other_side` / `required_side` del back.
	 * - true + side: otra cara o repetir el mismo lado.
	 * - false: cierre OCR (última captura aceptada).
	 */
	private applySendResultOkSideAction(
		response: IOcrResponse,
		currentDoc: IOcr | undefined,
		documentTestId: DOCUMENT_CHECK_ID | undefined,
	): void {
		if (response.required_other_side && response.required_side) {
			// Back pide otro lado (o repetir el mismo si el usuario no cambió de cara)
			if (this.isRepeatRequiredSidePrompt(response.required_side)) {
				this.resumeCaptureForRequiredSide(response.required_side);
			} else {
				this.applyRequiredOtherSide(response.required_side);
			}
			return;
		}

		// PRE: tras anverso+reverso, si el back pide scan PDF417 (DNI_COL) entrar en modo barcode
		if (response.barcode?.required_scan && !response.barcode?.verified) {
			this.enterBarcodeScanMode(response);
			return;
		}

		// Back confirma que no queda otro lado (required_other_side: false)
		// Marcar el documento pendiente para que documentCheckCycle avance a finishValidation
		this.markOcrDocumentComplete(currentDoc);
		this.screenShotService.stopSaveScreenshot();

		// Notificar documento completado
		this.notifyDocumentComplete(response, documentTestId);
	}

	/** Limpieza común tras OK (preview, detección, operations, flag de cámara). */
	private finalizeSendResultOk(response: IOcrResponse): void {
		// Ocultar imagen capturada cuando termina el procesamiento
		this.hideCapturedImagePreview();
		// Reiniciar el detected
		this.detected = false;
		// Actualizar las operations con la response (si procede)
		this.updateOperations(response);
		// Limpiar flag de OCR en progreso
		this.cameraService.setProcessingOCR(false);
	}

	// #endregion SEND RESULT — Respuesta OK

	// #region SEND RESULT — Helpers documento y operations

	/** Primer slot de documento aún no marcado como `complete`. */
	private getPendingOcrDocument(): IOcr | undefined {
		return this.ocrConf.document.find((ocr: IOcr) => !ocr.complete);
	}

	private updateOperations(response: IOcrResponse): void {
		// Si no hay process, no hacer nada
		if (!response.process || !response.process?.length) return;

		// Si cambia el número de ops (p. ej. CHOOSE), sustituir el process entero
		if (this.operations?.length !== response.process.length) {
			this.operations = response.process;
			this.operationService.setOperations(this.operations);
			return;
		}

		// NIF-658: misma longitud → fusionar solo FORM (prefill/disabled tras OCR OK).
		// No reemplazar todo el process: en OK de 1.ª cara el OCR aún no está completed.
		const merged = mergeFormConfigFromProcess(this.operations, response.process);
		if (merged === this.operations) {
			return;
		}
		this.operations = merged;
		this.operationService.setOperations(this.operations);
	}

	/** Fuerza sync del process del back (p. ej. cierre barcode con misma longitud HOME/OCR/FINISH). */
	private forceSyncOperations(process: IOperation[] | undefined): void {
		if (!process?.length) return;
		this.operations = process;
		this.operationService.setOperations(this.operations);
	}

	/**
	 * NIF-960: con omitir POL tras EUDI, marcar POL `completed` en la lista local
	 * (el back suele borrar la fila; si aún viene incompleta, Finish no debe verla pendiente).
	 */
	private applyEudiSkipPolCompleted(process: IOperation[] | undefined): IOperation[] {
		const base = process?.length ? process : (this.operations ?? []);
		if (!this.operation?.config?.skip_pol_when_eudi_authenticated || !base.length) {
			return base;
		}
		return base.map((op) =>
			op.type === OPERATION_TYPE.POL && !op.completed ? { ...op, completed: true } : op,
		);
	}

	/** Marca el slot OCR pendiente como completado (cierre OK, transición de cara o barcode). */
	private markOcrDocumentComplete(doc: IOcr | undefined): void {
		// Si no hay documento pendiente, no hacer nada
		if (!doc) return;

		// Marcar el slot actual como completado (documentValidation no fuerza complete)
		doc.complete = true;
		doc.status = TestStatus.OnComplete;

		// Registrar hora de fin si aún no existe
		if (!doc.finishAt) doc.finishAt = new Date().toISOString();
	}

	// #endregion SEND RESULT — Helpers documento y operations

	// #region SEND RESULT — Errores POST

	/**
	 * Maneja los errores que se producen al enviar el resultado del OCR.
	 *
	 * @param error El error recibido de la respuesta del servicio OCR
	 * @param resolve La función resolve de la Promise para devolver el resultado
	 * @returns void
	 */
	private handleSendResultError(
		error: { error: IOcrErrorResponse },
		status: TEST_STATUS,
		resolve: (value: boolean) => void,
	): void {
		// Limpiar flag de OCR en progreso
		this.cameraService.setProcessingOCR(false);

		if (!status || status !== TEST_STATUS.OK) {
			this.lastCancelReason = this.lastCancelReason ?? status;
			this.lastCancelHttp = {
				error_code: error?.error?.error?.error_code,
				details_code: (error?.error as { details_code?: number | string })?.details_code,
				retry: error?.error?.retry,
			};
		}

		// Log del error
		this.cloudLogsService.sendCloudLogs({ error }, OcrLog.REQUEST, LogType.Warning);

		// Notificar error al SDK
		const currentDoc = this.ocrConf.document.find((ocr: IOcr) => !ocr.complete);
		const documentTestId = currentDoc?.documentTestId;
		const operationType =
			documentTestId === DOCUMENT_CHECK_ID.FRONT ? OperationType.OCR_FRONT : OperationType.OCR_BACK;

		this.identitySDKService.notifyOperationError(operationType, error?.error?.error?.msg || 'OCR Error', {
			documentTestId,
			errorCode: error?.error?.error?.error_code,
			status,
		});

		// Ocultar imagen capturada cuando hay error
		this.hideCapturedImagePreview();

		const errorBody = error?.error;
		// 10xx / 3099 (NIF-944): overlay global vía ErrorService, no pageWarning de operación.
		if (this.errorService.hasGeneralError({ ...error?.error })) {
			this.screenShotService.stopSaveScreenshot();
			resolve(false);
			return;
		}
		// Reinicio de pareja OCR: el back limpió text1/text2/side; volver al slot inicial del journey.
		if (errorBody?.required_other_side === false && errorBody?.required_side === null) {
			this.resetOcrPairCapture();
			const retry = errorBody.retry ?? false;
			const pairResetErrorCode = errorBody?.error?.error_code?.toString();
			if (errorBody.result === OPERATION_STATUS.KO && !retry) {
				// NIF-984 / NIF-1085: mensaje claro antes del fail (no bucket MRZ / tips).
				if (
					pairResetErrorCode === ERROR_CODE.DIFFERENT_DOCUMENTS ||
					pairResetErrorCode === ERROR_CODE.MISSING_ADDRESS
				) {
					this.handleInternalCodeError(pairResetErrorCode, errorBody);
				}
				this.verificationFail(OPERATION_STATUS.KO);
				this.screenShotService.stopSaveScreenshot();
				resolve(false);
				return;
			}
			const errorCode = pairResetErrorCode;
			// 2003/2070/2071 llegan con required_side null (back limpia lado); mostrar restricción, no tips nitidez.
			if (isOcrRestrictionErrorCode(errorCode)) {
				this.handleInternalCodeError(errorCode, errorBody);
				this.screenShotService.stopSaveScreenshot();
				resolve(false);
				return;
			}
			// NIF-984: 2073 recuperable → tile informativa (sin audio); Retry reanuda captura.
			if (errorCode === ERROR_CODE.DIFFERENT_DOCUMENTS) {
				this.handleInternalCodeError(errorCode, errorBody);
				resolve(false);
				return;
			}
			// NIF-1085: 2038 → tile dirección (no tips nitidez ni ADJUNTAR KO anverso/reverso).
			if (errorCode === ERROR_CODE.MISSING_ADDRESS) {
				this.handleInternalCodeError(errorCode, errorBody);
				resolve(false);
				return;
			}
			// NIF-1082: 2074 con pair-reset → tile ambas caras en una foto.
			if (errorCode === ERROR_CODE.BOTH_SIDES_IN_ONE_IMAGE) {
				this.handleInternalCodeError(errorCode, errorBody);
				resolve(false);
				return;
			}
			if (retry && errorCode === ERROR_CODE.NO_FACE_IN_DOCUMENT) {
				// 2032: un solo aviso (texto + audio) sin pisar con onInit.
				this.promptNoFaceInDocumentRetry();
				resolve(false);
				return;
			}
			// NIF-821: KO recuperable de recaptura → pantalla tips (no resume silencioso / onInit).
			if (retry && !isStorageRetryError(errorBody)) {
				this.showInsufficientOcrReadWarning();
			}
			resolve(!retry);
			return;
		}
		// KO con required_side: back pide otro lado; copy/audio sin avanzar journey (no es OK + 2ª cara).
		if (errorBody?.required_other_side && errorBody?.required_side) {
			const sameSideAgain = this.isRepeatRequiredSidePrompt(errorBody.required_side);
			const requiredSideErrorCode = errorBody?.error?.error_code?.toString();
			const retry = errorBody.retry ?? false;
			if (errorBody.result === OPERATION_STATUS.KO && !retry) {
				this.verificationFail(OPERATION_STATUS.KO);
				this.screenShotService.stopSaveScreenshot();
				resolve(false);
				return;
			}
			// Preparar slot de la cara pedida antes de la tile (retry continúa en esa cara).
			if (sameSideAgain) {
				const pendingDoc = this.ocrConf.document.find((ocr: IOcr) => !ocr.complete);
				if (pendingDoc) {
					pendingDoc.photos = [];
					pendingDoc.startAt = '';
					pendingDoc.status = TestStatus.OnInit;
				}
				this.scoringDocumentService.setDocumentValidation([]);
				this.timerManagerService.resetTotalTime();
				this.detected = false;
				this.syncPendingDocumentForRequiredSide(errorBody.required_side);
			} else {
				this.syncPendingDocumentForRequiredSide(errorBody.required_side);
			}
			// NIF-988: 2004 → tile de error (falta cara), no tips nitidez ni mensaje MRZ.
			// NIF-921: en upload Flexible → tile/copy de adjuntar (no «Escanea…» / CHANGE_SIDE).
			if (requiredSideErrorCode === ERROR_CODE.INCORRECT_SIDE) {
				if (this.flexibleUploadSession) {
					// NIF-1088: back resetea la pareja en 2004 → vaciar adjuntos (no reenviar cheque/reverso colgado).
					this.returnToFlexibleUploadUi({
						missingSide: true,
						duplicateSide: sameSideAgain,
					});
				} else {
					this.showIncorrectDocumentSideWarning();
				}
				resolve(false);
				return;
			}
			if (sameSideAgain) {
				this.resumeCaptureForRequiredSide(errorBody.required_side);
			} else {
				this.promptRequiredSide(errorBody.required_side);
			}
			if (retry) {
				if (this.flexibleUploadSession) {
					// Misma cara repetida → copy «misma foto»; falta otra cara → missing-side clásico.
					this.returnToFlexibleUploadUi({
						keepFiles: true,
						missingSide: true,
						duplicateSide: sameSideAgain,
					});
				} else {
					// promptRequiredSide ya hizo clearDataHistory + TTS; solo reiniciamos captura.
					this.resumeTimersAfterError();
				}
			}
			resolve(!retry);
			return;
		}

		// Gasto de intento (POST RETRY): si el back cierra la operación (retry: false), no reanudar captura.
		if (status === TEST_STATUS.RETRY) {
			const canRetry = errorBody?.retry ?? false;
			if (!canRetry) {
				this.verificationFail(OPERATION_STATUS.KO);
				this.screenShotService.stopSaveScreenshot();
				resolve(false);
				return;
			}
			// NIF-821: tras gasto de intento recuperable → pantalla tips intermedios.
			if (!isStorageRetryError(errorBody)) {
				this.showInsufficientOcrReadWarning();
			}
			resolve(false);
			return;
		}

		// Error controlado
		const errorCode: string = error?.error?.error?.error_code?.toString();
		if (errorCode) {
			// Cambio de modo automático a manual si procede
			if (
				(this.operation.config.mode === MODE.AUTO || this.operation.config.mode === MODE.FLEXIBLE) &&
				this.operation.config?.fallback?.type === MODE.MANUAL &&
				(status === TEST_STATUS.KO ||
					status === TEST_STATUS.KO_MAXTRIES ||
					status === TEST_STATUS.KO_HELP ||
					errorCode === ERROR_CODE.FALLBACK_MANUAL)
			) {
				this.operation.config.mode = MODE.MANUAL;
				// Reanudar timers
				this.resumeTimersAfterError();
				return;
			}

			const retry = error?.error?.retry ?? false;

			// 2003/2070/2071: mensaje «documento no permitido» (también si retry=false; no tips nitidez).
			if (isOcrRestrictionErrorCode(errorCode)) {
				this.handleInternalCodeError(errorCode, error?.error);
				this.screenShotService.stopSaveScreenshot();
				resolve(false);
				return;
			}

			// NIF-984: 2073 → tile informativa (sin audio); Retry reanuda. Sin intentos → fail.
			if (errorCode === ERROR_CODE.DIFFERENT_DOCUMENTS) {
				if (error?.error?.result === OPERATION_STATUS.KO && !retry) {
					this.handleInternalCodeError(errorCode, error?.error);
					this.verificationFail(OPERATION_STATUS.KO);
					this.screenShotService.stopSaveScreenshot();
					resolve(false);
					return;
				}
				this.handleInternalCodeError(errorCode, error?.error);
				resolve(false);
				return;
			}

			// NIF-1085: 2038 → tile dirección; no tips / ADJUNTAR KO.
			if (errorCode === ERROR_CODE.MISSING_ADDRESS) {
				if (error?.error?.result === OPERATION_STATUS.KO && !retry) {
					this.handleInternalCodeError(errorCode, error?.error);
					this.verificationFail(OPERATION_STATUS.KO);
					this.screenShotService.stopSaveScreenshot();
					resolve(false);
					return;
				}
				this.handleInternalCodeError(errorCode, error?.error);
				resolve(false);
				return;
			}

			// NIF-1082: 2074 ambas caras en una foto → tile adjuntar; no tips «falta anverso».
			if (errorCode === ERROR_CODE.BOTH_SIDES_IN_ONE_IMAGE) {
				if (error?.error?.result === OPERATION_STATUS.KO && !retry) {
					this.handleInternalCodeError(errorCode, error?.error);
					this.verificationFail(OPERATION_STATUS.KO);
					this.screenShotService.stopSaveScreenshot();
					resolve(false);
					return;
				}
				this.handleInternalCodeError(errorCode, error?.error);
				resolve(false);
				return;
			}

			if (error?.error?.result === OPERATION_STATUS.KO && !retry) {
				this.verificationFail(OPERATION_STATUS.KO);
				this.screenShotService.stopSaveScreenshot();
				resolve(false);
				return;
			}

			// NIF-821: retry de recaptura → tips; UX dedicada (2004/2073/2074/2038) se mantiene.
			// 2003: restricción (misma UX que 2070). 2032: guía en pair-reset; 2099: HTTP (NIF-646).
			const isRecaptureRetry =
				retry && errorCode !== ERROR_CODE.NO_FACE_IN_DOCUMENT && !isStorageRetryError(error?.error);
			const hasDedicatedRetryUi =
				errorCode === ERROR_CODE.INCORRECT_SIDE ||
				errorCode === ERROR_CODE.DIFFERENT_DOCUMENTS ||
				errorCode === ERROR_CODE.BOTH_SIDES_IN_ONE_IMAGE ||
				errorCode === ERROR_CODE.MISSING_ADDRESS;

			if (isRecaptureRetry && !hasDedicatedRetryUi) {
				this.showInsufficientOcrReadWarning();
			} else {
				this.handleInternalCodeError(errorCode, error?.error);
				if (isRecaptureRetry) {
					this.resumeAfterRetryableError();
				}
			}

			resolve(!retry);
		} else {
			// Error no controlado
			// TODO: estandarizar
			this.setPageWarning(PAGE_WARNING.SERVER_ERROR);
			this.loading.show = false;
			this.screenShotService.stopSaveScreenshot();
			resolve(false);
		}
	}

	// #endregion SEND RESULT — Errores POST

	// #endregion SEND RESULT

	/**
	 * Maneja los errores internos relacionados con códigos específicos de error.
	 *
	 * @param errorCode El código de error de tipo `ERROR_CODE` que se ha producido.
	 * @param errorBody Cuerpo de error OCR (país en 2071, etc.).
	 *
	 * Códigos 2003/2070/2071: documento o país no permitido → pantalla «Documento no aceptado» (NIF-1023).
	 * Código 2073: anverso/reverso de documentos distintos (NIF-984; no reutilizar copy MRZ).
	 *
	 * @see {@link https://docs.google.com/document/d/1Ei8S6iVtO9VDZus5y-vde3ZIIOoZIWLL1ctdKkKhp78/edit?pli=1&tab=t.0#heading=h.4w8hp5awjwp2} Documento de referencia de códigos de error.
	 */
	private handleInternalCodeError(errorCode: string, errorBody?: IOcrErrorResponse): void {
		if (this.midniScreen !== null || this.eudiScreen !== null) return;
		// https://docs.google.com/document/d/1Ei8S6iVtO9VDZus5y-vde3ZIIOoZIWLL1ctdKkKhp78/edit?pli=1&tab=t.0#heading=h.4w8hp5awjwp2
		if (errorCode === ERROR_CODE.ERROR_MRZ) {
			this.clearAllDocumentSide();
			this.showGenericError();
		} else if (errorCode === ERROR_CODE.DIFFERENT_DOCUMENTS) {
			this.clearAllDocumentSide();
			this.showDifferentDocumentsWarning();
		} else if (errorCode === ERROR_CODE.MISSING_ADDRESS) {
			this.showMissingAddressWarning();
		} else if (errorCode === ERROR_CODE.BOTH_SIDES_IN_ONE_IMAGE) {
			// NIF-1082: un archivo con ambas caras → tile adjuntar (no «falta cara»).
			this.showFlexibleUploadBothSidesInOneWarning();
		} else if (isOcrRestrictionErrorCode(errorCode)) {
			this.showOcrRestrictionError(errorCode, errorBody);
		} else if (errorCode === ERROR_CODE.INCORRECT_SIDE) {
			// NIF-921: en adjuntar no usar copy/TTS de escaneo («Escanea…», «dale la vuelta»).
			if (this.flexibleUploadSession) {
				// NIF-1088: vaciar ficheros; keepFiles dejaba basura (p. ej. cheque) en el siguiente Continuar.
				this.returnToFlexibleUploadUi({
					missingSide: true,
					duplicateSide: true,
				});
				return;
			}
			if (isFirstSideScanUtil(this.ocrConf)) {
				return;
			}
			this.changeDocumentSide();
			// NIF-988: tile a pantalla completa (no solo alerta flotante).
			this.showIncorrectDocumentSideWarning();
		} else if (errorCode === ERROR_CODE.NO_FACE_IN_DOCUMENT) {
			this.promptNoFaceInDocumentRetry();
		} else if (errorCode === ERROR_CODE.INSUFFICIENT_OCR_DATA) {
			this.showInsufficientOcrReadWarning();
		} else {
			this.showGenericError();
		}
	}

	/** Muestra la ayuda de documento no aceptado con el mensaje de restricción 2003/2070/2071. */
	private showOcrRestrictionError(errorCode: string, errorBody?: IOcrErrorResponse): void {
		const message = resolveOcrRestrictionErrorMessage(this.trans, errorCode, errorBody);
		this.restrictionErrorMsg = message ?? '';
		this.setPageHelp('invalid_doc');
		if (!message) {
			return;
		}
		// speak() resuelve MP3 por clave i18n; el texto traducido forzaría TTS del navegador.
		const audioKey = resolveOcrRestrictionErrorAudioKey(errorCode);
		this.tts.speak(audioKey ?? message, true);
	}

	private clearAllDocumentSide(): void {
		this.ocrConf.document.forEach((ocr: IOcr) => {
			ocr.complete = false;
			ocr.status = TestStatus.OnInit;
			ocr.startAt = '';
			ocr.finishAt = '';
			ocr.photos = [];
			ocr.countTries = 1;
		});
	}

	/** Tras KO con `required_side: null`: nueva pareja de capturas desde el slot inicial. */
	private resetOcrPairCapture(): void {
		const freshDoc: IOcr = JSON.parse(JSON.stringify(this.ocrFrontConf));
		freshDoc.messages.onInit = resolveFirstScanOnInitKey(this.operation.config?.allowed_docs);
		this.ocrConf.document = [freshDoc];
		this.scoringDocumentService.setDocumentValidation([]);
		this.timerManagerService.resetTotalTime();
		this.detected = false;
		this.audioHistoryService.clearDataHistory();
	}

	private changeDocumentSide(): void {
		if (this.ocrConf?.document?.length) {
			if (this.ocrConf.document.length === 1) {
				// Añadimos el otro lado
				const srcConf = this.ocrConf.document.find(
					(ocr: IOcr) => ocr.documentTestId === DOCUMENT_CHECK_ID.FRONT,
				)
					? this.ocrFrontConf
					: this.ocrBackConf;
				const nextConf: IOcr = JSON.parse(JSON.stringify(srcConf));
				const foundIndexDocument = this.ocrConf.document.findIndex(
					(ocr: IOcr) => ocr.documentTestId === nextConf.documentTestId,
				);
				if (foundIndexDocument > -1) {
					this.ocrConf.document[foundIndexDocument] = nextConf;
				} else {
					this.ocrConf.document.push(nextConf);
				}
				this.audioHistoryService.clearDataHistory();
			} else {
				this.clearAllDocumentSide();
			}
		}
	}

	// #region SEND RESULT — Transición de caras

	/** KO / mismo lado otra vez: back pide `required_side`; copy/audio sin avanzar journey. */
	private promptRequiredSide(requiredSide: 'front' | 'back'): void {
		this.syncPendingDocumentForRequiredSide(requiredSide);
		const initKey = this.flexibleUploadSession
			? requiredSide === 'front'
				? 'OCR.FLEXIBLE.UPLOAD_ATTACH_FRONT'
				: 'OCR.FLEXIBLE.UPLOAD_ATTACH_BACK'
			: requiredSide === 'front'
				? 'OCR.FRONT.INSTRUCTIONS.TEXT.ONINIT'
				: 'OCR.BACK.INSTRUCTIONS.TEXT.ONINIT';
		const currentDoc = this.ocrConf.document.find((ocr: IOcr) => !ocr.complete);
		if (currentDoc) {
			currentDoc.messages.onInit = initKey;
		}
		this.audioHistoryService.clearDataHistory();
		this.skipSidePromptHistoryOnce = true;
		// Encadenado de 2 ficheros: el siguiente sendResult va ya; sin TTS/UI intermedia.
		if (this.flexibleUploadChaining) {
			return;
		}
		// NIF-921 Carmen: en upload Flexible el aviso de cara es fullscreen, sin audio.
		if (this.flexibleUploadSession) {
			this.updateSubtitle();
			return;
		}
		// forceAudio: TextToSpeechService ignora el mismo clip si ya se locutó (NIF-575).
		this.tts.speak(initKey, true);
		this.updateSubtitle();
	}

	/** Alinea el slot pendiente con la cara que pide el back (evita audio de «sin foto» en slot opuesto). */
	private syncPendingDocumentForRequiredSide(requiredSide: 'front' | 'back'): void {
		const pendingDoc = this.ocrConf.document.find((ocr: IOcr) => !ocr.complete);
		if (!pendingDoc) {
			return;
		}

		const targetId = requiredSide === 'front' ? DOCUMENT_CHECK_ID.FRONT : DOCUMENT_CHECK_ID.BACK;
		if (pendingDoc.documentTestId === targetId) {
			return;
		}

		const srcConf = requiredSide === 'front' ? this.ocrFrontConf : this.ocrBackConf;
		pendingDoc.documentTestId = targetId;
		pendingDoc.messages.onInit = srcConf.messages.onInit;
		pendingDoc.status = TestStatus.OnInit;
		pendingDoc.photos = [];
		pendingDoc.startAt = '';
	}

	/** El slot pendiente ya coincide con `required_side`; no añadir una entrada nueva. */
	private isRepeatRequiredSidePrompt(requiredSide: 'front' | 'back'): boolean {
		// 1ª captura usa siempre slot FRONT como placeholder: coincidir con required_side
		// `front` no es repetición — hay que avanzar (applyRequiredOtherSide) y mostrar Lottie.
		if (isFirstSideScanUtil(this.ocrConf)) return false;
		const pendingDoc = this.ocrConf.document.find((ocr: IOcr) => !ocr.complete);
		return pendingDoc?.documentTestId === requiredSide;
	}

	/** OK con required_side repetido: no push otra entrada; reiniciar captura y relanzar audio. */
	private resumeCaptureForRequiredSide(requiredSide: 'front' | 'back'): void {
		const pendingDoc = this.ocrConf.document.find((ocr: IOcr) => !ocr.complete);
		if (pendingDoc) {
			pendingDoc.photos = [];
			pendingDoc.startAt = '';
			pendingDoc.status = TestStatus.OnInit;
		}
		this.scoringDocumentService.setDocumentValidation([]);
		this.timerManagerService.resetTotalTime();
		this.detected = false;
		this.promptRequiredSide(requiredSide);
	}

	/** OK: avanza al siguiente lado indicado por el back (`required_side`). */
	private applyRequiredOtherSide(requiredSide: 'front' | 'back'): void {
		// Marcar la 1ª captura antes de añadir el siguiente lado (alineado a manual-ocr).
		// Si se reemplazara la misma entrada (p. ej. reverso primero → required_side front),
		// complete quedaría en un objeto huérfano y isFirstSideScan seguiría en true.
		const currentDoc = this.ocrConf.document.find((ocr: IOcr) => !ocr.complete);
		this.markOcrDocumentComplete(currentDoc);

		const srcConf = requiredSide === 'front' ? this.ocrFrontConf : this.ocrBackConf;
		const nextConf: IOcr = JSON.parse(JSON.stringify(srcConf));
		// Usar la clave de texto ONINIT sin la mención a pasaporte
		nextConf.messages.onInit =
			requiredSide === 'front' ? 'OCR.FRONT.INSTRUCTIONS.TEXT.ONINIT' : 'OCR.BACK.INSTRUCTIONS.TEXT.ONINIT';
		this.ocrConf.document.push(nextConf);
		// Reiniciar contador por lado: el semimanual solo persiste si ya se activó en el lado anterior.
		this.timerManagerService.resetTotalTime();
		this.detected = false;
		// Audio del lado que falta (required_side); el back indica cuál falta si el usuario repite el mismo.
		this.promptRequiredSide(requiredSide);
	}

	// #endregion SEND RESULT — Transición de caras

	private verificationFail(status: OPERATION_STATUS): void {
		this.operation.completed = true;
		const cancelReason = this.lastCancelReason ?? PAGE_WARNING.VERIFICATION_FAIL;
		const pendingDoc = this.getPendingOcrDocument();
		const videoWidth = this.video?.videoWidth ?? 0;
		const videoHeight = this.video?.videoHeight ?? 0;
		this.cloudLogsService.sendCloudLogs(
			{
				status,
				observations: PAGE_WARNING.VERIFICATION_FAIL,
				cancelReason,
				testStatus: this.lastCancelReason,
				operationType: 'OCR',
				tutorialActive: Boolean(this.operation?.config?.tutorial),
				pageWarning: { type: this.pageWarning?.type, show: this.pageWarning?.show },
				warningTime: this.timerManagerService.warningTime,
				totalTime: this.timerManagerService.totalTime,
				warningTimeThreshold: OCR_WARNING_TIME,
				screenDurationMs: Date.now() - this.screenStartedAt,
				hasVideoFrame: videoWidth > 0,
				videoWidth,
				videoHeight,
				photosStored: pendingDoc?.photos?.length ?? 0,
				sendAttempts: this.ocrSendAttempts,
				error_code: this.lastCancelHttp?.error_code,
				details_code: this.lastCancelHttp?.details_code,
				retry: this.lastCancelHttp?.retry,
			},
			OcrLog.FINISH,
			LogType.Warning,
		);
		this.processService.unSubcribeCheckStatus();
		this.operations.forEach((op: IOperation) => {
			if (op.type !== OPERATION_TYPE.FINISH) {
				op.completed = true;
			}
		});
		const foundFinish = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
		if (foundFinish) {
			(foundFinish as IFinishOperation).config.status = status;
		} else {
			this.setPageWarning(PAGE_WARNING.VERIFICATION_FAIL);
		}
	}

	/**
	 * Selecciona la mejor foto del array basándose en:
	 * 1. Cumplir requisitos mínimos de nitidez
	 * 2. Maximizar un score combinado de nitidez (70%) + tamaño del documento (30%)
	 */
	private selectBestPhoto(photos: IDocumentTestPhoto[]): IDocumentTestPhoto | null {
		if (!photos || photos.length === 0) return null;

		const minFourier = this.ocrConf.general.minSharpnessFourier;
		const minLapacian = this.ocrConf.general.minSharpnessLapacian;

		// Filtrar fotos que cumplen requisitos mínimos de nitidez
		const validPhotos = photos.filter((p) => p.fourier >= minFourier && p.lapacian >= minLapacian);

		// Si hay fotos válidas, seleccionar la mejor; si no, usar todas como fallback
		const candidatePhotos = validPhotos.length > 0 ? validPhotos : photos;

		// Calcular valores máximos para normalizar (evitar división por 0)
		const maxSharpness = Math.max(...candidatePhotos.map((p) => p.fourier + p.lapacian), 1);
		const maxArea = Math.max(
			...candidatePhotos.map((p) => Math.abs((p.docSize?.width || 0) * (p.docSize?.height || 0))),
			1,
		);

		// Resetear selected en todas las fotos
		photos.forEach((p) => (p.selected = false));

		// Seleccionar la foto con mejor score combinado
		const bestPhoto = candidatePhotos.reduce((best, current) => {
			const currentSharpness = current.fourier + current.lapacian;
			const currentArea = Math.abs((current.docSize?.width || 0) * (current.docSize?.height || 0));
			// Normalizar y combinar: 70% nitidez + 30% tamaño documento
			const currentScore = (currentSharpness / maxSharpness) * 0.7 + (currentArea / maxArea) * 0.3;

			const bestSharpness = best.fourier + best.lapacian;
			const bestArea = Math.abs((best.docSize?.width || 0) * (best.docSize?.height || 0));
			const bestScore = (bestSharpness / maxSharpness) * 0.7 + (bestArea / maxArea) * 0.3;

			return currentScore > bestScore ? current : best;
		});

		// Marcar la mejor foto como seleccionada
		if (bestPhoto) {
			bestPhoto.selected = true;
		}

		return bestPhoto;
	}

	// #region SEND RESULT — Payload y notificaciones SDK

	private getBodyResult(valid: boolean, status: TEST_STATUS): IOcrPayload {
		let image: string = '';
		let selectedPhoto: IDocumentTestPhoto | undefined;
		const documentTest: IOcr = this.ocrConf.document.find((ocr: IOcr) => !ocr.complete);
		if (documentTest && documentTest.photos?.length) {
			// Seleccionar la mejor foto: priorizar nitidez Y tamaño del documento
			selectedPhoto = this.selectBestPhoto(documentTest.photos);
			if (selectedPhoto?.img) {
				image = selectedPhoto.img;
			}
		}
		// Usar allCamerasValue para obtener el valor actual sincronizadamente
		// Esto asegura que siempre tengamos las cámaras disponibles cuando se envía el resultado
		let camerasToUse: MediaDeviceInfo[] = this.allCameras;
		if (this.cameraService.allCamerasValue && this.cameraService.allCamerasValue.length > 0) {
			camerasToUse = this.cameraService.allCamerasValue;
		}
		const cameras = camerasToUse.map((c: MediaDeviceInfo) => c.label);

		this.koDebugImagesService.sendOcrKoDebugLogs(
			valid,
			status,
			'ocr',
			this.ocrConf.document,
			this.capturedImage,
			this.imageData16x9,
			{
				operationType: this.operation?.type,
			},
		);

		const result: IOcrPayload = {
			status: valid,
			data: JSON.stringify({
				status: status,
				ocrConf: {
					...this.ocrConf,
					document: this.ocrConf.document.map(({ photos, ...rest }) => ({
						...rest,
						photos: photos.map(({ img, ...rest2 }) => rest2),
					})),
				},
			}),
			cameras,
			device_id: this.auth.getDeviceId(),
		};
		// Si no tiene imagen y estamos en semimanual con imagen capturada, usamos esa imagen
		if (
			!image &&
			this.capturedImage &&
			(this.isModeSemiManual || this.flexibleScreen === 'upload' || this.flexibleScreen === 'photo')
		) {
			image = this.capturedImage;
		}

		// Si tiene image, añadirla
		if (image) result.image = image;

		// Si es valid y no tiene image, añadir string vacío
		if (valid && !image) result.image = '';

		if (this.resolveIsPassport()) {
			result.is_passport = true;
		}

		// NIF-921: método real para Admin (UPLOAD|PHOTO|SCAN); MIDNI/EUDI los escribe el back.
		result.capture_method = resolveOcrCaptureMethod({
			flexibleUploadSession: this.flexibleUploadSession,
			flexibleScreen: this.flexibleScreen,
			isModeSemiManual: this.isModeSemiManual,
		});

		return result;
	}

	private shouldSendFaceBbox(documentTest: IOcr | undefined): boolean {
		return (
			this.operation?.config?.obfuscation_photo === true &&
			documentTest?.documentTestId !== DOCUMENT_CHECK_ID.BACK
		);
	}

	/** Inferencia puntual sobre el JPEG del POST; no usa el loop VIDEO (maxResults 2). */
	private async attachFaceBboxIfNeeded(body: IOcrPayload): Promise<void> {
		const documentTest = this.getPendingOcrDocument();
		if (!body.image || !this.shouldSendFaceBbox(documentTest)) {
			return;
		}
		const faceBbox = await detectFaceBboxOnJpegDataUrl(
			this.documentDetector,
			body.image,
			this.positionDocumentService,
			this.runningMode,
		);
		if (faceBbox) {
			body.face_bbox = faceBbox;
		}
	}

	private buildOcrPhotoRecord(imageHelper: IImageHelper): IDocumentTestPhoto {
		return {
			img: this.imageData16x9,
			label: this.cameraService.getCurrentDeviceLabel(),
			lapacian: imageHelper.lapacian,
			fourier: imageHelper.fourier,
			docSize: this.positionDocumentService.getDocumentDetection(this.results?.detections)?.boundingBox,
			gridAnalysisBrightness: imageHelper.gridAnalysisBrightness,
			consecutiveCellsWith95: imageHelper.consecutiveCellsWith95,
			consecutiveCellsWith98: imageHelper.consecutiveCellsWith98,
			selected: false,
		};
	}

	/** Pasaporte según detección actual o mayoría en scoring reciente; omite el campo en DNI/NIE. */
	private resolveIsPassport(): boolean {
		if (!this.isPassportAllowed) return false;

		const detection = this.positionDocumentService.getDocumentDetection(this.results?.detections);
		if (detection?.name === MODEL_DOCUMENT_CATEGORY.PASSPORT) return true;

		const validation = this.scoringDocumentService.getDocumentValidation();
		if (!validation.length) return false;

		const recent = validation.slice(-20);
		const passportCount = recent.filter(
			(r: IDocumentResult) => r.categoryName === MODEL_DOCUMENT_CATEGORY.PASSPORT,
		).length;
		return passportCount > recent.length / 2;
	}

	/** Slot OCR pendiente (`front`/`back`). En ayuda, `front` tras 1ª cara → Figma `5372:30263` (AYUDA - ANVERSO). */
	public getDocumentSideId(): DOCUMENT_CHECK_ID {
		return this.ocrConf.document.find((ocr: IOcr) => !ocr.complete)?.documentTestId;
	}

	/**
	 * Notifica al SDK según el lado del documento procesado
	 */
	private notifyDocumentSideComplete(documentTestId: DOCUMENT_CHECK_ID, response: IOcrResponse): void {
		if (documentTestId === DOCUMENT_CHECK_ID.FRONT) {
			this.identitySDKService.notifyOperationSuccess(OperationType.OCR_FRONT, {
				documentTestId,
				result: response.result,
				response: response,
			});
		} else if (documentTestId === DOCUMENT_CHECK_ID.BACK) {
			this.identitySDKService.notifyOperationSuccess(OperationType.OCR_BACK, {
				documentTestId,
				result: response.result,
				response: response,
			});
		}
	}

	/**
	 * Notifica al SDK cuando se completa todo el procesamiento del documento
	 */
	private notifyDocumentComplete(response: IOcrResponse, documentTestId: DOCUMENT_CHECK_ID): void {
		this.identitySDKService.notifyOperationSuccess(OperationType.OCR_COMPLETE, {
			documentType: this.operation.type,
			documentTestId,
			result: response.result,
			response: response,
		});
	}

	// #endregion SEND RESULT — Payload y notificaciones SDK

	/*
	 * Animación guía OCR (Lottie en tec-scan).
	 * Marco con margen 12px: `.pwcs-lottie-frame` en pwcs-process-theme (NIF-610 / QA Carmen).
	 * Transición 2ª cara: archivo según cara pendiente (no el placeholder FRONT de 1ª captura).
	 */
	private setAnimation(documentTest: IOcr): void {
		// Iniciamos animación
		if (documentTest && documentTest.showAnimation) {
			documentTest.showAnimation = false;
			let newAnimation: string = '';
			const hasCompletedSide = this.ocrConf.document.some((ocr: IOcr) => ocr.complete);
			if (hasCompletedSide) {
				// Según la cara que toca (no el id del slot completo: la 1ª captura
				// puede ser reverso físico con placeholder FRONT).
				newAnimation = this.getDniPhotoSideAsset(
					documentTest.documentTestId === DOCUMENT_CHECK_ID.FRONT
						? 'assets/animations/back-to-front.json'
						: 'assets/animations/front-to-back.json',
				);
			} else {
				newAnimation = this.getDniPhotoSideAsset('assets/animations/init-front.json');
			}
			if (newAnimation !== this.animation) {
				this.animation = newAnimation;
				this.match = true;
				this.refreshOcrLottieOptions();
			}
		}
	}

	/** Carga el JSON de Lottie y aplica colores desde las variables CSS del diseño. */
	private refreshOcrLottieOptions(): void {
		this.lottieThemeSub?.unsubscribe();
		if (!this.animation) {
			this.lottieAnimationOptions = null;
			return;
		}
		this.lottieThemeSub = this.lottieThemeService
			.loadThemedAnimation(this.animation)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (opts) => {
					this.lottieAnimationOptions = opts;
				},
				error: () => {
					this.lottieAnimationOptions = { path: this.animation! };
				},
			});
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public loopComplete(event: BMCompleteLoopEvent): void {
		this.animation = null;
		this.lottieAnimationOptions = null;
		this.match = false;
		// Al terminar la animación, comienza el siguiente test
		this.isGapBetweenTests = false;
	}

	private async showCheckAnimation(): Promise<void> {
		return new Promise((resolve) => {
			this.clearScanningAlert();
			this.check = true;
			timer(1000).subscribe(() => {
				this.check = false;
				resolve();
			});
			this.audioService.playSuccessAudio();
		});
	}

	/*
	 * Tutorial
	 */
	public async finishTutorial(): Promise<void> {
		// NIF-936: no revelar el preview hasta que app-video (y streaming) estén listos.
		if (!this.tutorialContinueEnabled) return;

		this.operation.config.tutorial = false;
		this.cloudLogsService.sendCloudLogs(
			{ event: 'tutorialDismissed', operationType: 'OCR' },
			OcrLog.IN_PROCESS,
			LogType.Info,
		);
		this.stopWarningTime();
		// NIF-801: limpiar totalTime por si hubo leak en tutorial; captura lo arranca en documentCheckCycle
		this.stopTotalTime();
		this.timerManagerService.resetTotalTime();
		this.audioService.preloadOcrMrzAudios();
		// Verificar streaming antes de permitir continuar (modo estricto)
		try {
			const isRecordingRequired = await this.streamingManagementService.isRecordingRequired();

			if (isRecordingRequired) {
				try {
					// Esperar a que el streaming esté listo antes de continuar
					await this.streamingManagementService.waitForStreamingReady();
				} catch (streamingError: any) {
					// MODO ESTRICTO: Si required_recording está activo, el streaming es OBLIGATORIO
					this.cloudLogsService.sendCloudLogs(
						{ ocr: `Streaming connection failed after tutorial - Aborting: ${streamingError?.toString()}` },
						OcrLog.IN_PROCESS,
						LogType.Error,
					);

					if (!this.streamingManagementService.isStreamingRetryInProgress()) {
						// Mostrar pantalla de error con botón de reintento
						this.errorService.setStreamingError(async () => {
							await this.streamingManagementService.retryStreaming(() => this.errorService.clearError());
						});
					}
					return;
				}
			}
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ ocr: `Error checking streaming in finishTutorial: ${error?.toString()}` },
				OcrLog.IN_PROCESS,
				LogType.Error,
			);
		}

		// Fullscreen
		if (!this.operation.config.iframe) {
			this.fullscreenService.enterFullscreen();
		}

		// NIF-960: solo EUDI → entrar al wallet tras cerrar tutorial
		void this.bootstrapEudiOnlyEntry();
	}

	/*
	 * Controles de la cámara
	 */
	public onCameraError(err: any): void {
		this.hideLoading();
		this.hidePreviewLoading();
		if (err?.name === 'NotAllowedError') {
			if (!this.browserstack) {
				this.permissionsCamera = false;
				this.updateSubtitle();
				this.onPermissionsDenied();
			}
		} else {
			this.setPageWarning(PAGE_WARNING.CAMERA_ERROR);
			this.stopTotalTime();
			this.startWarningTime();
		}
	}

	public onCameraPermissionRevoked(): void {
		this.stopCameraCaptureLoop();
		this.hideLoading();
		this.hidePreviewLoading();
		if (!this.browserstack) {
			this.permissionsCamera = false;
			this.updateSubtitle();
			this.onPermissionsDenied();
		}
	}

	private stopCameraCaptureLoop(): void {
		if (this.animationFrame) {
			window.cancelAnimationFrame(this.animationFrame);
			this.animationFrame = 0;
		}
		if (this.video) {
			this.video.removeEventListener('loadeddata', this.predictWebcam);
		}
	}

	public onCameraStarted(camera: {
		stream: MediaStream;
		video: HTMLVideoElement;
		canvas: HTMLCanvasElement;
		ctx: CanvasRenderingContext2D;
	}): void {
		this.video = camera.video;
		this.canvas = camera.canvas;
		this.ctx = camera.ctx;
		this.stream = camera.stream;
		this.positionDocumentService.resetDocumentFitZoom();
		if (this.canvas?.width && this.canvas?.height) {
			this.positionDocumentService.setControlRect(this.canvas, this.getAnalysisViewportAspect());
		}
		this.syncOcrPreviewZoom();

		if (!this.cameraPermissionsReady) {
			this.permissionsCamera = true;
			this.onPermissionsGranted();
		} else {
			this.resetTestClocksAfterPermissionsGranted();
			this.resumeTestTimersIfApplicable();
			this.resetOcrCaptureAfterCameraPermissionLoss();
		}

		// Añadir el event listener cuando tengamos el video, independientemente del modelo
		this.addPredictWebcamEvent();

		this.getAllCameras();
		this.triggerOcrAutofocusTarget('camera_started');

		// Esperar a que el streaming esté completamente activo antes de continuar
		void this.waitForStreamingAndStartProcessing().catch(() => undefined);
	}

	/**
	 * Espera a que el streaming esté activo antes de iniciar el procesamiento OCR
	 * Si required_recording está activo y el streaming falla, ABORTA la operación (modo estricto)
	 */
	private async waitForStreamingAndStartProcessing(): Promise<void> {
		// Verificar si el streaming está habilitado en la configuración
		const isRecordingRequired = await this.streamingManagementService.isRecordingRequired();

		if (isRecordingRequired) {
			// Tras clearError del RETRY el OCR remonta: no competir con retryStreaming (estado stale).
			if (this.streamingManagementService.isStreamingRetryInProgress()) {
				await this.streamingManagementService.waitUntilStreamingRetryIdle();
			} else {
				try {
					// Esperar a que el streaming esté completamente activo
					await this.streamingManagementService.waitForStreamingReady();
				} catch (streamingError: any) {
					const retryInProgress = this.streamingManagementService.isStreamingRetryInProgress();
					// Si required_recording está activo, el streaming es OBLIGATORIO
					this.cloudLogsService.sendCloudLogs(
						{ ocr: `Streaming connection failed - Aborting operation: ${streamingError?.toString()}` },
						OcrLog.IN_PROCESS,
						LogType.Error,
					);

					this.hideLoading();
					this.hidePreviewLoading();

					if (!retryInProgress) {
						// Mostrar pantalla de error con botón de reintento
						this.errorService.setStreamingError(async () => {
							await this.streamingManagementService.retryStreaming(() => this.errorService.clearError());
						});
					}
					throw streamingError;
				}
			}
		}

		try {
			await waitForVideoPreviewReady(this.video);

			// NIF-936: habilitar «Estoy listo» cuando preview (y WHIP si aplica) ya están listos.
			if (this.operation?.config?.tutorial) {
				this.tutorialContinueEnabled = true;
				this.cdr.detectChanges();
			}

			this.hideLoading();
			this.hidePreviewLoading();
		} catch (error: any) {
			// Error crítico no relacionado con streaming
			this.cloudLogsService.sendCloudLogs(
				{ ocr: `Critical error in waitForStreamingAndStartProcessing: ${error?.toString()}` },
				OcrLog.IN_PROCESS,
				LogType.Error,
			);
			this.hideLoading();
			this.hidePreviewLoading();
		}
	}

	private addPredictWebcamEvent(): void {
		if (this.video) {
			// Comprobar si existe el event listener para no añadirlo varias veces
			if (this.video.hasAttribute('data-loadeddata-listener')) return;
			// Añadir atributo para identificar que ya tiene el event listener
			this.video.setAttribute('data-loadeddata-listener', 'true');

			// Eliminar el listener anterior y añadir el nuevo
			this.video.removeEventListener('loadeddata', this.predictWebcam);
			// Añadir el nuevo listener
			this.video.addEventListener('loadeddata', this.predictWebcam);
		}
	}

	//#region HISTORY
	/**
	 * Inicia un timer que se ejecuta cada segundo para capturar datos históricos
	 * sin interferir con el animationFrame que corre a 40-50 fps
	 */
	private startSecondlyDataCapture(): void {
		if (!this.canRunTestTimers()) return;
		// Limpiar suscripción anterior si existe
		if (this.secondlyTimerSubscription) {
			this.secondlyTimerSubscription.unsubscribe();
		}

		// Crear un intervalo que se ejecute cada segundo SEG_HISTORY_INTERVAL
		this.secondlyTimerSubscription = interval(SEG_HISTORY_INTERVAL * 1000)
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.captureDataSnapshot();
			});
	}

	/** Detiene el timer de captura cada segundo (usado al entrar en pantalla MiDNI para no reproducir locuciones de DNI físico). */
	private stopSecondlyDataCapture(): void {
		if (this.secondlyTimerSubscription) {
			this.secondlyTimerSubscription.unsubscribe();
			this.secondlyTimerSubscription = null;
		}
	}

	/**
	 * Sincroniza el canvas principal con el frame actual del vídeo (sin generar dataURL 16:9).
	 * Necesario para que getDocumentImageHelper refleje el frame vigente fuera del ciclo de validación de fotos.
	 */
	private drawCurrentVideoOnMainCanvas(): void {
		if (!this.ctx || !this.canvas || !this.video) return;
		if (!this.video.videoWidth || !this.video.videoHeight || !this.canvas.width || !this.canvas.height) return;
		this.ctx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
	}

	/**
	 * Actualiza isSharpValid / isBrightnessValid con la misma fuente de datos que la captura de fotos,
	 * para que el historial de audio y el texto central no queden desalineados respecto al frame actual.
	 */
	private refreshLiveImageQualityFlags(): void {
		if (!this.permissionsCamera || this.showCapturedImage || this.handsetLandscape) return;
		if (this.operation?.config?.tutorial) return;
		if (!this.positionDocumentService.getDocumentDetection(this.results?.detections)) return;

		this.drawCurrentVideoOnMainCanvas();
		const imageHelper = this.getDocumentImageHelper(true);
		this.isSharpDocument(imageHelper);
		this.isBrightnessDocument(imageHelper);
	}

	/**
	 * Captura un snapshot del estado actual del OCR cada segundo
	 * Almacena el estado en dataHistoryArray para análisis posterior
	 */
	private captureDataSnapshot(): void {
		if (!this.ocrCaptureActive) return;
		if (this.operation?.completed) return;
		if (this.ocrConf?.general?.complete || this.ocrConf?.general?.status === TestStatus.OnComplete) return;
		if (this.pageWarning?.show || this.loading?.show || this.sendingResult) return;
		if (this.midniScreen !== null || this.eudiScreen !== null) return;
		this.refreshLiveImageQualityFlags();
		const snapshot: IOcrHistory = {
			timestamp: new Date().toISOString(),
			documentCentered: this.detected,
			pageWarning: this.pageWarning,
			documentDetection: Boolean(this.positionDocumentService.getDocumentDetection(this.results?.detections)),
			isTooClose: this.isTooClose(),
			isSharpValid: this.isSharpValid,
			isBrightnessValid: this.isBrightnessValid,
			animation: this.animation,
			speech: null,
		};

		// Agregar snapshot al array histórico
		this.audioHistoryService.addDataHistory(snapshot);

		this.textAndSpeechByHistory();
	}

	/**
	 * Analizar el dataHistory para mostrar mensajes de error o reproducir audios
	 */
	private textAndSpeechByHistory(): void {
		if (this.operation?.completed) return;
		if (this.ocrConf?.general?.complete || this.ocrConf?.general?.status === TestStatus.OnComplete) return;
		if (!this.audioHistoryService.dataHistoryArray.length || this.sendingResult) return;
		if (this.midniScreen !== null || this.eudiScreen !== null) return;

		if (this.skipSidePromptHistoryOnce) {
			this.skipSidePromptHistoryOnce = false;
			const pendingDoc = this.ocrConf.document.find((ocr: IOcr) => !ocr.complete);
			const initMessage = pendingDoc?.messages?.onInit;
			if (initMessage && this.audioHistoryService.dataHistoryArray.length) {
				this.audioHistoryService.dataHistoryArray.at(-1).speech = initMessage;
			}
			return;
		}

		const lastSeconds: IOcrHistory[] = this.audioHistoryService.getDataHistoryLastSecs(1);

		// Comenzamos la prueba
		if (this.audioHistoryService.dataHistoryArray.length === 1) {
			const message: string = this.audioHistoryService.getInitDocumentMsg(this.ocrConf);
			if (message) {
				this.tts.speak(message);
				this.audioHistoryService.dataHistoryArray.at(0).speech = message;
			}
			return;
		}

		const index = findLastIndex(this.audioHistoryService.dataHistoryArray, (h: IOcrHistory) => h.speech);
		let lastSpeech: { speech: string; index: number } = { speech: '', index: 0 };
		if (index !== -1) {
			lastSpeech = { speech: this.audioHistoryService.dataHistoryArray[index]?.speech, index };
		}

		let hasCondition: boolean = false;
		const states: IOcrDocumentStatus[] = this.audioHistoryService.getStateOcr(
			this.ocrConf,
			this.results,
			this.operation?.config?.allowed_docs,
		);
		for (let i = 0; i < states.length; i++) {
			const state = states[i];
			if (state.conditions()) {
				// Gestión de los textos
				const actualConf = state.promptConfig.find((conf) => conf.audio === lastSpeech.speech);
				if (actualConf) {
					this.setCenterScanText(actualConf);
					this.alertContent = actualConf.alert as IAlert;
				}
				// Gestión de los audios
				if (
					lastSpeech.index + state.repeatTime / SEG_HISTORY_INTERVAL <
						this.audioHistoryService.dataHistoryArray.length ||
					!state.promptConfig.map((conf) => conf.audio).includes(lastSpeech.speech)
				) {
					if (state.promptConfig?.length) {
						if (state.promptConfig.length === 1) {
							this.tts.speak(state.promptConfig[0].audio, true);
							lastSeconds.at(0).speech = state.promptConfig[0].audio;
							this.setCenterScanText(state.promptConfig[0]);
							this.alertContent = state.promptConfig[0].alert as IAlert;
						}
						if (state.promptConfig.length > 1) {
							const index = state.promptConfig.findIndex((conf) => conf.audio === lastSpeech.speech);
							if (index === -1) {
								this.tts.speak(state.promptConfig[0].audio);
								lastSeconds.at(0).speech = state.promptConfig[0].audio;
								this.setCenterScanText(state.promptConfig[0]);
								this.alertContent = state.promptConfig[0].alert as IAlert;
							} else {
								const nextIndex = (index + 1) % state.promptConfig.length;
								this.tts.speak(state.promptConfig[nextIndex].audio);
								lastSeconds.at(0).speech = state.promptConfig[nextIndex].audio;
								this.setCenterScanText(state.promptConfig[nextIndex]);
								this.alertContent = state.promptConfig[nextIndex].alert as IAlert;
							}
						}
					}
				}
				hasCondition = true;
				break;
			}
		}
		if (!hasCondition) {
			if (!this.scanningAlertActive) this.centerScanText = '';
			this.alertContent = this.audioHistoryService.getEmptyAlert();
		}
	}

	private setCenterScanText(conf: IOcrPromptConfig): void {
		this.centerScanText = conf?.keepPrevCenterText ? this.centerScanText : conf.centerText;
	}

	private isTooClose(): boolean {
		const documentDetection = this.positionDocumentService.getDocumentDetection(this.results?.detections);
		if (!documentDetection) return false;
		return this.positionDocumentService.isDocumentTooClose(
			documentDetection.boundingBox.width,
			documentDetection.boundingBox.height,
		);
	}
	//#endregion HISTORY

	private getAllCameras(): void {
		this.cameraService.getAllCameras();
	}

	private logNoProgressTimeoutIfNeeded(
		hasDocumentCategory: boolean,
		detectedNow: boolean,
		strictDiagnostics: ReturnType<PositionDocumentService['getStrictValidationDiagnostics']>,
		documentDetection: IDocumentDetection | null,
	): void {
		const timeoutMs = 8000;
		const now = Date.now();

		if (hasDocumentCategory && !detectedNow) {
			if (!this.documentWithoutStrictMatchSince) {
				this.documentWithoutStrictMatchSince = now;
				return;
			}

			const elapsed = now - this.documentWithoutStrictMatchSince;
			if (elapsed < timeoutMs || now - this.lastNoProgressLogAt < timeoutMs) return;

			const track = this.stream?.getVideoTracks?.()[0];
			const debugImage = this.buildDebugFrameWithRectangles();
			this.lastNoProgressLogAt = now;
			this.cloudLogsService.sendCloudLogs(
				{
					ocrNoProgressTimeout: {
						elapsedMs: elapsed,
						currentDeviceId: this.cameraService.getCurrentDeviceId(),
						currentDeviceLabel: this.cameraService.getCurrentDeviceLabel(),
						videoSettings: track?.getSettings?.() ?? null,
						bestDetection: documentDetection
							? {
									categoryName: documentDetection.name,
									score: documentDetection.score,
									boundingBox: documentDetection.boundingBox,
								}
							: null,
						strictValidation: strictDiagnostics,
					},
				},
				OcrLog.IN_PROCESS,
				LogType.Warning,
				debugImage || undefined,
			);
			return;
		}

		this.documentWithoutStrictMatchSince = null;
	}

	/**
	 * Heartbeat cada 6 s en captura automática: detección, tamaño bbox, centrado y lados que fallan (NIF-827).
	 * Sin imagen (ocrNoProgressTimeout ya adjunta frame debug a los 8 s sin frameMatch).
	 */
	private logOcrAutoCenterHeartbeatIfNeeded(
		hasDocumentCategory: boolean,
		detectedNow: boolean,
		strictDiagnostics: ReturnType<PositionDocumentService['getStrictValidationDiagnostics']>,
		documentDetection: IDocumentDetection | null,
	): void {
		if (this.isModeSemiManual || this.showCapturedImage || this.sendingResult || !this.ocrCaptureActive) {
			return;
		}

		const now = Date.now();
		if (now - this.lastOcrAutoCenterLogAt < OcrComponent.OCR_AUTO_CENTER_LOG_INTERVAL_MS) {
			return;
		}
		this.lastOcrAutoCenterLogAt = now;

		const sides = {
			right: strictDiagnostics.rightSideValid,
			left: strictDiagnostics.leftSideValid,
			top: strictDiagnostics.topSideValid,
			bottom: strictDiagnostics.bottomSideValid,
			formValid: strictDiagnostics.formValid,
			centerInRect: strictDiagnostics.centerInRect,
		};

		let failing: string[];
		if (!hasDocumentCategory) {
			failing = ['noDetection'];
		} else if (!detectedNow) {
			failing = (Object.keys(sides) as Array<keyof typeof sides>).filter((key) => !sides[key]);
		} else {
			failing = [];
		}

		const pendingDoc = this.getPendingOcrDocument();
		const bbox = documentDetection?.boundingBox
			? {
					width: documentDetection.boundingBox.width,
					height: documentDetection.boundingBox.height,
					originX: documentDetection.boundingBox.originX,
					originY: documentDetection.boundingBox.originY,
				}
			: null;

		this.cloudLogsService.sendCloudLogs(
			{
				ocrAutoCenter: {
					hasDocument: hasDocumentCategory,
					centered: detectedNow,
					score: documentDetection?.score ?? null,
					bbox,
					category: documentDetection?.name ?? null,
					sides,
					failing,
					zoom: strictDiagnostics.ocrExtraDocumentZoom,
					zoomZone: strictDiagnostics.documentZoomZone,
					expectedSide: pendingDoc?.documentTestId ?? null,
					elapsedSideSec: this.timerManagerService.totalTime,
				},
			},
			OcrLog.IN_PROCESS,
			LogType.Info,
		);
	}

	private buildDebugFrameWithRectangles(): string | null {
		try {
			if (!this.canvas || !this.ctx || !this.video) return null;
			if (!this.video.videoWidth || !this.video.videoHeight) return null;

			// Captura del frame actual con overlays para depuración remota.
			this.drawCurrentVideoOnMainCanvas();
			this.positionDocumentService.setControlRect(this.canvas, this.getAnalysisViewportAspect());
			this.positionDocumentService.drawControl(this.ctx);
			return this.canvas.toDataURL('image/jpeg', quality);
		} catch (error) {
			this.cloudLogsService.sendCloudLogs(
				{ ocrDebugFrameError: error?.toString?.() ?? 'Unknown error building debug frame' },
				OcrLog.IN_PROCESS,
				LogType.Warning,
			);
			return null;
		}
	}

	private canContinueCameraFrameLoop(): boolean {
		return !!this.video && this.permissionsCamera && this.canRunTestTimers() && !this.barcodeScanActive;
	}

	private schedulePredictWebcamFrame(): void {
		if (!this.canContinueCameraFrameLoop()) return;
		this.animationFrame = window.requestAnimationFrame(this.predictWebcam);
	}

	private predictWebcam = async () => {
		// Sin captura activa (OCR completado o ngOnDestroy): no encadenar RAF ni validar.
		if (!this.ocrCaptureActive) return;
		if (!this.canContinueCameraFrameLoop()) return;

		// Si el modelo no está listo aún, continuar al siguiente frame
		if (!this.documentDetector) {
			this.schedulePredictWebcamFrame();
			return;
		}

		// Ensure video is ready
		if (
			!this.video.videoWidth ||
			!this.video.videoHeight ||
			this.video.videoWidth === 0 ||
			this.video.videoHeight === 0
		) {
			// Retry on next frame
			this.schedulePredictWebcamFrame();
			return;
		}

		this.canvas.width = JSON.parse(JSON.stringify(this.video.videoWidth));
		this.canvas.height = JSON.parse(JSON.stringify(this.video.videoHeight));
		this.positionDocumentService.setControlRect(this.canvas, this.getAnalysisViewportAspect());
		// Now let's start detecting the stream.
		if (this.runningMode === 'IMAGE') {
			this.runningMode = 'VIDEO';
			await this.documentDetector.setOptions({ runningMode: this.runningMode });
		}

		const startTimeMs = performance.now();
		if (this.lastVideoTime !== this.video.currentTime) {
			this.lastVideoTime = this.video.currentTime;
			this.results = this.documentDetector.detectForVideo(this.video, startTimeMs);
		}
		let documentDetection: IDocumentDetection | null = null;
		if (this.results?.detections?.length) {
			documentDetection = this.positionDocumentService.getDocumentDetection(this.results.detections);
			if (documentDetection) {
				this.positionDocumentService.setDocumentPoints(
					{
						x: documentDetection.boundingBox.originX,
						y: documentDetection.boundingBox.originY,
						width: documentDetection.boundingBox.width,
						height: documentDetection.boundingBox.height,
					},
					this.canvas,
				);
			} else {
				this.positionDocumentService.setDocumentPoints(null);
			}
		} else {
			this.results = undefined;
			this.positionDocumentService.setDocumentPoints(null);
		}
		this.positionDocumentService.updateDocumentFitZoom(Boolean(documentDetection), documentDetection?.name ?? null);
		this.syncOcrPreviewZoom();

		// Actualizamos el subtitle
		this.updateSubtitle();

		if (
			this.ocrConf?.general &&
			this.ocrConf?.general?.status !== TestStatus.OnComplete &&
			this.ocrConf?.general?.status !== TestStatus.OnFail &&
			!this.loading.show &&
			!this.operation.config.tutorial &&
			!this.pageWarning.show &&
			!this.handsetLandscape &&
			!this.showCapturedImage &&
			this.midniScreen === null &&
			!this.barcodeScanActive
		) {
			this.positionDocumentService.drawControl(this.ctx);
			this.documentCheckCycle();
		}

		this.schedulePredictWebcamFrame();
	};

	/*
	 * Control de la identificación del documento
	 */
	private documentCheckCycle(): void {
		if (!this.ocrCaptureActive) return;
		if (this.midniScreen !== null || this.eudiScreen !== null) return;
		// Picker/upload Flexible: no capturar en segundo plano tras el overlay.
		if (this.flexibleScreen === 'picker' || this.flexibleScreen === 'upload') return;
		if (this.barcodeScanActive || this.awaitingBarcodeScan) return;
		if (!this.canRunTestTimers()) return;

		// Iniciamos por primera vez la lectura del documento
		if (this.ocrConf.general && !this.ocrConf.general.startAt) {
			// Iniciar timer cada segundo para capturar datos históricos
			this.startSecondlyDataCapture();

			this.startTest();
			this.ocrConf.general.startAt = new Date().toISOString();
		}
		if (this.ocrConf.general?.startAt && !this.sendingResult) {
			this.startTotalTime();
			this.activateSemiManualModeIfNeeded();
		}
		// Lectura del documento general ha fallado
		if (this.ocrConf.general.status === TestStatus.OnFail) {
			if (!this.ocrConf.general.finishAt) {
				this.ocrConf.general.finishAt = new Date().toISOString();
			}
			return;
		}
		// Encuadrar documento
		if (this.isThereAnyPendingDocumentOcr()) {
			// Revisamos que prueba no está completada por orden y seteamos el recuadro de control
			const documentTest: IOcr = this.ocrConf.document.find((ocr: IOcr) => !ocr.complete);
			if (documentTest) {
				this.isDetected();
				this.documentValidation(documentTest);
			}
			return;
		}
		// Últimas validaciones
		if (!this.ocrConf.general.finishAt && !this.ocrConf.general.controlValidation) {
			this.finishValidation();
			return;
		}
	}

	/**
	 * Calcula caracteristicas de la imagen del documento detectado en la imagen actual
	 * @param skipImageDraw Si es true, no redibuja la imagen del video (asume que ya está dibujada)
	 */
	private getDocumentImageHelper(skipImageDraw: boolean = false): IImageHelper {
		if (!this.ctx) {
			return {
				lapacian: 0,
				fourier: 0,
				gridAnalysisBrightness: null,
				consecutiveCellsWith95: null,
				consecutiveCellsWith98: null,
			};
		}

		// Si no se debe saltar el dibujo, dibujar la imagen del video
		if (!skipImageDraw) this.drawImageForDocumentValidation();

		// Si no hay documento detectado, usar enfoque de toda la imagen como fallback
		let imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
		if (!this.results?.detections?.length) {
			const imageGrid = this.imageHelperService.analyzeImageGrid(imageData);
			return {
				lapacian: this.imageHelperService.getLaplacianFilterSharpness(imageData),
				fourier: this.imageHelperService.getFourierSharpness(imageData),
				gridAnalysisBrightness: imageGrid,
				consecutiveCellsWith95: this.imageHelperService.findConsecutiveBrightCells(imageGrid, 95),
				consecutiveCellsWith98: this.imageHelperService.findConsecutiveBrightCells(imageGrid, 98),
			};
		}
		const documentDetection = this.positionDocumentService.getDocumentDetection(this.results?.detections);
		if (!documentDetection) {
			// Si no se puede obtener la detección, usar enfoque de toda la imagen
			const imageGrid = this.imageHelperService.analyzeImageGrid(imageData);
			return {
				lapacian: this.imageHelperService.getLaplacianFilterSharpness(imageData),
				fourier: this.imageHelperService.getFourierSharpness(imageData),
				gridAnalysisBrightness: imageGrid,
				consecutiveCellsWith95: this.imageHelperService.findConsecutiveBrightCells(imageGrid, 95),
				consecutiveCellsWith98: this.imageHelperService.findConsecutiveBrightCells(imageGrid, 98),
			};
		}

		// Actualizamos la imagen con solo la región del documento detectado
		imageData = this.ctx.getImageData(
			documentDetection.boundingBox.originX,
			documentDetection.boundingBox.originY,
			documentDetection.boundingBox.width,
			documentDetection.boundingBox.height,
		);
		const sharpness = this.imageHelperService.getLaplacianFilterSharpness(imageData);
		const sharpnessFourier = this.imageHelperService.getFourierSharpness(imageData);
		const gridAnalysisBrightness = this.imageHelperService.analyzeImageGrid(imageData);
		const consecutiveCellsWith95 = this.imageHelperService.findConsecutiveBrightCells(gridAnalysisBrightness, 95);
		const consecutiveCellsWith98 = this.imageHelperService.findConsecutiveBrightCells(gridAnalysisBrightness, 98);

		// Debug logging para ayudar a identificar problemas
		if (this.testMode) {
			console.log(
				`
				[OCR Debug] Sharpness lapacian: ${sharpness.toFixed(2)},
				Sharpness fourier: ${sharpnessFourier.toFixed(2)},
				Min Required lapacian: ${this.ocrConf?.general?.minSharpnessLapacian || 'N/A'},
				Min Required fourier: ${this.ocrConf?.general?.minSharpnessFourier || 'N/A'},
				Grid analysis brightness: ${JSON.stringify(gridAnalysisBrightness) || 'N/A'},
				Consecutive cells 95: ${consecutiveCellsWith95?.length && consecutiveCellsWith95[0]?.cells?.length ? consecutiveCellsWith95[0].cells.length : 0},
				Consecutive cells 98: ${consecutiveCellsWith98?.length && consecutiveCellsWith98[0]?.cells?.length ? consecutiveCellsWith98[0].cells.length : 0},
				Detected: ${this.detected},
				BlurSeconds: ${this.timerManagerService?.blurSeconds || 0}
				`,
			);
		}
		return {
			lapacian: sharpness,
			fourier: sharpnessFourier,
			gridAnalysisBrightness,
			consecutiveCellsWith95,
			consecutiveCellsWith98,
		};
	}

	private async documentValidation(documentTest: IOcr): Promise<void> {
		// Iniciamos el tiempo de la prueba
		if (!documentTest.startAt) {
			this.setAnimation(documentTest);
			documentTest.startAt = new Date().toISOString();
		}
		// Prueba estado OnWarning
		if (documentTest.status === TestStatus.OnWarning) {
			const startDate = new Date(documentTest.startAt);
			startDate.setSeconds(startDate.getSeconds() + documentTest.maxTime);
			// Revisamos si ha pasado el tiempo de la prueba para pasar a estado OnFail
			if (startDate < new Date()) {
				documentTest.status = TestStatus.OnFail;
				// Revisamos si tiene más intentos y volvemos al estado OnWarning o pasamos al estado OnFail todo el proceso
				documentTest.countTries++;
				if (documentTest.countTries > documentTest.tries) {
					if (this.ocrConf.general.countTries + 1 > this.ocrConf.general.tries) {
						this.generalTries();
					} else {
						this.displayHelpWarning(false);
					}
				} else {
					this.subscriptionTimers.add(
						timer(this.ocrConf.general.timeBetweenTest * 1000).subscribe(() => {
							documentTest.startAt = '';
							documentTest.status = TestStatus.OnInit;
						}),
					);
				}
			}
		}
		// Prueba estado OnInit
		if (documentTest.status === TestStatus.OnInit) {
			const startDate = new Date(documentTest.startAt);
			startDate.setSeconds(startDate.getSeconds() + documentTest.maxTime / 2);
			// Revisamos si ha pasado la mitad del tiempo de la prueba para pasar a estado OnWarning
			if (startDate < new Date()) {
				documentTest.status = TestStatus.OnWarning;
			}
		}
		// Prueba estado OnFail
		if (documentTest.status === TestStatus.OnFail) {
			return;
		}
		// Recopilamos resultados
		if (this.scoringDocumentService.getDocumentValidation().length === 0) {
			this.scoringDocumentService.addDocumentValidation(this.getResult());
			// Fase 1: Almacenamos primera imagen (solo si cumple nitidez mínima)
			if (documentTest.photos.length === 0) {
				this.drawImageForDocumentValidation();
				const imageHelper: IImageHelper = this.getDocumentImageHelper(true);
				// Solo guardar si cumple el mínimo de nitidez (texto legible)
				if (this.isSharpDocument(imageHelper)) {
					documentTest.photos.push(this.buildOcrPhotoRecord(imageHelper));
				}
			}
			// Tiempo que se debe mantener la fase 1 para validarla
			this.subscriptionTimers.add(
				timer(documentTest.minTime * 1000).subscribe(async () => {
					if (
						this.ocrCaptureActive &&
						!this.destroy$.closed &&
						this.scoringDocumentService.getDocumentValidation().length &&
						documentTest.status !== TestStatus.OnComplete &&
						documentTest.status !== TestStatus.OnFail &&
						!this.pageWarning.show &&
						!this.handsetLandscape
					) {
						// Fase 2: Almacenamos segunda imagen (solo si cumple nitidez mínima)
						if (documentTest.photos.length === 1) {
							this.drawImageForDocumentValidation();
							const imageHelper: IImageHelper = this.getDocumentImageHelper(true);
							// Solo guardar si cumple el mínimo de nitidez (texto legible)
							if (this.isSharpDocument(imageHelper)) {
								documentTest.photos.push(this.buildOcrPhotoRecord(imageHelper));
							}
						}
						// Validaciones
						if (this.isDocumentValid(documentTest) && (await this.sendResult(true))) {
							this.resetTimers();
							// complete solo si applyRequiredOtherSide o cierre final lo marcaron (no forzar aquí).
							if (documentTest.complete) {
								documentTest.status = TestStatus.OnComplete;
								if (!documentTest.finishAt) {
									documentTest.finishAt = new Date().toISOString();
								}
							}
							this.scoringDocumentService.setDocumentValidation([]);
						} else {
							// Revisamos que hemos capturado algo para no eliminar la foto capturada
							if (!this.showCapturedImage) documentTest.photos = [];
						}
						this.scoringDocumentService.setDocumentValidation([]);
					}
				}),
			);
		} else {
			this.scoringDocumentService.addDocumentValidation(this.getResult());
		}
	}

	/** Aspecto del preview en pantalla (`tec-scan` / vídeo); define la banda visible sobre el sensor.
	 *  Prioridad: medición DOM → caché → fallback `OCR_SCAN_PREVIEW_ASPECT` / aspecto del sensor. */
	private getAnalysisViewportAspect(): number {
		const previewCandidates: Array<HTMLElement | undefined> = [this.video, this.canvas];
		for (const el of previewCandidates) {
			if (!el) {
				continue;
			}
			const rect = el.getBoundingClientRect();
			if (rect.width > 0 && rect.height > 0) {
				this.lastAnalysisViewportAspect = rect.width / rect.height;
				return this.lastAnalysisViewportAspect;
			}
		}
		if (this.lastAnalysisViewportAspect > 0) {
			return this.lastAnalysisViewportAspect;
		}
		if (this.userAgentService.isMobile()) {
			return OCR_SCAN_PREVIEW_ASPECT;
		}
		if (this.video?.videoWidth && this.video?.videoHeight) {
			return this.video.videoWidth / this.video.videoHeight;
		}
		return OCR_SCAN_PREVIEW_ASPECT;
	}

	private getGuideFocusPoint(): { x: number; y: number } | null {
		if (!this.video?.videoWidth || !this.video?.videoHeight || !this.canvas) {
			return null;
		}
		this.positionDocumentService.setControlRect(this.canvas, this.getAnalysisViewportAspect());
		return this.positionDocumentService.getGuideFocusPointNormalized(this.video.videoWidth, this.video.videoHeight);
	}

	private getDocumentFocusPoint(documentDetection: IDocumentDetection | null): { x: number; y: number } | null {
		if (!this.video?.videoWidth || !this.video?.videoHeight) {
			return null;
		}
		if (documentDetection?.boundingBox) {
			const box = documentDetection.boundingBox;
			return {
				x: Math.min(1, Math.max(0, (box.originX + box.width / 2) / this.video.videoWidth)),
				y: Math.min(1, Math.max(0, (box.originY + box.height / 2) / this.video.videoHeight)),
			};
		}
		return this.positionDocumentService.getDocumentFocusPointNormalized(
			this.video.videoWidth,
			this.video.videoHeight,
		);
	}

	private triggerOcrAutofocusTarget(_reason: string, documentDetection: IDocumentDetection | null = null): void {
		const track = this.stream?.getVideoTracks?.()?.[0];
		if (!track) return;
		const point = this.getDocumentFocusPoint(documentDetection) || this.getGuideFocusPoint();
		if (!point) return;
		void this.cameraFocusService.triggerDetectionRefocus(track, point);
	}

	private drawImageForDocumentValidation(): void {
		if (!this.ctx || !this.canvas || !this.video) return;

		const vw = this.video.videoWidth;
		const vh = this.video.videoHeight;
		if (!vw || !vh) return;

		if (this.canvas.width !== vw || this.canvas.height !== vh) {
			this.canvas.width = vw;
			this.canvas.height = vh;
		}
		this.positionDocumentService.setControlRect(this.canvas, this.getAnalysisViewportAspect());
		const sourceRect = this.positionDocumentService.getVisibleAnalysisSourceRect();
		if (!sourceRect) return;

		// Mismo factor que el preview vivo (`syncOcrPreviewZoom`); el JPEG ya lleva el zoom horneado.
		const previewZoom = this.positionDocumentService.getEffectivePreviewZoom();
		const { sx, sy, sw, sh } = applyCenterZoomToSourceRect(sourceRect, previewZoom, vw, vh);

		const tempCanvas = document.createElement('canvas');
		tempCanvas.width = Math.max(1, Math.round(sw));
		tempCanvas.height = Math.max(1, Math.round(sh));
		const tempCtx = tempCanvas.getContext('2d');

		if (!tempCtx) return;

		tempCtx.drawImage(this.video, sx, sy, sw, sh, 0, 0, tempCanvas.width, tempCanvas.height);
		this.imageData16x9 = tempCanvas.toDataURL('image/jpeg', quality);

		this.ctx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
	}

	/**
	 * Sincroniza zoom CSS del `<app-video>` en vivo.
	 * Contrato: el vídeo usa `scale`/`translate`; la imagen capturada (`imageData16x9`) NO —
	 * su zoom va horneado en `drawImageForDocumentValidation` vía `getEffectivePreviewZoom()`.
	 */
	private syncOcrPreviewZoom(): void {
		const extra = this.positionDocumentService.getOcrExtraDocumentZoom();
		const useLegacyGuide = extra <= 1.001;
		const nextFactor = useLegacyGuide
			? this.positionDocumentService.getZoomFactor()
			: this.positionDocumentService.getEffectivePreviewZoom();
		const nextOffsetX = useLegacyGuide ? this.positionDocumentService.getZoomOffsetX() : 0;
		const nextOffsetY = useLegacyGuide ? this.positionDocumentService.getZoomOffsetY() : 0;
		const nextOrigin = useLegacyGuide ? '0 0' : 'center center';

		if (
			Math.abs(nextFactor - this.ocrPreviewZoomFactor) < 0.001 &&
			Math.abs(nextOffsetX - this.ocrPreviewZoomOffsetX) < 0.001 &&
			Math.abs(nextOffsetY - this.ocrPreviewZoomOffsetY) < 0.001 &&
			nextOrigin === this.ocrPreviewTransformOrigin
		) {
			return;
		}

		this.ocrPreviewZoomFactor = nextFactor;
		this.ocrPreviewZoomOffsetX = nextOffsetX;
		this.ocrPreviewZoomOffsetY = nextOffsetY;
		this.ocrPreviewTransformOrigin = nextOrigin;
		this.cdr.detectChanges();
	}

	/*
	 * Controles del documento
	 */
	// Ocr de documento pendiente
	private isThereAnyPendingDocumentOcr(): boolean {
		const totalLifeCheckPending: number = this.ocrConf.document.filter((ocr: IOcr) => !ocr.complete)?.length;
		return Boolean(totalLifeCheckPending);
	}

	/**
	 * Comprueba si hay evidencia de encuadre suficiente para validar tras `minTime`.
	 * Pasaporte parpadea en MediaPipe: en el tick del timer puede no haber detección instantánea
	 * aunque el historial acumulado durante la fase 1 ya cumpla el umbral (NIF-575).
	 */
	private hasDetectionEvidenceForValidation(): boolean {
		// Sticky `detected` (500 ms DNI / 1000 ms pasaporte) cuenta como encuadre válido.
		if (this.detected) return true;
		const history = this.scoringDocumentService.getDocumentValidation();
		if (!history.length) return false;
		// Fallback: % de muestras con `detected` durante el intervalo de captura.
		return (
			this.scoringDocumentService.getPercentageTimeInMiddle(history) >=
			this.ocrConf.general.minPercenteageTimeInMiddle
		);
	}

	private isDocumentValid(documentTest: IOcr): boolean {
		// Si no hay fotos con nitidez suficiente, el documento no es válido
		if (!documentTest?.photos?.length) {
			return false;
		}

		// Encuadre: sticky o % histórico en minTime (no solo frame exacto del timer).
		if (!this.hasDetectionEvidenceForValidation()) return false;

		// Misma selección de foto que getBodyResult (nitidez + área), no solo la de mayor tamaño.
		const bestPhoto = this.selectBestPhoto(documentTest.photos);
		if (!bestPhoto) return false;

		// Encuadre y calidad local; el lado lo valida el backend (modelo genérico `document`)
		return (
			this.isSharpDocument(bestPhoto) &&
			this.isBrightnessDocument(bestPhoto) &&
			this.scoringDocumentService.getPercentageTimeInMiddle(
				this.scoringDocumentService.getDocumentValidation(),
			) >= this.ocrConf.general.minPercenteageTimeInMiddle &&
			this.scoringDocumentService.getPercentageDocument(this.scoringDocumentService.getDocumentValidation()) >
				documentTest.minPercentageDocument
		);
	}

	// Validaciones finales
	private async finishValidation(): Promise<void> {
		// Evita avanzar a POL si queda algún lado pendiente (p. ej. KO required_side en curso).
		if (this.isThereAnyPendingDocumentOcr()) return;
		this.stopOcrCapture();
		this.ocrConf.general.controlValidation = true;
		this.ocrConf.general.status = TestStatus.OnComplete;
		this.ocrConf.general.complete = true;
		// Limpiar timeout de blur al completar la validación exitosamente
		this.nextOperation(OPERATION_STATUS.OK);
	}

	private repeatAllProcess(): void {
		if (this.ocrConf.general.status === TestStatus.OnWarning) {
			this.resetTimers();
			// Resetear el estado de la operación para que se pueda avanzar después del éxito
			this.operation.completed = false;
			this.ocrConf = {
				...this.ocrConf,
				general: {
					...this.ocrConf.general,
					status: TestStatus.OnInit,
					controlValidation: false,
					complete: false,
					finishAt: '', // Resetear finishAt para reiniciar completamente
				},
			};
			const documentTest: IOcr[] = this.ocrConf.document.filter((ocr: IOcr) => !ocr.complete);
			documentTest.forEach((ocr: IOcr) => {
				ocr.complete = false;
				ocr.status = TestStatus.OnInit;
				ocr.startAt = '';
				ocr.finishAt = '';
				ocr.photos = [];
				ocr.countTries = 1;
				// Rehabilitar la guía animada al reiniciar el flujo.
				ocr.showAnimation = this.operation.config?.setting?.show_guide ?? true;
			});
			this.scoringDocumentService.setDocumentValidation([]);
			this.audioHistoryService.clearDataHistory();
		}
	}

	private activateSemiManualModeIfNeeded(): void {
		if (
			this.isModeSemiManual ||
			this.isHelpByNavHelp ||
			this.midniScreen !== null ||
			this.eudiScreen !== null ||
			this.flexibleScreen !== null
		) {
			return;
		}
		if (this.timerManagerService.totalTime >= OCR_SEMI_MANUAL_TIME) {
			this.isModeSemiManual = true;
			this.tts.speak('OCR.GENERAL.MODE_SEMI_MANUAL');
		}
	}

	// #region Handle Document
	public takePhoto(): void {
		if (!this.video || !this.canvas || !this.ctx) return;
		this.showCapturedImage = true;

		const documentTest: IOcr = this.ocrConf.document.find((ocr: IOcr) => !ocr.complete);

		// Ajustar el canvas al tamaño del video
		this.canvas.width = this.video.videoWidth;
		this.canvas.height = this.video.videoHeight;

		// Calcular el controlRect con las dimensiones del video
		this.positionDocumentService.setControlRect(this.canvas, this.getAnalysisViewportAspect());

		// Capturar banda de análisis alineada con guías y payload al backend
		this.drawImageForDocumentValidation();

		const imageHelper: IImageHelper = this.getDocumentImageHelper();
		// Validar nitidez mínima antes de guardar
		if (this.isSharpDocument(imageHelper)) {
			// Almacenar JPEG de la banda visible (imageData16x9, nombre histórico)
			documentTest.photos.push(this.buildOcrPhotoRecord(imageHelper));
		}

		this.capturedImage = this.imageData16x9;
		this.updateSubtitle();
	}

	public repeatPhoto(): void {
		const documentTest: IOcr = this.ocrConf.document.find((ocr: IOcr) => !ocr.complete);
		documentTest.photos.pop();

		this.capturedImage = null;
		this.imageData16x9 = '';
		this.updateSubtitle();
	}

	public async savePhoto(): Promise<void> {
		await this.sendResult(true);
		// Si el back pide otra cara (OK+required_side), sendResult ya preparó el slot; no cerrar OCR.
		if (this.isThereAnyPendingDocumentOcr()) {
			return;
		}
		const documentTest: IOcr | undefined = this.ocrConf.document.find((ocr: IOcr) => !ocr.complete);
		if (!documentTest) {
			return;
		}
		documentTest.status = TestStatus.OnComplete;
		this.resetTimers();
		if (!documentTest.finishAt) {
			documentTest.finishAt = new Date().toISOString();
		}
		documentTest.complete = true;
		this.scoringDocumentService.setDocumentValidation([]);
	}
	// #endregion

	/*
	 * Controles de posición de la cara en el centro
	 */
	private isDetected(): void {
		this.positionDocumentService.setControlRect(this.canvas, this.getAnalysisViewportAspect());
		const hasDocumentCategory = this.positionDocumentService.hasDocumentCategory(this.results?.detections);
		const documentDetection = this.positionDocumentService.getDocumentDetection(this.results?.detections);
		// frameMatch: relajado (3/4 lados) solo si categoría === passport; DNI/NIE mantiene 4/4 estrictos.
		const strictDiagnostics = this.positionDocumentService.getStrictValidationDiagnostics(
			documentDetection?.name ?? null,
		);
		const detectedNow = hasDocumentCategory && strictDiagnostics.frameMatch;

		const detectedHoldMs = documentDetection?.name === MODEL_DOCUMENT_CATEGORY.PASSPORT ? 1000 : 500;
		this.setDetected(detectedNow, detectedHoldMs);
		this.logNoProgressTimeoutIfNeeded(hasDocumentCategory, detectedNow, strictDiagnostics, documentDetection);
		this.logOcrAutoCenterHeartbeatIfNeeded(hasDocumentCategory, detectedNow, strictDiagnostics, documentDetection);

		if (detectedNow || hasDocumentCategory) {
			this.triggerOcrAutofocusTarget('is_detected', documentDetection);
		}

		// Si el documento ha sido detectado correctamente
		if (this.detected) {
			// Detenemos la animación y los temporizadores de desenfoque y centrado
			// Si ya hemos completado una cara, esperamos a que la animación termine para quitarla
			if (!this.ocrConf.document.find((ocr: IOcr) => ocr.complete)) {
				this.animation = null;
				this.lottieAnimationOptions = null;
				this.match = false;
			}
			// Verificación de enfoque
			// this.startBlurTime();
			// this.stopCenterTime();
			// Actualizamos el texto de alerta de escaneo
			// this.updateAlertScanText();
		} else {
			// Si no está detectado, iniciamos solo el temporizador de centrado
			// this.startCenterTime();
			// Detenemos blur time porque no hay documento para verificar enfoque
			// this.stopBlurTime();
		}
		this.updateScanningAlert(this.detected);
	}

	/*
	 * Función para que no parpadee mucho cuando detectamos un documento
	 */
	private setDetected(value: boolean, holdMs = 500) {
		// Si el valor es TRUE → aplicar de inmediato
		if (value) {
			this.detected = true;
			clearTimeout(this.stickyTimer);
			this.stickyTimer = null;
			return;
		}

		if (!this.stickyTimer) {
			// Si el valor es FALSE → esperar holdMs antes de bajarlo
			this.stickyTimer = setTimeout(() => {
				this.detected = value;
				clearTimeout(this.stickyTimer);
				this.stickyTimer = null;
			}, holdMs);
		}
	}

	/**
	 * Alert in-scan «Escaneando…» tras 2 s con borde detectado sin verde (NIF-610 / QA Carmen).
	 * Solo copy centrado en marco; sin controles extra fuera del diseño visible.
	 */
	private updateScanningAlert(isDetectedFrame: boolean): void {
		if (this.check || this.sendingResult || this.showCapturedImage || this.animation || this.pageWarning.show) {
			this.clearScanningAlert();
			return;
		}
		if (!isDetectedFrame) {
			this.clearScanningAlert();
			return;
		}
		if (this.scanningTimer || this.scanningAlertActive) {
			return;
		}
		this.scanningTimer = setTimeout(() => {
			this.scanningTimer = null;
			if (this.detected && !this.check && !this.sendingResult && !this.showCapturedImage) {
				this.scanningAlertActive = true;
				this.centerScanText = this.trans.instant('OCR.GENERAL.SCANNING');
			}
		}, OcrComponent.SCANNING_DELAY_MS);
	}

	private clearScanningAlert(): void {
		if (this.scanningTimer) {
			clearTimeout(this.scanningTimer);
			this.scanningTimer = null;
		}
		if (this.scanningAlertActive) {
			this.scanningAlertActive = false;
			const scanningLabel = this.trans.instant('OCR.GENERAL.SCANNING');
			if (this.centerScanText === scanningLabel) {
				this.centerScanText = '';
			}
		}
	}

	private showIncorrectSide(): void {
		if (this.midniScreen !== null || this.eudiScreen !== null) return;
		// Reproducir audio de lado incorrecto
		this.tts.speak('OCR.GENERAL.CHANGE_SIDE');
		// Actualizar el contenido de la alerta
		this.alertContent = this.audioHistoryService.createIncorrectSideAlert(this.ocrConf, this.results);
		// Actualizar el texto de alerta de escaneo (lo limpiará)
		this.centerScanText = '';
		// Reiniciamos
		this.audioHistoryService.clearDataHistory();
	}

	/**
	 * NIF-988: tile de error cuando falta la otra cara (2004), no tips de nitidez ni MRZ.
	 */
	private showIncorrectDocumentSideWarning(): void {
		if (this.midniScreen !== null || this.eudiScreen !== null) return;
		this.tts.speak('OCR.GENERAL.CHANGE_SIDE', true);
		this.audioHistoryService.clearDataHistory();
		this.hideCapturedImagePreview();
		this.setPageWarning(PAGE_WARNING.INCORRECT_DOCUMENT_SIDE);
		this.stopTimersDuringRequest();
		this.startWarningTime();
		this.loading.show = false;
		this.screenShotService.stopSaveScreenshot();
	}

	private showGenericError(): void {
		if (this.midniScreen !== null || this.eudiScreen !== null) return;
		// Reproducir audio de error genérico
		this.tts.speak('OCR.GENERAL.DONT_READ_DOCUMENT');
		// Limpiamos el historial para que se reinicen los audios
		this.audioHistoryService.clearDataHistory();
	}

	/** NIF-984: anverso/reverso de documentos distintos → tile status (sin TTS). */
	private showDifferentDocumentsWarning(): void {
		if (this.midniScreen !== null || this.eudiScreen !== null) return;
		this.setPageWarning(PAGE_WARNING.DIFFERENT_DOCUMENTS);
		this.stopTimersDuringRequest();
		this.startWarningTime();
		this.loading.show = false;
	}

	/** NIF-1085: dirección no reconocida (2038) → tile status (no tips ni ADJUNTAR KO). */
	private showMissingAddressWarning(): void {
		if (this.midniScreen !== null) return;
		this.hideCapturedImagePreview();
		this.setPageWarning(PAGE_WARNING.MISSING_ADDRESS);
		this.stopTimersDuringRequest();
		this.startWarningTime();
		this.loading.show = false;
	}

	// TODO NIF-641: descomentar cuando producto confirme auto-continuar tras 5 s
	// private static readonly INSUFFICIENT_READ_AUTO_RETRY_MS = 5000;

	private showInsufficientOcrReadWarning(): void {
		if (this.midniScreen !== null || this.eudiScreen !== null) return;
		// NIF-921 Figma ADJUNTAR KO: en upload Flexible no tips de escaneo.
		if (this.flexibleUploadSession) {
			this.showFlexibleUploadKoWarning();
			return;
		}
		this.setPageWarning(PAGE_WARNING.INSUFFICIENT_OCR_READ);
		this.stopTimersDuringRequest();
		this.startWarningTime();
		this.loading.show = false;
	}

	/**
	 * Muestra la imagen capturada durante el envío al backend
	 */
	private showCapturedImageDuringSend(image: string | undefined): void {
		if (!image) return;

		this.capturedImage = image;
	}

	/**
	 * Oculta la imagen capturada
	 */
	private hideCapturedImagePreview(): void {
		this.showCapturedImage = false;
		this.capturedImage = null;
	}

	private getResult(): IDocumentResult {
		const category: IDocumentDetection = this.positionDocumentService.getDocumentDetection(
			this.results?.detections,
		);
		return {
			detected: this.detected,
			categoryName: category ? category.name : null,
			score: category ? category.score : 0,
		};
	}

	public updateSubtitle(): void {
		this.subtitle = this.generateSubtitle();
	}

	// Evaluamos el número de intentos que lleva el usuario para ver si repetimos la prueba o la marcamos como incompleta
	private generalTries(): void {
		this.loading = {
			show: true,
			text: this.trans.instant('GENERAL.LOADING.LIFE_CHECK_VALIDATION'),
		};
		this.ocrConf.general.countTries++;
		if (this.ocrConf.general.countTries > this.ocrConf.general.tries) {
			this.ocrConf.general.status = TestStatus.OnFail;
			this.sendResult(false, TEST_STATUS.KO_MAXTRIES);
		} else {
			// Avisar al backend de que se ha gastado un intento más
			this.sendResult(false, TEST_STATUS.RETRY);
			this.loading.show = false;
			this.ocrConf.general.finishAt = new Date().toISOString();
			this.ocrConf.general.status = TestStatus.OnWarning;
			this.repeatAllProcess();
		}
	}

	/**
	 * Cierra la pantalla de ayuda / reintenta el proceso OCR.
	 * @param consumeAttempt Si es true, suma intento y POST RETRY vía `generalTries`. Por defecto false (cerrar ayuda sin gastar).
	 */
	public retryAllProcess(consumeAttempt: boolean = false): void {
		if (this.midniScreen !== null || this.eudiScreen !== null) {
			this.setPageWarning(null, false);
			return;
		}
		// NIF-802: tras tips/errores del mismo OCR, volver a la UI de adjuntar (no a cámara).
		if (this.flexibleUploadSession) {
			const wasMissingSide = this.pageWarning.type === PAGE_WARNING.FLEXIBLE_UPLOAD_MISSING_SIDE;
			this.startTotalTime();
			if (consumeAttempt) this.generalTries();
			this.isHelpByNavHelp = false;
			this.isMidniHelpContext = false;
			this.restrictionErrorMsg = '';
			this.setPageWarning(null, false);
			this.stopWarningTime();
			// NIF-921: falta cara → conservar ficheros; tips KO → limpiar y reintentar.
			this.returnToFlexibleUploadUi(wasMissingSide ? { keepFiles: true } : { clearFiles: true });
			return;
		}
		this.startTotalTime();
		if (consumeAttempt) this.generalTries();
		this.isHelpByNavHelp = false;
		this.isMidniHelpContext = false;
		this.restrictionErrorMsg = '';
		this.setPageWarning(null, false);
		this.stopWarningTime();
		if (!consumeAttempt) {
			// NIF-785: help / invalid_doc dejan onFail + countTries agotados; reiniciar intento
			// local antes de resumeTimers (si no, el POST reenvía startAt muerto → 2010).
			const currentDocument = this.ocrConf?.document?.find((ocr: IOcr) => !ocr.complete);
			if (currentDocument) {
				currentDocument.status = TestStatus.OnInit;
				currentDocument.countTries = 1;
				currentDocument.finishAt = '';
				currentDocument.showAnimation = this.operation?.config?.setting?.show_guide ?? true;
			}
			this.resumeAfterNavHelp();
		}
	}

	/** Tras cerrar ayuda por nav: reinicia prompts y recupera cámara si el stream murió bajo display:none. */
	private resumeAfterNavHelp(): void {
		this.clearScanningAlert();
		this.audioHistoryService.clearDataHistory();
		this.resumeTimersAfterError();
		this.ensureOcrCameraAfterNavHelp();
	}

	private ensureOcrCameraAfterNavHelp(): void {
		if (!this.isOcrStreamTrackDead() && !this.isOcrPreviewDead()) return;
		this.cameraService.resetCameraStream();
	}

	private isOcrStreamTrackDead(): boolean {
		const mediaStream = this.stream;
		if (!mediaStream?.active) return true;
		const videoTrack = mediaStream.getVideoTracks?.()[0];
		return !videoTrack || videoTrack.readyState === 'ended';
	}

	private isOcrPreviewDead(): boolean {
		if (!this.video) return false;
		return this.video.videoWidth <= 0 || this.video.videoHeight <= 0;
	}

	public checkPermissions(): void {
		this.checkCameraPermissions();
	}

	private checkCameraPermissions(): void {
		const seq = this.cameraService.beginPermissionCheck();
		this.showLoading(this.trans.instant('GENERAL.LOADING.PERMISSIONS'));
		// Arrancamos el reloj justo al iniciar la petición de permisos: así el KO por timeout
		// también cubre el caso en que el usuario no responde al prompt del navegador.
		this.cameraPermissionsTimerService.startWait(() => this.onCameraPermissionsTimeout());
		this.cameraService
			.resolveHasCameraPermission(this.ocrService.constraints)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (hasPermission: boolean) => this.finishPermissionCheck(seq, hasPermission),
				error: () => this.finishPermissionCheck(seq, false),
			});
	}

	private finishPermissionCheck(seq: number, hasPermission: boolean): void {
		if (!this.cameraService.isPermissionCheckCurrent(seq)) {
			return;
		}
		if (hasPermission) {
			this.permissionsCamera = true;
			this.onPermissionsGranted();
			this.activatePreviewLoading('GENERAL.LOADING.CAMERA_STARTING');
			setTimeout(() => {
				this.startWebcam();
			}, 500);
		} else {
			this.permissionsCamera = false;
			this.updateSubtitle();
			this.onPermissionsDenied();
			this.hidePreviewLoading();
		}
		this.hideLoading();
	}

	private canRunTestTimers(): boolean {
		return this.permissionsCamera && this.cameraPermissionsReady;
	}

	private onPermissionsDenied(): void {
		this.stopCameraCaptureLoop();
		this.resetOcrCaptureAfterCameraPermissionLoss();
		this.stopActiveGuidanceAudio();
		this.cameraPermissionsReady = false;
		this.cameraService.releaseActiveStream();
		this.stopTotalTime();
		this.stopWarningTime();
		this.resetTimers();
		this.cameraPermissionsTimerService.startWait(() => this.onCameraPermissionsTimeout());
	}

	/** Detiene locuciones y audios de guía mientras no hay permisos de cámara. */
	private stopActiveGuidanceAudio(): void {
		this.tts.stop();
		this.audioService.stop();
		this.stopSecondlyDataCapture();
		this.audioHistoryService.clearDataHistory();
	}

	private onPermissionsGranted(): void {
		this.cameraPermissionsTimerService.stopWait();
		this.cameraPermissionsReady = true;
		this.resetTestClocksAfterPermissionsGranted();
		this.resumeTestTimersIfApplicable();
	}

	private resetTestClocksAfterPermissionsGranted(): void {
		if (this.ocrConf?.general) {
			this.ocrConf.general.startAt = '';
		}
		(this.ocrConf?.document ?? []).forEach((doc: IOcr) => {
			if (!doc.complete) {
				doc.startAt = '';
			}
		});
		this.stopTotalTime();
		this.timerManagerService.resetTotalTime();
		this.resetTimers();
	}

	private resumeTestTimersIfApplicable(): void {
		if (this.operation?.config?.tutorial && !this.pageWarning.show && !this.handsetLandscape) {
			this.startWarningTime();
		}
		// NIF-801: totalTime solo en captura, no en pantalla tutorial «Estoy listo»
		if (!this.operation?.config?.tutorial && !this.handsetLandscape && !this.pageWarning.show) {
			this.startTotalTime();
		}
	}

	private onCameraPermissionsTimeout(): void {
		// `cameraPermissionsReady` solo pasa a true en onPermissionsGranted(): si seguimos aquí,
		// el usuario no ha concedido permisos (haya respondido NO o no haya respondido al prompt).
		if (this.cameraPermissionsReady) {
			return;
		}
		this.cloudLogsService.sendCloudLogs(
			{
				observations: 'Camera and microphone permissions timeout',
				status: TEST_STATUS.KO_PERMISSION,
			},
			OcrLog.FINISH,
			LogType.Warning,
		);
		void this.sendResult(false, TEST_STATUS.KO_PERMISSION);
	}

	public changeCamera(): void {
		this.cameraService.changeCamera();
	}

	public onCameraSwitchStarted(): void {
		this.positionDocumentService.resetDocumentFitZoom();
		this.syncOcrPreviewZoom();
		onCameraPreviewSwitchStarted({
			activatePreviewLoading: (key) => this.activatePreviewLoading(key),
			hidePreviewLoading: () => this.hidePreviewLoading(),
		});
	}

	public onCameraSwitchCompleted(): void {
		onCameraPreviewSwitchCompleted({
			activatePreviewLoading: (key) => this.activatePreviewLoading(key),
			hidePreviewLoading: () => this.hidePreviewLoading(),
		});
	}

	/*
	 * Nav Help
	 */
	public navHelpChange(action: INavHelp): void {
		const actions = {
			[NavHelpAction.ROTATE]: () => this.changeCamera(),
			[NavHelpAction.QR]: () => (this.isVisibleScan = true),
			[NavHelpAction.HELP]: () => {
				if (this.eudiEnabled && this.eudiScreen === 'wallet') {
					this.openEudiHelp();
					return;
				}
				this.displayHelpWarning(true);
			},
			[NavHelpAction.LANGUAGE]: () => this.languagePickerUi.open(),
			[NavHelpAction.VOLUMEN]: () => this.navHelpService.toggleAudioGuide(action),
		};
		return actions[action.id]();
	}

	public openDocumentHelp(): void {
		this.isMidniHelpContext = false;
		this.setPageHelp('valid_docs');
	}

	public openMiDNIDownloadHelp(): void {
		this.isMidniHelpContext = true;
		this.closeMiDNI();
		this.setPageHelp('valid_docs');
	}

	public openMiDNI(): void {
		if (!this.midniEnabled) {
			return;
		}
		this.tts.stop();
		this.audioService.stop();
		this.midniError = '';
		this.flexibleScreen = null;

		// Dejar de capturar snapshots y de reproducir locuciones del escaneo de DNI físico
		this.stopSecondlyDataCapture();
		this.audioHistoryService.clearDataHistory();

		this.midniScreen = 'desktop';
		const instructionKey = this.isMidniMobileBrowser()
			? 'OCR.MIDNI.SCANNER_INSTRUCTION_MOBILE'
			: 'OCR.MIDNI.SCANNER_INSTRUCTION';
		setTimeout(() => this.tts.speak(instructionKey), 100);
	}

	/** True cuando el flujo MiDNI se muestra en un navegador móvil (texto de ayuda “mismo dispositivo”). */
	public isMidniMobileBrowser(): boolean {
		return this.userAgentService.isMobile();
	}

	/**
	 * @param options.resumePhysicalOcrGuidance Si es false (éxito MiDNI / fin OCR), no reactiva el timer de locuciones del escaneo de DNI físico.
	 */
	public closeMiDNI(options?: { resumePhysicalOcrGuidance?: boolean }): void {
		const resumePhysicalOcrGuidance = options?.resumePhysicalOcrGuidance !== false;
		this.hideLoading();
		this.audioService.stop();
		this.tts.stop();
		this.midniError = '';
		this.midniHintKey = '';
		this.stopMiDNICountdown();
		if (resumePhysicalOcrGuidance) {
			// Volver a activar el timer de locuciones del escaneo de DNI si el usuario vuelve a la cámara clásica
			this.startSecondlyDataCapture();
		} else {
			this.stopSecondlyDataCapture();
		}
	}

	/**
	 * Volver atrás desde MiDNI: en Flexible vuelve al picker de métodos; si no, al escaneo OCR.
	 */
	public backFromMiDNI(): void {
		if (this.isFlexibleMode) {
			this.closeMiDNI({ resumePhysicalOcrGuidance: false });
			this.openFlexiblePicker();
			return;
		}
		this.closeMiDNI();
		this.midniScreen = null;
		this.alertContent = { label: '', status: 'default' };
	}

	// #region Flexible capture (NIF-802)

	/** Link bajo el escaneo: MiDNI directo, upload directo o picker (≥2 alternativas). */
	public onFlexiblePreferClick(): void {
		const alternatives = this.flexibleAlternatives;
		if (alternatives.length === 0) {
			return;
		}
		if (alternatives.length === 1) {
			this.selectFlexibleMethod(alternatives[0]);
			return;
		}
		this.openFlexiblePicker();
	}

	public openFlexiblePicker(): void {
		this.tts.stop();
		this.audioService.stop();
		this.stopSecondlyDataCapture();
		// NIF-921: no seguir contando semi-manual / warning mientras el picker está abierto.
		this.stopTimersDuringRequest();
		this.audioHistoryService.clearDataHistory();
		this.midniScreen = null;
		if (this.eudiScreen !== null) {
			this.closeEudi({ resumePhysicalOcrGuidance: false });
		}
		this.flexibleUploadSession = false;
		this.resetFlexibleUploadState();
		this.flexibleScreen = 'picker';
		this.isModeSemiManual = false;
		this.showCapturedImage = false;
		this.capturedImage = null;
		this.alertContent = this.audioHistoryService.getEmptyAlert();
		this.updateSubtitle();
	}

	public selectFlexibleMethod(method: OcrFlexibleMethod): void {
		if (method === 'scan') {
			this.backToAutomaticScan();
			return;
		}
		if (method === 'midni') {
			this.openMiDNI();
			return;
		}
		if (method === 'eudi') {
			this.openEudi();
			return;
		}
		if (method === 'upload') {
			this.openFlexibleUpload();
			return;
		}
		if (method === 'photo') {
			this.openFlexiblePhoto();
		}
	}

	public openFlexibleUpload(): void {
		this.tts.stop();
		this.audioService.stop();
		this.stopSecondlyDataCapture();
		this.audioHistoryService.clearDataHistory();
		this.midniScreen = null;
		this.resetFlexibleUploadState();
		this.flexibleUploadSession = true;
		this.flexibleScreen = 'upload';
		this.isModeSemiManual = false;
		this.showCapturedImage = false;
		this.capturedImage = null;
		this.alertContent = this.audioHistoryService.getEmptyAlert();
	}

	public openFlexiblePhoto(): void {
		this.tts.stop();
		this.audioService.stop();
		this.midniScreen = null;
		this.flexibleUploadSession = false;
		this.flexibleScreen = 'photo';
		this.isModeSemiManual = true;
		this.showCapturedImage = false;
		this.capturedImage = null;
		this.alertContent = this.audioHistoryService.getEmptyAlert();
		this.startSecondlyDataCapture();
		this.updateSubtitle();
	}

	/** Vuelve al escaneo automático (desde picker, upload o foto). */
	public backToAutomaticScan(): void {
		this.tts.stop();
		this.audioService.stop();
		this.flexibleScreen = null;
		this.flexibleUploadSession = false;
		this.resetFlexibleUploadState();
		this.isModeSemiManual = false;
		this.showCapturedImage = false;
		this.capturedImage = null;
		this.alertContent = this.audioHistoryService.getEmptyAlert();
		// NIF-921: reiniciar timers y estado del lado pendiente (evitar semi-manual/ayuda al volver).
		this.stopTotalTime();
		this.timerManagerService.resetTotalTime();
		this.stopWarningTime();
		const documentTest = this.ocrConf?.document?.find((ocr: IOcr) => !ocr.complete);
		if (documentTest) {
			documentTest.photos = [];
			documentTest.startAt = '';
			documentTest.status = TestStatus.OnInit;
		}
		this.startTotalTime();
		this.startSecondlyDataCapture();
		this.updateSubtitle();
	}

	/** Desde upload: vuelve al picker de métodos. */
	public backFromFlexibleUpload(): void {
		this.openFlexiblePicker();
	}

	/** Desde warning falta-cara: cerrar warning y volver al picker. */
	public changeMethodFromFlexibleUploadMissingSide(): void {
		this.setPageWarning(null, false);
		this.openFlexiblePicker();
	}

	/** Desde foto: vuelve al picker («Prefiero otro método»). */
	public preferOtherMethodFromPhoto(): void {
		this.openFlexiblePicker();
	}

	/** Abre el modal de instrucciones (mismo patrón que DOCUMENTS). */
	public openFlexibleUploadInstructions(): void {
		const passportOnly = this.flexibleUploadDocProfile === 'passport_only';
		this.flexibleUploadInstructionsDoc = {
			uuid: 'ocr-flexible-upload',
			document_type_uuid: '',
			type: passportOnly ? 'passport' : 'dni',
			required: true,
			method: 'manual',
			title: this.flexibleUploadTitleKey,
			instructions: this.trans.instant(this.flexibleUploadHelpBodyKey),
		};
		this.isVisibleFlexibleUploadInstructions = true;
	}

	public async onFlexibleFilesSelected(files: FileList | null): Promise<void> {
		this.flexibleUploadErrorKey = '';
		this.flexibleUploadMissingSide = false;
		this.flexibleUploadDuplicateSide = false;
		if (!files?.length) {
			return;
		}
		const incoming: File[] = [];
		for (let i = 0; i < files.length; i++) {
			const file = files.item(i);
			if (!file) {
				continue;
			}
			if (!isOcrFlexibleUploadFileAllowed(file)) {
				this.flexibleUploadErrorKey = 'OCR.FLEXIBLE.UPLOAD_ERROR_EXTENSION';
				return;
			}
			if (file.size > OCR_FLEXIBLE_UPLOAD_MAX_BYTES) {
				this.flexibleUploadErrorKey = 'OCR.FLEXIBLE.UPLOAD_ERROR_SIZE';
				return;
			}
			incoming.push(file);
		}
		const merged = [...this.flexibleUploadFiles, ...incoming];
		if (merged.length > this.flexibleUploadMaxFiles) {
			this.flexibleUploadErrorKey = 'OCR.FLEXIBLE.UPLOAD_ERROR_MAX_FILES';
			return;
		}
		if (await hasDuplicateOcrFlexibleUploadFiles(merged)) {
			this.flexibleUploadErrorKey = 'OCR.FLEXIBLE.UPLOAD_ERROR_DUPLICATE_FILES';
			return;
		}
		this.flexibleUploadFiles = merged;
		this.flexibleUploadControl.setValue(null);
	}

	/** True mientras quepan más caras (máx. 1 pasaporte / 2 documento). */
	get canAddMoreFlexibleUploadFiles(): boolean {
		return this.flexibleUploadFiles.length < this.flexibleUploadMaxFiles;
	}

	public removeFlexibleUploadFile(index: number): void {
		this.flexibleUploadFiles = this.flexibleUploadFiles.filter((_, i) => i !== index);
		this.flexibleUploadControl.setValue(null);
		this.flexibleUploadErrorKey = '';
		if (index < this.flexibleUploadConsumedCount) {
			this.flexibleUploadConsumedCount = Math.max(0, this.flexibleUploadConsumedCount - 1);
		}
		if (!this.flexibleUploadFiles.length) {
			this.flexibleUploadMissingSide = false;
			this.flexibleUploadConsumedCount = 0;
		}
	}

	public async submitFlexibleUpload(): Promise<void> {
		if (!this.flexibleUploadFiles.length || this.sendingResult) {
			return;
		}
		this.flexibleUploadErrorKey = '';
		this.flexibleUploadSession = true;
		this.flexibleUploadDuplicateSide = false;

		try {
			if (await hasDuplicateOcrFlexibleUploadFiles(this.flexibleUploadFiles)) {
				this.flexibleUploadErrorKey = 'OCR.FLEXIBLE.UPLOAD_ERROR_DUPLICATE_FILES';
				return;
			}
			// Solo ficheros aún no aceptados (si ya OK la 1ª cara, no reenviar el 1er archivo).
			const filesToProcess = this.flexibleUploadFiles.slice(this.flexibleUploadConsumedCount);
			if (!filesToProcess.length) {
				// NIF-921: falta cara → pantalla completa (no alert inline).
				this.showFlexibleUploadMissingSideWarning();
				return;
			}

			const sideImages = await this.buildFlexibleUploadSideImagesFromFiles(filesToProcess);
			if (!sideImages.length) {
				// NIF-1082: ya se mostró tile «ambas caras en una foto»; no pisar con error de lectura.
				if (this.pageWarning.type === PAGE_WARNING.BOTH_SIDES_IN_ONE_IMAGE) {
					return;
				}
				this.flexibleUploadErrorKey = 'OCR.FLEXIBLE.UPLOAD_ERROR_READ';
				return;
			}

			this.flexibleUploadMissingSide = false;
			// Spinner antes de quitar overlay upload (evita flash de cámara/UI).
			this.showLoading(this.trans.instant('OCR.GENERAL.CHECKING_DATA'));
			// ≥2 imágenes nuevas: encadenar sendResult sin UI/TTS entre caras.
			this.flexibleUploadChaining = sideImages.length > 1;
			this.flexibleScreen = null;

			for (let i = 0; i < sideImages.length; i++) {
				const dataUrl = sideImages[i];
				const documentTest = this.getPendingOcrDocument();
				if (!documentTest) {
					this.flexibleUploadChaining = false;
					this.hideLoading();
					this.returnToFlexibleUploadUi({ keepFiles: true });
					return;
				}
				documentTest.photos = [
					{
						img: dataUrl,
						lapacian: this.ocrConf.general.minSharpnessLapacian,
						fourier: this.ocrConf.general.minSharpnessFourier,
						label: filesToProcess[Math.min(i, filesToProcess.length - 1)]?.name || 'upload',
						docSize: { originX: 0, originY: 0, width: 1, height: 1, angle: 0 },
						selected: false,
						gridAnalysisBrightness: {
							gridSize: 0,
							threshold: 0,
							cells: [],
						},
						consecutiveCellsWith95: [],
						consecutiveCellsWith98: [],
					},
				];
				this.capturedImage = dataUrl;
				this.showCapturedImage = true;

				const ok = await this.sendResult(true);
				if (!ok) {
					this.flexibleUploadChaining = false;
					if (this.pageWarning.show) {
						// NIF-921 Figma ADJUNTAR KO: conservar ficheros («revisa los archivos adjuntos»).
						this.flexibleScreen = null;
						this.hideCapturedImagePreview();
						this.hideLoading();
						return;
					}
					this.hideLoading();
					this.returnToFlexibleUploadUi({ keepFiles: true, missingSide: true });
					return;
				}

				this.flexibleUploadConsumedCount = Math.min(
					this.flexibleUploadFiles.length,
					this.flexibleUploadConsumedCount + 1,
				);

				if (this.isThereAnyPendingDocumentOcr()) {
					if (i < sideImages.length - 1) {
						continue;
					}
					this.flexibleUploadChaining = false;
					this.hideLoading();
					this.returnToFlexibleUploadUi({ keepFiles: true, missingSide: true });
					return;
				}

				this.flexibleUploadChaining = false;
				this.flexibleScreen = null;
				this.flexibleUploadSession = false;
				this.resetFlexibleUploadState();
				this.hideLoading();
				await this.finishValidation();
				return;
			}
			this.flexibleUploadChaining = false;
			this.hideLoading();
		} catch {
			this.flexibleUploadChaining = false;
			this.hideLoading();
			this.flexibleUploadErrorKey = 'OCR.FLEXIBLE.UPLOAD_ERROR_READ';
			this.returnToFlexibleUploadUi({ keepFiles: true });
		}
	}

	/**
	 * 2+ ficheros → cada uno es una cara.
	 * 1 fichero → una imagen; si parece ambas caras juntas (NIF-1082) → [] y tile de error.
	 */
	private async buildFlexibleUploadSideImagesFromFiles(files: File[]): Promise<string[]> {
		const sideImages: string[] = [];
		const checkBothSidesInOne = files.length === 1 && this.flexibleUploadConsumedCount === 0;

		for (const file of files) {
			const rawDataUrl = await readFileAsDataUrl(file);
			if (!rawDataUrl) {
				return [];
			}
			const normalized = await normalizeOcrFlexibleUploadDataUrl(rawDataUrl);
			if (checkBothSidesInOne && (await this.detectBothSidesInOneUploadImage(normalized))) {
				this.showFlexibleUploadBothSidesInOneWarning();
				return [];
			}
			sideImages.push(normalized);
		}
		return sideImages;
	}

	/**
	 * NIF-1082: MediaPipe ≥2 docs o aspect muy ancho/alto → ambas caras en una foto (rechazar).
	 */
	private async detectBothSidesInOneUploadImage(dataUrl: string): Promise<boolean> {
		if (!dataUrl.startsWith('data:image/')) {
			return false;
		}
		const fromDetector = await this.cropDocumentsFromUploadWithDetector(dataUrl);
		if (fromDetector.length >= 2) {
			return true;
		}
		return looksLikeDualLayoutUploadDataUrl(dataUrl);
	}

	/** Usa el ObjectDetector OCR ya cargado (modo IMAGE) para recortar cada documento. */
	private async cropDocumentsFromUploadWithDetector(dataUrl: string): Promise<string[]> {
		if (!this.documentDetector) {
			return [];
		}
		const img = await new Promise<HTMLImageElement | null>((resolve) => {
			const el = new Image();
			el.onload = () => resolve(el);
			el.onerror = () => resolve(null);
			el.src = dataUrl;
		});
		if (!img) {
			return [];
		}

		const prevMode = this.runningMode;
		if (this.animationFrame) {
			cancelAnimationFrame(this.animationFrame);
			this.animationFrame = 0;
		}
		try {
			if (prevMode !== 'IMAGE') {
				await this.documentDetector.setOptions({ runningMode: 'IMAGE' });
				this.runningMode = 'IMAGE';
			}
			const result = this.documentDetector.detect(img);
			const documentBoxes = (result?.detections ?? [])
				.filter((d) => {
					const name = d.categories?.[0]?.categoryName;
					return name === MODEL_DOCUMENT_CATEGORY.DOCUMENT || name === MODEL_DOCUMENT_CATEGORY.PASSPORT;
				})
				.map((d) => d.boundingBox)
				.filter((b): b is NonNullable<typeof b> => !!b)
				.sort((a, b) => a.originX - b.originX || a.originY - b.originY);

			if (documentBoxes.length < 2) {
				return [];
			}
			const crops: string[] = [];
			for (const box of documentBoxes.slice(0, 2)) {
				crops.push(await cropOcrFlexibleUploadDataUrl(dataUrl, box));
			}
			return crops;
		} catch {
			return [];
		} finally {
			if (prevMode === 'VIDEO' && this.documentDetector) {
				try {
					await this.documentDetector.setOptions({ runningMode: 'VIDEO' });
					this.runningMode = 'VIDEO';
				} catch {
					/* ignore */
				}
				if (this.ocrCaptureActive && this.flexibleScreen !== 'upload' && this.flexibleScreen !== 'picker') {
					this.schedulePredictWebcamFrame();
				}
			}
		}
	}

	/**
	 * Vuelve a la UI de adjuntar tras prompt de cara / tips.
	 * `keepFiles`: conserva el 1er archivo cuando solo falta la otra cara.
	 * `missingSide`: muestra PAGE_WARNING fullscreen (NIF-921 Carmen).
	 */
	private returnToFlexibleUploadUi(options?: {
		keepFiles?: boolean;
		clearFiles?: boolean;
		missingSide?: boolean;
		duplicateSide?: boolean;
	}): void {
		this.flexibleUploadSession = true;
		this.flexibleScreen = 'upload';
		this.loading.show = false;
		this.hideCapturedImagePreview();
		this.flexibleUploadErrorKey = '';
		this.flexibleUploadMissingSide = !!options?.missingSide;
		this.flexibleUploadDuplicateSide = !!options?.duplicateSide;
		if (options?.clearFiles) {
			this.flexibleUploadFiles = [];
			this.flexibleUploadControl.setValue(null);
			this.flexibleUploadConsumedCount = 0;
			this.flexibleUploadMissingSide = false;
			this.flexibleUploadDuplicateSide = false;
		} else if (!options?.keepFiles) {
			this.flexibleUploadFiles = [];
			this.flexibleUploadControl.setValue(null);
			this.flexibleUploadConsumedCount = 0;
		}
		if (options?.missingSide) {
			this.showFlexibleUploadMissingSideWarning(!!options?.duplicateSide);
		}
	}

	/** Pantalla completa cuando falta anverso/reverso al Continuar (sin alert inline ni TTS). */
	private showFlexibleUploadMissingSideWarning(duplicateSide: boolean = false): void {
		this.flexibleUploadKoAfterSend = false;
		this.flexibleUploadDuplicateSide = duplicateSide;
		this.flexibleUploadSession = true;
		this.flexibleScreen = 'upload';
		this.loading.show = false;
		this.hideCapturedImagePreview();
		this.flexibleUploadErrorKey = '';
		this.flexibleUploadMissingSide = true;
		this.setPageWarning(PAGE_WARNING.FLEXIBLE_UPLOAD_MISSING_SIDE);
	}

	/** Figma ADJUNTAR KO: tile status tras KO OCR de ficheros adjuntos. */
	private showFlexibleUploadKoWarning(): void {
		this.flexibleUploadKoAfterSend = true;
		this.flexibleUploadDuplicateSide = false;
		this.flexibleUploadSession = true;
		this.flexibleScreen = 'upload';
		this.loading.show = false;
		this.hideCapturedImagePreview();
		this.flexibleUploadErrorKey = '';
		this.flexibleUploadMissingSide = true;
		this.stopTimersDuringRequest();
		this.setPageWarning(PAGE_WARNING.FLEXIBLE_UPLOAD_MISSING_SIDE);
	}

	/** NIF-1082: anverso+reverso en la misma foto (detección local o KO 2074). */
	private showFlexibleUploadBothSidesInOneWarning(): void {
		this.flexibleUploadKoAfterSend = true;
		this.flexibleUploadSession = true;
		this.flexibleScreen = 'upload';
		this.loading.show = false;
		this.hideCapturedImagePreview();
		this.flexibleUploadErrorKey = '';
		this.flexibleUploadMissingSide = false;
		this.flexibleUploadDuplicateSide = false;
		this.stopTimersDuringRequest();
		this.setPageWarning(PAGE_WARNING.BOTH_SIDES_IN_ONE_IMAGE);
	}

	public truncateFlexibleFileName(fileName: string, maxLength: number = 28): string {
		if (!fileName || fileName.length <= maxLength) {
			return fileName;
		}
		const lastDotIndex = fileName.lastIndexOf('.');
		const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';
		const nameWithoutExtension = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
		if (nameWithoutExtension.length <= maxLength) {
			return fileName;
		}
		return `${nameWithoutExtension.substring(0, maxLength)}…${extension}`;
	}

	public flexibleMethodTitleKey(method: OcrFlexibleMethod): string {
		switch (method) {
			case 'midni':
				return 'OCR.FLEXIBLE.METHOD_MIDNI_TITLE';
			case 'eudi':
				return 'OCR.FLEXIBLE.METHOD_EUDI_TITLE';
			case 'upload':
				return 'OCR.FLEXIBLE.METHOD_UPLOAD_TITLE';
			case 'photo':
				return 'OCR.FLEXIBLE.METHOD_PHOTO_TITLE';
			case 'scan':
				return 'OCR.FLEXIBLE.METHOD_SCAN_TITLE';
		}
	}

	public flexibleMethodDescriptionKey(method: OcrFlexibleMethod): string {
		switch (method) {
			case 'midni':
				return 'OCR.FLEXIBLE.METHOD_MIDNI_DESCRIPTION';
			case 'eudi':
				return 'OCR.FLEXIBLE.METHOD_EUDI_DESCRIPTION';
			case 'upload':
				return 'OCR.FLEXIBLE.METHOD_UPLOAD_DESCRIPTION';
			case 'photo':
				return 'OCR.FLEXIBLE.METHOD_PHOTO_DESCRIPTION';
			case 'scan':
				return 'OCR.FLEXIBLE.METHOD_SCAN_DESCRIPTION';
		}
	}

	public flexibleMethodTagImage(method: OcrFlexibleMethod, index: number): ITagImage {
		return ocrFlexibleMethodTagImage(method, index);
	}

	private resetFlexibleUploadState(): void {
		this.flexibleUploadFiles = [];
		this.flexibleUploadControl.setValue(null);
		this.flexibleUploadErrorKey = '';
		this.flexibleUploadMissingSide = false;
		this.flexibleUploadBothSidesAlertDismissed = false;
		this.flexibleUploadConsumedCount = 0;
		this.flexibleUploadChaining = false;
		this.flexibleUploadKoAfterSend = false;
		this.flexibleUploadDuplicateSide = false;
		this.isVisibleFlexibleUploadInstructions = false;
	}

	// #endregion Flexible capture

	/**
	 * Llamado por MidniQrScannerComponent cuando detecta un QR en la cámara.
	 * Envía los bytes al backend para validar la firma ECDSA y extraer los datos.
	 */
	public onMiDNIQrDetected(event: MiDNIQrResult): void {
		this.showLoading(this.trans.instant('OCR.MIDNI.VALIDATING'));
		this.midniError = '';

		this.midniApiService.validateQr(event.qrBase64, event.frameBase64, event.qrImageBase64).subscribe({
			next: (response) => {
				this.hideLoading();
				if (response.result === OPERATION_STATUS.OK) {
					this.handleMiDNISuccess(response);
				} else {
					// Misma guía para cualquier rechazo del backend
					this.midniError = response.error ?? '';
					this.midniHintKey = 'OCR.MIDNI.PROMPT_4';
					this.tts.speak('OCR.MIDNI.PROMPT_4');
					const isAgeQr =
						response.error?.toLowerCase().includes('edad') ||
						response.error?.toLowerCase().includes('solo edad');
					if (isAgeQr) {
						setTimeout(() => {
							this.midniError = '';
							this.midniHintKey = '';
						}, 4000);
					}
				}
			},
			error: () => {
				this.hideLoading();
				this.midniError = 'error';
				this.midniHintKey = 'OCR.MIDNI.PROMPT_4';
				this.tts.speak('OCR.MIDNI.PROMPT_4');
			},
		});
	}

	public retryMiDNI(): void {
		this.hideLoading();
		this.midniError = '';
		this.midniHintKey = '';
		// Nota: al limpiar midniError el template recrea el scanner (ngOnInit lo reinicia)
	}

	/**
	 * Recibe la clave i18n del prompt del scanner y:
	 * 1. Actualiza el texto de guía visible en pantalla (midniHintKey)
	 * 2. Locuta con el mismo AudioService/TTS que el resto de la app (misma voz)
	 */
	public onMiDNIScanPrompt(key: string): void {
		this.alertContent = {
			label: this.trans.instant(key),
			status: 'default',
			icon: 'icon-info',
		};
		this.tts.speak(key);
	}

	public openMiDNIAppStore(): void {
		window.open('https://apps.apple.com/es/app/midni/id6477598076', '_blank');
	}

	public openMiDNIPlayStore(): void {
		window.open('https://play.google.com/store/apps/details?id=es.gob.interior.policia.midni&hl=es', '_blank');
	}

	private handleMiDNISuccess(response: { result?: string; process?: IOperation[] }): void {
		const syntheticResponse: IOcrResponse = {
			result: OPERATION_STATUS.OK,
			required_other_side: false,
			required_side: 'front',
			retry: false,
			process: response.process ?? [],
			code: 200,
		};

		this.tts.stop();
		this.audioService.stop();
		this.midniHintKey = '';
		this.alertContent = this.audioHistoryService.getEmptyAlert();
		this.audioHistoryService.clearDataHistory();
		this.midniScreen = null;

		this.notifyDocumentComplete(syntheticResponse, DOCUMENT_CHECK_ID.FRONT);
		this.updateOperations(syntheticResponse);
		this.closeMiDNI({ resumePhysicalOcrGuidance: false });
		// No usar finishValidation(): exige slots OCR físicos completos (guard de parejas).
		// MiDNI sustituye el OCR; cancelar con closeMiDNI() sí vuelve a la lectura normal.
		this.stopOcrCapture();
		this.ocrConf.general.controlValidation = true;
		this.ocrConf.general.status = TestStatus.OnComplete;
		this.ocrConf.general.complete = true;
		this.nextOperation(OPERATION_STATUS.OK);
	}

	private stopMiDNICountdown(): void {
		if (this.midniCountdownSub) {
			this.midniCountdownSub.unsubscribe();
			this.midniCountdownSub = null;
		}
	}

	// ─── EUDI Wallet (NIF-960) ───────────────────────────────────────────────

	public openEudi(options?: { armGestureSpeakFallback?: boolean }): void {
		if (!this.eudiEnabled) {
			return;
		}
		this.tts.stop();
		this.audioService.stop();
		// Pausar timers OCR (side_max_time / warning / semimanual) para no mezclarlos con EUDI.
		this.stopTimersDuringRequest();
		this.isModeSemiManual = false;
		this.stopSecondlyDataCapture();
		this.audioHistoryService.clearDataHistory();
		this.midniScreen = null;
		this.flexibleScreen = null;
		this.flexibleUploadSession = false;
		this.resetFlexibleUploadState();
		this.eudiError = '';
		this.eudiHelpExpanded = {};
		this.eudiValidateCompleted = false;
		this.eudiScreen = 'wallet';
		this.startEudiPresentation();
		this.playEudiInstruction(options?.armGestureSpeakFallback === true);
	}

	/**
	 * Volver desde EUDI: en Flexible → picker («Cambiar de método»); si no, al escaneo OCR.
	 */
	public backFromEudi(): void {
		if (this.isFlexibleMode) {
			this.closeEudi({ resumePhysicalOcrGuidance: false });
			this.openFlexiblePicker();
			return;
		}
		this.closeEudi();
		this.alertContent = { label: '', status: 'default' };
	}

	/**
	 * Locuta instrucción EUDI. Si el MP3 ya está en caché, habla en sync (mantiene gesto
	 * del clic «Prefiero…»). Si no, precarga y habla al resolver.
	 * En auto-entrada (sin gesto), Howler puede silenciar: fallback en el primer pointerdown.
	 */
	private playEudiInstruction(armGestureSpeakFallback = false): void {
		const instructionKey = resolveEudiInstructionKey(this.isEudiMobileContext);
		const speakNow = (): void => {
			if (this.eudiScreen !== 'wallet') {
				return;
			}
			this.tts.speak(instructionKey, true);
		};

		if (this.audioService.hasAudio(instructionKey)) {
			speakNow();
		} else {
			void this.audioService.preloadEudiInstructionAudios().then(speakNow);
		}

		if (armGestureSpeakFallback) {
			const onGesture = (): void => {
				if (this.eudiScreen === 'wallet') {
					this.tts.speak(instructionKey, true);
				}
			};
			window.addEventListener('pointerdown', onGesture, { once: true, capture: true });
			window.addEventListener('keydown', onGesture, { once: true, capture: true });
		}
	}

	/**
	 * NIF-960: si el workflow solo permite EUDI (sin DNI/pasaporte/NIE),
	 * abrir directamente la pantalla wallet (QR / Abrir Wallet).
	 */
	private async bootstrapEudiOnlyEntry(): Promise<void> {
		if (!isEudiOnlyOcrConfig(this.operation?.config)) {
			return;
		}
		if (this.operation?.config?.tutorial || this.eudiScreen !== null || this.pageWarning?.show) {
			return;
		}
		await this.audioService.preloadEudiInstructionAudios();
		void this.audioService.preloadOcrMrzAudios();
		// Sin gesto de usuario: Howler puede exigir unlock → fallback en primer toque.
		this.openEudi({ armGestureSpeakFallback: true });
	}

	public openEudiHelp(): void {
		this.eudiHelpExpanded = {};
		this.eudiScreen = 'help';
	}

	public closeEudiHelp(): void {
		this.eudiHelpExpanded = {};
		this.eudiScreen = 'wallet';
	}

	public toggleEudiHelp(key: string): void {
		this.eudiHelpExpanded[key] = !this.eudiHelpExpanded[key];
	}

	/** Deep link a la Wallet (móvil). */
	public openEudiWalletApp(): void {
		if (!this.eudiAuthorizationUri) {
			return;
		}
		window.location.href = this.eudiAuthorizationUri;
	}

	public openEudiPlayStore(): void {
		// TODO
		console.log('openEudiPlayStore');
	}

	public openEudiAppStore(): void {
		// TODO
		console.log('openEudiAppStore');
	}

	public closeEudi(options?: { resumePhysicalOcrGuidance?: boolean }): void {
		const resumePhysicalOcrGuidance = options?.resumePhysicalOcrGuidance !== false;
		this.hideLoading();
		this.stopEudiPoll();
		this.stopEudiSessionWatch();
		this.eudiScreen = null;
		this.eudiAuthorizationUri = null;
		this.eudiTransactionId = null;
		this.eudiError = '';
		this.eudiWaiting = false;
		this.eudiSessionDeadlineMs = null;
		if (resumePhysicalOcrGuidance) {
			// Volver a OCR físico: reanudar timers de lado (side_max_time) desde cero limpio.
			this.resumeTimersAfterError();
			this.startSecondlyDataCapture();
		} else {
			this.stopSecondlyDataCapture();
		}
	}

	public retryEudi(): void {
		if (this.eudiValidateCompleted) return;
		this.eudiError = '';
		this.startEudiPresentation();
	}

	private startEudiPresentation(): void {
		if (this.eudiValidateCompleted) return;
		this.stopEudiPoll();
		this.stopEudiSessionWatch();
		this.eudiWaiting = true;
		this.eudiAuthorizationUri = null;
		this.eudiTransactionId = null;
		const maxSessionSeconds = this.eudiMaxSessionSeconds;
		this.eudiSessionDeadlineMs = Date.now() + maxSessionSeconds * 1000;
		this.startEudiSessionWatch();

		this.eudiApiService.init({ same_device: this.isEudiMobileContext }).subscribe({
			next: (res) => {
				if (this.eudiScreen !== 'wallet' || this.eudiValidateCompleted) {
					return;
				}
				this.eudiTransactionId = res.transaction_id;
				this.eudiAuthorizationUri = res.authorization_request_uri;
				this.eudiWaiting = true;
				this.eudiPollSub = this.eudiApiService.pollUntilDone(res.transaction_id, maxSessionSeconds).subscribe({
					next: (status) => {
						if (status.status === 'completed') {
							if (this.eudiValidateCompleted) return;
							this.completeEudi(res.transaction_id);
						} else if (status.status === 'failed') {
							this.onEudiSessionFailed(status.feedback?.message);
						}
					},
					error: () => {
						this.onEudiSessionFailed('error');
					},
				});
			},
			error: () => {
				this.onEudiSessionFailed('error');
			},
		});
	}

	/** Caducidad local de sesión EUDI (techo = side_max_time del back). */
	private startEudiSessionWatch(): void {
		this.stopEudiSessionWatch();
		this.eudiSessionWatchSub = interval(1000).subscribe(() => {
			if (this.eudiScreen !== 'wallet' || this.eudiSessionDeadlineMs === null) {
				return;
			}
			if (Date.now() >= this.eudiSessionDeadlineMs) {
				this.onEudiSessionFailed('poll_timeout');
			}
		});
	}

	private stopEudiSessionWatch(): void {
		if (this.eudiSessionWatchSub) {
			this.eudiSessionWatchSub.unsubscribe();
			this.eudiSessionWatchSub = null;
		}
	}

	private onEudiSessionFailed(reason?: string): void {
		this.stopEudiPoll();
		this.stopEudiSessionWatch();
		this.eudiWaiting = false;
		this.eudiError =
			reason === 'poll_timeout'
				? this.trans.instant('OCR.EUDI.EXPIRED')
				: reason === 'error' || !reason
					? 'error'
					: reason;
	}

	private completeEudi(transactionId: string): void {
		if (this.eudiValidateCompleted || this.operation?.completed) {
			return;
		}
		this.stopEudiPoll();
		this.showLoading(this.trans.instant('OCR.EUDI.WAITING'));
		this.eudiApiService.validate(transactionId).subscribe({
			next: (response) => {
				this.hideLoading();
				if (response.result === OPERATION_STATUS.OK) {
					this.handleEudiSuccess(response);
				} else {
					this.eudiWaiting = false;
					this.eudiError = response.error ?? 'error';
				}
			},
			error: () => {
				this.hideLoading();
				this.eudiWaiting = false;
				this.eudiError = 'error';
			},
		});
	}

	private handleEudiSuccess(response: { result?: string; process?: IOperation[] }): void {
		this.eudiValidateCompleted = true;
		this.stopEudiPoll();
		this.processService.unSubcribeCheckStatus();

		// Omitir POL: marcar completed en lista local antes del sync (evita Finish NIF-969 falso).
		const syncedProcess = this.applyEudiSkipPolCompleted(response.process);

		const syntheticResponse: IOcrResponse = {
			result: OPERATION_STATUS.OK,
			required_other_side: false,
			required_side: 'front',
			retry: false,
			process: syncedProcess,
			code: 200,
		};

		this.tts.stop();
		this.audioService.stop();
		this.alertContent = this.audioHistoryService.getEmptyAlert();
		this.audioHistoryService.clearDataHistory();
		this.eudiScreen = null;

		this.notifyDocumentComplete(syntheticResponse, DOCUMENT_CHECK_ID.FRONT);
		// Misma longitud HOME/OCR/…: updateOperations no-op; forzar process del back (OCR completed).
		this.forceSyncOperations(syncedProcess);
		this.closeEudi({ resumePhysicalOcrGuidance: false });
		this.stopOcrCapture();
		this.ocrConf.general.controlValidation = true;
		this.ocrConf.general.status = TestStatus.OnComplete;
		this.ocrConf.general.complete = true;
		this.nextOperation(OPERATION_STATUS.OK);
	}

	private stopEudiPoll(): void {
		if (this.eudiPollSub) {
			this.eudiPollSub.unsubscribe();
			this.eudiPollSub = null;
		}
		this.stopEudiSessionWatch();
	}

	// ─── Barcode scan (DNI_COL PDF417) ───────────────────────────────────────

	private enterBarcodeScanMode(_response: IOcrResponse): void {
		this.awaitingBarcodeScan = true;
		this.isGapBetweenTests = false;
		this.hideCapturedImagePreview();
		this.showCapturedImage = false;
		this.cameraService.setProcessingOCR(false);

		if (this.animationFrame) {
			cancelAnimationFrame(this.animationFrame);
			this.animationFrame = 0;
		}

		this.stopSecondlyDataCapture();
		this.audioHistoryService.clearDataHistory();
		this.hideLoading();

		// Montar barcode con forceFreshGetUserMedia: nuevo getUserMedia (4K) y libera tracks OCR.
		this.barcodeScanActive = true;
		this.updateSubtitle();
		this.startBarcodeInstructionAudio();
	}

	private startBarcodeInstructionAudio(): void {
		this.stopBarcodeInstructionAudio();
		setTimeout(() => this.tts.speak('OCR.BARCODE.INSTRUCTION'), 100);
		this.barcodeInstructionIntervalId = setInterval(() => {
			if (!this.barcodeScanActive || this.barcodeSending) {
				return;
			}
			this.tts.speak('OCR.BARCODE.INSTRUCTION', true);
		}, OcrComponent.BARCODE_INSTRUCTION_REPEAT_MS);
	}

	private stopBarcodeInstructionAudio(): void {
		if (this.barcodeInstructionIntervalId !== null) {
			clearInterval(this.barcodeInstructionIntervalId);
			this.barcodeInstructionIntervalId = null;
		}
	}

	private leaveBarcodeScanMode(): void {
		this.stopBarcodeInstructionAudio();
		this.barcodeScanActive = false;
		this.awaitingBarcodeScan = false;
		this.updateSubtitle();
	}

	public onBarcodeManualCapture(): void {
		this.barcodeScanner?.resumeCapture();
		this.barcodeScanner?.captureManually();
	}

	public onBarcodeImageCaptured(frame: IBarcodeCapturedFrame): void {
		if (this.barcodeSending || !this.barcodeScanActive) {
			this.barcodeScanner?.resumeCapture();
			return;
		}

		this.barcodeSending = true;
		this.showLoading(this.trans.instant('OCR.GENERAL.CHECKING_DATA'));

		this.ocrService.sendReadBarcode(this.buildReadBarcodePayload(frame)).subscribe({
			next: (response: IBarcodeReadResponse) => this.handleReadBarcodeResponse(response),
			error: (error: { error: IOcrErrorResponse }) => {
				this.barcodeSending = false;
				this.hideLoading();
				this.handleReadBarcodeError(error);
			},
		});
	}

	private handleReadBarcodeResponse(response: IBarcodeReadResponse): void {
		this.barcodeSending = false;
		this.hideLoading();

		if (response.result === OPERATION_STATUS.OK) {
			// Max attempts skip o cierre OCR: avanzar sin exigir verified.
			if (response.ocr_completed || response.barcode?.skipped) {
				this.completeOcrAfterBarcodeVerified(response);
				return;
			}

			if (response.barcode?.verified) {
				void this.finalizeOcrAfterBarcode();
				return;
			}

			if (response.barcode?.retry) {
				this.barcodeScanner?.resumeCapture();
				return;
			}
		}

		this.leaveBarcodeScanMode();
		this.verificationFail(OPERATION_STATUS.KO);
	}

	/**
	 * Cierre OCR tras barcode OK (ocr_completed o POST OCR posterior sin required_scan).
	 * No usa finishValidation(): en integration ese método exige slots OCR físicos completos
	 * (guard de parejas). Mismo criterio que MiDNI en integration.
	 */
	private advanceAfterBarcodeOcrComplete(
		response: IOcrResponse | IBarcodeReadResponse,
		documentTestId: DOCUMENT_CHECK_ID,
	): void {
		this.leaveBarcodeScanMode();

		for (const doc of this.ocrConf.document) {
			this.markOcrDocumentComplete(doc);
		}

		this.forceSyncOperations(response.process);

		this.screenShotService.stopSaveScreenshot();
		this.notifyDocumentComplete(response as IOcrResponse, documentTestId);

		this.ocrConf.general.controlValidation = true;
		this.ocrConf.general.status = TestStatus.OnComplete;
		this.ocrConf.general.complete = true;
		this.nextOperation(OPERATION_STATUS.OK);
	}

	private completeOcrAfterBarcodeVerified(response: IBarcodeReadResponse): void {
		this.advanceAfterBarcodeOcrComplete(response, DOCUMENT_CHECK_ID.BACK);
	}

	private handleReadBarcodeError(error: { error: IOcrErrorResponse }): void {
		this.handleSendResultError(error, TEST_STATUS.KO, () => {
			this.leaveBarcodeScanMode();
			// Barcode forceFreshGetUserMedia mató el stream OCR; remount al volver a captura.
			this.ensureOcrCameraAfterNavHelp();
		});
	}

	private buildReadBarcodePayload(frame: IBarcodeCapturedFrame): IBarcodeReadPayload {
		let camerasToUse: MediaDeviceInfo[] = this.allCameras;
		if (this.cameraService.allCamerasValue?.length) {
			camerasToUse = this.cameraService.allCamerasValue;
		}

		return {
			status: true,
			image: frame.image,
			device_id: this.auth.getDeviceId(),
			cameras: camerasToUse.map((c: MediaDeviceInfo) => c.label),
			cropped: frame.cropped,
			upscale: true,
		};
	}

	private buildBackSideOcrPayload(): IOcrPayload {
		const backDoc = this.ocrConf.document.find((ocr: IOcr) => ocr.documentTestId === DOCUMENT_CHECK_ID.BACK);
		const result = backDoc?.photos?.length ? this.selectBestPhoto(backDoc.photos) : null;
		let camerasToUse: MediaDeviceInfo[] = this.allCameras;
		if (this.cameraService.allCamerasValue?.length) {
			camerasToUse = this.cameraService.allCamerasValue;
		}

		return {
			status: true,
			image: result?.img ?? '',
			device_id: this.auth.getDeviceId(),
			cameras: camerasToUse.map((c: MediaDeviceInfo) => c.label),
			capture_method: resolveOcrCaptureMethod({
				flexibleUploadSession: this.flexibleUploadSession,
				flexibleScreen: this.flexibleScreen,
				isModeSemiManual: this.isModeSemiManual,
			}),
			data: JSON.stringify({
				status: TEST_STATUS.OK,
				ocrConf: {
					...this.ocrConf,
					document: this.ocrConf.document.map(({ photos, ...rest }) => ({
						...rest,
						photos: photos.map(({ img, ...rest2 }) => rest2),
					})),
				},
			}),
		};
	}

	private async finalizeOcrAfterBarcode(): Promise<void> {
		this.leaveBarcodeScanMode();
		this.showLoading(this.trans.instant('OCR.GENERAL.CHECKING_DATA'));
		this.cameraService.setProcessingOCR(true);

		return new Promise((resolve) => {
			this.ocrService.sendInfoOcr(this.buildBackSideOcrPayload()).subscribe({
				next: (response: IOcrResponse) => {
					try {
						this.cameraService.setProcessingOCR(false);
						this.hideLoading();

						if (
							response.result === OPERATION_STATUS.OK &&
							!response.barcode?.required_scan &&
							!response.required_other_side
						) {
							this.advanceAfterBarcodeOcrComplete(response, DOCUMENT_CHECK_ID.BACK);
							resolve();
							return;
						}

						if (response.result === OPERATION_STATUS.OK && response.barcode?.required_scan) {
							this.enterBarcodeScanMode(response);
							resolve();
							return;
						}

						const error = { error: response as unknown as IOcrErrorResponse };
						this.handleSendResultError(error, TEST_STATUS.KO, () => resolve());
					} catch {
						this.cameraService.setProcessingOCR(false);
						this.hideLoading();
						resolve();
					}
				},
				error: (error: { error: IOcrErrorResponse }) => {
					this.cameraService.setProcessingOCR(false);
					this.hideLoading();
					this.handleSendResultError(error, TEST_STATUS.KO, () => resolve());
				},
			});
		});
	}

	// #region TIMERS
	/*
	 * Timer
	 */
	public startTest(): void {
		this.startTotalTime();
	}

	// Total time
	private startTotalTime(): void {
		if (!this.canRunTestTimers()) return;
		// NIF-801: no contar espera en tutorial («Estoy listo»)
		if (this.operation?.config?.tutorial) return;
		this.timerManagerService.startTotalTime();
	}

	private stopTotalTime(): void {
		this.timerManagerService.stopTotalTime();
	}

	/**
	 * Detener todos los timers relevantes durante una petición al servidor
	 * Esto incluye todos los timers que podrían disparar acciones mientras esperamos respuesta
	 */
	private stopTimersDuringRequest(): void {
		// Detener el timer de tiempo total
		this.stopTotalTime();
		// Detener el warningTime si está activo (este es el que puede abrir la pantalla de help)
		this.stopWarningTime();
		// Detener los timers de validación del documento
		this.resetTimers();
	}

	/**
	 * Reanuda los timers después de un error que permite reintentar
	 * Reinicia el flujo normal como si estuviera empezando de 0
	 */
	private resumeTimersAfterError(): void {
		// No reiniciar timers si hay una página de warning activa o estamos en landscape
		if (this.pageWarning.show || this.handsetLandscape) return;

		// Reiniciar el timer de tiempo total
		this.startTotalTime();

		// Resetear el estado de validación del documento para permitir nuevas capturas
		const currentDocument = this.ocrConf?.document?.find((ocr: IOcr) => !ocr.complete);
		if (currentDocument) {
			// Limpiar fotos capturadas parcialmente para permitir nuevas capturas
			currentDocument.photos = [];
			// Resetear el startAt para reiniciar el conteo de tiempo del documento
			currentDocument.startAt = '';
		}
		// Limpiar el scoring del documento
		this.scoringDocumentService.setDocumentValidation([]);
	}

	/**
	 * Tras KO 2032 (documento sin cara): un solo texto + audio (rostro no detectado / evitar brillos).
	 * No llama a resumeAfterRetryableError para no reproducir onInit encima de este aviso.
	 */
	private promptNoFaceInDocumentRetry(): void {
		this.alertContent = {
			label: this.trans.instant('OCR.AUDIOS.NO_FACE_IN_DOCUMENT'),
			status: 'default',
			icon: 'icon-info',
		};
		this.tts.speak('OCR.AUDIO.NO_FACE_IN_DOCUMENT', true);
		this.updateSubtitle();
		this.resumeTimersAfterError();
	}

	/**
	 * Tras KO con `retry: true` en códigos con UX dedicada (p. ej. 2004).
	 * Recaptura genérica usa `showInsufficientOcrReadWarning` (NIF-821).
	 * Sin `clearDataHistory`, AudioHistoryService no repite la misma locución (NIF-573/575).
	 */
	private resumeAfterRetryableError(): void {
		this.audioHistoryService.clearDataHistory();
		const currentDoc = this.ocrConf?.document?.find((ocr: IOcr) => !ocr.complete);
		const initKey = currentDoc?.messages?.onInit;
		if (initKey) {
			this.tts.speak(initKey, true);
			this.updateSubtitle();
		}
		this.resumeTimersAfterError();
	}

	/**
	 * Detiene el loop de captura OCR antes de POL o al destruir el componente.
	 * Sin esto el RAF y los timers pueden seguir haciendo POST /ocr en background.
	 */
	private stopOcrCapture(): void {
		this.ocrCaptureActive = false;
		this.stopCameraCaptureLoop();
		this.stopSecondlyDataCapture();
		this.cameraService.setProcessingOCR(false);
	}

	// Warning time
	private startWarningTime(): void {
		if (!this.canRunTestTimers()) return;
		this.timerManagerService.startWarningTime(() => {
			if (this.timerManagerService.warningTime >= OCR_WARNING_TIME) {
				this.stopWarningTime();
				this.ocrConf.general.status = TestStatus.OnFail;
				const status = this.getLivenessStatus();
				this.sendResult(false, status);
			}
		});
	}

	private getLivenessStatus(): TEST_STATUS {
		if (this.pageWarning.show) {
			switch (this.pageWarning.type) {
				case PAGE_WARNING.HELP:
					return TEST_STATUS.KO_HELP;
				case PAGE_WARNING.CAMERA_ERROR:
					return TEST_STATUS.KO_CAMERA;
			}
		}
		if (this.handsetLandscape) {
			return TEST_STATUS.KO_HANDSET_LANDSCAPE;
		}
		if (this.operation.config.tutorial) {
			return TEST_STATUS.KO_TUTORIAL;
		}
		return TEST_STATUS.KO;
	}

	public stopWarningTime(): void {
		this.timerManagerService.stopWarningTime();
	}

	private resetTimers(): void {
		if (this.subscriptionTimers) {
			this.subscriptionTimers.unsubscribe();
			this.subscriptionTimers = new Subscription();
		}
	}

	// #endregion

	private isSharpDocument(photo: IImageHelper | IDocumentTestPhoto): boolean {
		this.isSharpValid =
			photo.lapacian >= this.ocrConf.general.minSharpnessLapacian &&
			photo.fourier >= this.ocrConf.general.minSharpnessFourier;
		return this.isSharpValid;
	}

	private isBrightnessDocument(photo: IImageHelper | IDocumentTestPhoto): boolean {
		this.isBrightnessValid =
			!(
				photo.consecutiveCellsWith95?.length &&
				photo.consecutiveCellsWith95[0]?.cells?.length >= this.ocrConf.general.maxConsecutiveCellsWith95
			) &&
			!(
				photo.consecutiveCellsWith98?.length &&
				photo.consecutiveCellsWith98[0]?.cells?.length >= this.ocrConf.general.maxConsecutiveCellsWith98
			);
		return this.isBrightnessValid;
	}

	/**
	 * Tras perder o recuperar permisos de cámara: limpia captura parcial y scoring para que
	 * `documentValidation` vuelva a programar el timer de envío (si scoring no está vacío, solo acumula muestras y nunca envía).
	 */
	private resetOcrCaptureAfterCameraPermissionLoss(): void {
		this.resetDocumentValidationState();
		this.sendingResult = false;
		const pendingDoc = this.ocrConf?.document?.find((ocr: IOcr) => !ocr.complete);
		if (pendingDoc?.status === TestStatus.OnFail) {
			pendingDoc.status = TestStatus.OnInit;
			pendingDoc.startAt = '';
		}
		if (
			this.ocrConf?.general &&
			!this.ocrConf.general.complete &&
			this.ocrConf.general.status === TestStatus.OnFail
		) {
			this.ocrConf.general.status = TestStatus.OnInit;
			this.ocrConf.general.finishAt = '';
		}
	}

	/**
	 * Resetea el estado de validación del documento cuando se cambia de orientación.
	 * Limpia los timers de validación y restaura el estado del documento actual a un estado limpio
	 * para permitir una nueva captura sin interferencias del estado anterior.
	 */
	private resetDocumentValidationState(): void {
		// Cancelar timers pendientes de validación
		this.resetTimers();

		// Limpiar el estado de validación del documento actual
		const currentDocument = this.ocrConf?.document?.find((ocr: IOcr) => !ocr.complete);
		if (currentDocument) {
			// Limpiar fotos capturadas parcialmente
			currentDocument.photos = [];
			// Resetear el estado si estaba en validación
			if (currentDocument.status === TestStatus.OnWarning || currentDocument.status === TestStatus.OnInit) {
				currentDocument.status = TestStatus.OnInit;
			}
		}

		// Limpiar el servicio de validación de documentos
		this.scoringDocumentService.setDocumentValidation([]);

		// Resetear el flag de escaneo
		this.detected = false;
	}

	private setOcrSetting(ocrOperation: IOcrOperation): IOcrConf {
		this.ocrCaptureActive = true;
		// NIF-785: maxTime es la ventana de un timeout local (OnFail). Con tries=2 en ocr-conf
		// hacen falta 2 timeouts para el help → side_max_time/2 para que el help ≈ side_max_time.
		this.ocrFrontConf.maxTime = (ocrOperation.config?.setting?.side_max_time ?? 150) / 2;
		this.ocrBackConf.maxTime = (ocrOperation.config?.setting?.side_max_time ?? 150) / 2;
		this.ocrFrontConf.showAnimation = ocrOperation.config?.setting?.show_guide ?? true;
		this.ocrBackConf.showAnimation = ocrOperation.config?.setting?.show_guide ?? true;

		this.ocrFrontConf.messages.onInit = 'OCR.FRONT.INSTRUCTIONS.TEXT.ONINIT';
		this.ocrBackConf.messages.onInit = 'OCR.BACK.INSTRUCTIONS.TEXT.ONINIT';

		const ocrConfCopy: IOcrConf = JSON.parse(JSON.stringify(ocrConf));
		if (this.userAgentService.isPC()) {
			ocrConfCopy.general.minSharpnessLapacian = OCR_SHARPNESS_DESKTOP.minSharpnessLapacian;
			ocrConfCopy.general.minSharpnessFourier = OCR_SHARPNESS_DESKTOP.minSharpnessFourier;
		}
		// Clonar la conf inicial evita mutaciones compartidas de `showAnimation` entre reintentos/cambios de estado.
		const initialDocumentConf: IOcr = JSON.parse(JSON.stringify(this.ocrFrontConf));
		initialDocumentConf.messages.onInit = resolveFirstScanOnInitKey(ocrOperation.config?.allowed_docs);
		ocrConfCopy.document.push(initialDocumentConf);
		// NIF-829: default 2 si el back no envía total_tries; respetar el valor del perfil.
		ocrConfCopy.general.tries = ocrOperation.config?.setting?.total_tries ?? 2;
		return ocrConfCopy;
	}

	private displayHelpWarning(isHelpByNavHelp: boolean): void {
		this.isHelpByNavHelp = isHelpByNavHelp;
		if (this.midniEnabled && this.midniScreen === 'desktop') {
			this.isMidniHelpContext = true;
			this.closeMiDNI();
			this.setPageHelp('valid_docs');
		} else {
			this.isMidniHelpContext = false;
			this.setPageHelp();
		}
	}

	/**
	 * Establece la ayuda de la página según el tipo especificado.
	 *
	 * Contenido del tile «Documentación» (solo capas visibles NIF-610 / Identity—PWCS):
	 * - 1ª captura: Figma `9301:31540` (AYUDA - GENÉRICA) — copy + collapse docs, sin imagen id-front.
	 * - Front→back: Figma `5258:126597` (AYUDA - REVERSO) — copy + ilustración `9303:31791` (id-back).
	 * - Back→front: Figma `5372:30263` (AYUDA - ANVERSO) — copy + id-front.
	 * Las ramas visibles se resuelven en plantilla vía `isFirstSideScan` / `isSecondScanBackSide`.
	 *
	 * @param type Tipo de ayuda a mostrar: 'anv_rev' para revisión de anverso, 'valid_docs' para documentos válidos. Por defecto es 'anv_rev'.
	 */
	private setPageHelp(type: 'anv_rev' | 'valid_docs' | 'invalid_doc' | 'change_device' = 'anv_rev'): void {
		const isHelpDocs = type !== 'anv_rev' && type !== 'change_device';

		let helpDocTitle = 'OCR.HELP.TITLE_VALID_DOCUMENTS';
		if (type === 'invalid_doc') helpDocTitle = 'OCR.HELP.TITLE_INVALID_DOCUMENT';

		this.helpTitle = isHelpDocs ? helpDocTitle : 'LIFE_CHECK.HELP.TITLE';
		if (type === 'valid_docs' && this.isMidniHelpContext) {
			this.helpTitle = 'OCR.BACK.HELP.TITLE';
		}
		if (type !== 'invalid_doc') {
			this.restrictionErrorMsg = '';
		}
		this.isVisibleHelpValidDocuments = isHelpDocs;
		this.isNotValidDocument = type === 'invalid_doc';
		this.setPageWarning(PAGE_WARNING.HELP);

		// Detener todos los timers de proceso y activar el warning time
		this.stopTimersDuringRequest();
		this.startWarningTime();
	}

	/**
	 * Establece el tipo y la visibilidad de la advertencia en la página.
	 *
	 * Esta función actualiza el objeto `pageWarning` para mostrar u ocultar una advertencia en la interfaz,
	 * y define el tipo de advertencia que se debe mostrar.
	 *
	 * @param type El tipo de advertencia de la página, puede ser `PAGE_WARNING` o `null`.
	 * @param show Indica si se debe mostrar la advertencia en la página. Por defecto es `true`.
	 */
	private setPageWarning(type: PAGE_WARNING | null, show: boolean = true): void {
		this.pageWarning.show = show;
		this.pageWarning.type = type;

		// Actualizamos el subtitle
		this.updateSubtitle();
	}

	/**
	 * Actualiza la propiedad `isPassportAllowed` indicando si el pasaporte está permitido
	 * según la configuración de documentos permitidos en la operación actual.
	 *
	 * - Si `allowed_docs.passport` es un array, se consideran permitidos (un array vacío implica que se permiten todos).
	 * - Si `allowed_docs` no contiene la propiedad `passport`, significa que no se permiten pasaportes.
	 */
	private updateIsPassportAllowed(): void {
		const allowedDocs = this.operation?.config?.allowed_docs;
		const passport = allowedDocs ? allowedDocs?.passport : undefined;

		this.isPassportAllowed = !!(passport && Array.isArray(passport) && passport.length >= 0);
	}

	/** DNI/NIE permitido en `allowed_docs` (documento de dos caras). */
	private isIdentityDocAllowed(): boolean {
		const allowedDocs = this.operation?.config?.allowed_docs;
		if (!allowedDocs) {
			return false;
		}
		return Array.isArray(allowedDocs.dni) || allowedDocs.nie === true;
	}

	public getOcrAllowedDniLabel(): string {
		return resolveOcrAllowedDniText(
			this.trans,
			this.operation,
			this.configService.getCurrentConfig()?.company_country,
		);
	}

	public getOcrAllowedDniEuropeanLabel(): string {
		return resolveOcrAllowedDniText(
			this.trans,
			this.operation,
			this.configService.getCurrentConfig()?.company_country,
			'european',
		);
	}

	public getOcrAllowedDniSpainLabel(): string {
		return resolveOcrAllowedDniText(
			this.trans,
			this.operation,
			this.configService.getCurrentConfig()?.company_country,
			'spain',
		);
	}

	public isSpanishCompany(): boolean {
		return isSpanishCompanyCountry(this.configService.getCurrentConfig()?.company_country);
	}

	/** True en 1ª captura (ninguna cara `complete`). Ayuda genérica Figma `9301:31540`. */
	public isFirstSideScan(): boolean {
		return isFirstSideScanUtil(this.ocrConf);
	}

	/**
	 * Segunda captura del reverso tras anverso.
	 * Ayuda visible Figma `5258:126597` (ilustración reverso `9303:31791` / `id-back.svg`).
	 */
	public isSecondScanBackSide(): boolean {
		if (this.isFirstSideScan() || this.getDocumentSideId() !== DOCUMENT_CHECK_ID.BACK) {
			return false;
		}
		return this.ocrConf.document.some((doc) => doc.complete && doc.documentTestId === DOCUMENT_CHECK_ID.FRONT);
	}

	public getDniPhotoSideAsset(assetPath: string): string {
		return resolveDniPhotoSideAsset(
			assetPath,
			this.operation?.config?.dni_wireframe_photo_side,
			this.designService.isDarkMode(),
		);
	}

	public isDniForOtherCountries(): boolean {
		return isDniForOtherCountriesInOcr(this.operation);
	}

	public isDniForSpain(): boolean {
		return isDniForSpainInOcr(this.operation);
	}

	public isPassportForOtherCountries(): boolean {
		return isPassportForOtherCountriesInOcr(this.operation);
	}

	public isPassportForSpain(): boolean {
		return isPassportForSpainInOcr(this.operation);
	}

	// #region Gestión de los textos
	/**
	 * Genera el subtítulo adecuado para la interfaz de usuario del componente OCR,
	 * basado en el estado actual de los permisos, advertencias, configuración de la operación,
	 * y el progreso del reconocimiento de documentos.
	 *
	 * @returns {string} El subtítulo traducido correspondiente al estado actual, o una cadena vacía si no aplica.
	 */
	private generateSubtitle(): string {
		if (
			!this.permissionsCamera ||
			this.handsetLandscape ||
			this.pageWarning.type === PAGE_WARNING.VERIFICATION_FAIL ||
			this.pageWarning.type === PAGE_WARNING.SERVER_ERROR ||
			this.pageWarning.type === PAGE_WARNING.CAMERA_ERROR ||
			this.pageWarning.type === PAGE_WARNING.DIFFERENT_DOCUMENTS ||
			this.pageWarning.type === PAGE_WARNING.MISSING_ADDRESS
		) {
			return '';
		}
		if (this.pageWarning.type === PAGE_WARNING.HELP) {
			if (this.isMidniHelpContext && this.isVisibleHelpValidDocuments) {
				return this.trans.instant('OCR.BACK.HELP.DESCRIPTION');
			}
			const label = this.isVisibleHelpValidDocuments
				? 'OCR.HELP.DESCRIPTION_VALID_DOCUMENTS'
				: 'OCR.BACK.HELP.DESCRIPTION';
			return this.trans.instant(label);
		}
		if (this.operation?.config.tutorial) {
			// Si no hay pasaporte permitido, usar un título diferente
			if (!this.isPassportAllowed) {
				return this.operation.type === OPERATION_TYPE.OCR
					? getOcrTutorialDniOnlyTitle(
							this.trans,
							this.operation,
							this.configService.getCurrentConfig()?.company_country,
						)
					: this.trans.instant('OCR.FRONT.TUTORIAL_TITLE');
			}
			// Título por defecto cuando hay pasaporte permitido
			return this.operation.type === OPERATION_TYPE.OCR
				? this.trans.instant('OCR.BACK.TUTORIAL_TITLE')
				: this.trans.instant('OCR.FRONT.TUTORIAL_TITLE');
		}
		if (this.loading.show || this.pageWarning.show || this.handsetLandscape) {
			return '';
		}
		if ((this.midniScreen !== null && this.midniEnabled) || (this.eudiScreen !== null && this.eudiEnabled)) {
			return '';
		}
		if (this.barcodeScanActive) {
			return this.trans.instant('OCR.BARCODE.INSTRUCTION');
		}
		if (!this.operation?.config.tutorial) {
			if (this.ocrConf.document.length) {
				const found = this.ocrConf.document.find((ocr: IOcr) => !ocr.complete);
				if (found) {
					let message: string = this.subtitle;
					if (found.status === TestStatus.OnInit || found.status === TestStatus.OnWarning) {
						message = found.messages[TestStatus.OnInit];
					}
					const translatedMessage = message ? this.trans.instant(message) : '';
					return translatedMessage;
				}
				return '';
			}
			return this.trans.instant(this.ocrConf.general[TestStatus.OnInit]);
		}
		return '';
	}
	// #endregion

	// #region Loading
	/**
	 * Muestra un indicador de carga personalizado según la configuración de la operación.
	 * Si la operación utiliza un iframe, muestra el indicador de carga en el iframe;
	 * de lo contrario, muestra el indicador de carga estándar.
	 *
	 * @param text Texto opcional que se mostrará junto al indicador de carga.
	 */
	private showLoading(text = ''): void {
		this.operation.config.iframe ? this.loadingService.showIframe(text) : this.loadingService.show(text);
	}

	/**
	 * Oculta el indicador de carga personalizado.
	 * Si la configuración de la operación utiliza un iframe, oculta el indicador de carga específico para iframes;
	 * de lo contrario, oculta el indicador de carga estándar.
	 */
	private hideLoading(): void {
		this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
	}

	private activatePreviewLoading(textKey: string): void {
		this.loading = {
			show: true,
			text: this.trans.instant(textKey),
		};
	}

	private hidePreviewLoading(): void {
		this.loading = { show: false, text: '' };
	}
	// #endregion

	ngOnDestroy(): void {
		// Detener la captura de OCR
		this.stopOcrCapture();
		this.stopBarcodeInstructionAudio();
		this.stopEudiPoll();
		this.stopMiDNICountdown();
		this.cameraPermissionsTimerService.stopWait();

		// Desuscribirse de todos los timers
		if (this.video) {
			this.video.removeEventListener('loadeddata', this.predictWebcam);
		}
		this.video = null;
		// Limpiar timer de captura de datos cada segundo
		if (this.secondlyTimerSubscription) {
			this.secondlyTimerSubscription.unsubscribe();
			this.secondlyTimerSubscription = null;
		}
		// Detener TODOS los timers del servicio
		this.stopTotalTime();
		this.stopWarningTime();
		if (this.subscriptionTimers) {
			this.subscriptionTimers.unsubscribe();
		}
		if (this.stream) {
			// NO detener los tracks del stream si el streaming está activo
			// El streaming se encargará de gestionar el ciclo de vida del stream
			const isStreamingActive = this.streamingManagementService.isStreamingActive();

			if (!isStreamingActive && this.stream) {
				this.stream.getTracks().forEach((track) => track.stop());
			}
		}
		this.subscriptionTimers.unsubscribe();
		this.stream = null;
		this.screenShotService.stopSaveScreenshot();
		// Limpiar la imagen capturada
		this.hideCapturedImagePreview();
		if (this.stickyTimer) {
			clearTimeout(this.stickyTimer);
			this.stickyTimer = null;
		}
		this.clearScanningAlert();
		this.lottieThemeSub?.unsubscribe();
		this.destroy$.next();
		this.destroy$.complete();
	}
}
